// ===== Packages =====

import React, {
    useCallback,
    useMemo,
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
}                                                       from 'react';
import styled                                          from 'styled-components';
import {
    Slate,
    Editable,
    withReact,
    ReactEditor,
}                                                       from 'slate-react';
import {
    createEditor,
    Node,
    Transforms,
    Editor,
    Descendant,
    Path,
    Range,
    NodeEntry,
    Location,
    Point,
    Text,
}                                                       from 'slate';
import { withHistory }                                  from 'slate-history';
import * as Y                                           from 'yjs';
import {
    withYjs,
    YjsEditor,
    withCursors,
    CursorEditor,
    slateNodesToInsertDelta,
    yTextToSlateElement,
}                                                       from '@slate-yjs/core';
import {
    useDecorateRemoteCursors,
    getRemoteCursorsOnLeaf,
    useRemoteCursorOverlayPositions,
    TextWithRemoteCursors,
}                                                       from '@slate-yjs/react';
import {
    StorageError,
    getDownloadURL,
    UploadTaskSnapshot,
    StorageReference,
    UploadTask,
}                                                       from 'firebase/storage';
import {
    useParams,
}                                                       from 'react-router-dom';
import ShortUniqueId                                    from 'short-unique-id';
// eslint-disable-next-line import/no-extraneous-dependencies
import mime                                             from 'mime';
import { v4 as uuidv4 }                                 from 'uuid';

// ===== Components =====

import {
    SlateLeaf,
    PlaceholderBox,
}                                                       from '../../../styles';
import Modal                                            from '../../Modal';
import {
    Button,
    GenericModal,
    PortableToolbar,
}                                                       from '../helpers';
import {
    BottomBufferElement,
    BottomBufferHelpers,
    withBottomBuffers,
}                                                       from '../plugins/bottomBuffersPlugin';
import {
    withDividers,
    DividerHelpers,
    DividerElement,
}                                                       from '../plugins/dividersPlugin';
import {
    EmphasizerHelpers,
}                                                       from '../plugins/emphasizersPlugin';
import {
    withHeaders,
    HeaderHelpers,
    HeaderElement,
}                                                       from '../plugins/headersPlugin';
import {
    withLines,
    LineHelpers,
}                                                       from '../plugins/linesPlugin';
import {
    withAudioNotes,
    AudioNoteHelpers,
    AudioNoteElement,
}                                                       from '../plugins/audioNotesPlugin';
import {
    withWebLinks,
    WebLinkHelpers,
    WebLinkElement,
}                                                       from '../plugins/webLinksPlugin';
import {
    withZoomLevels,
    ZoomLevelHelpers,
    ZoomLevelElement,
}                                                       from '../plugins/zoomLevelsPlugin';
import {
    withLists,
    ListHelpers,
    ListElement,
    ListItemElement,
}                                                       from '../plugins/listsPlugin';
import {
    withFigures,
    FigureHelpers,
    CaptionElement,
    FigureElement,
    GalleryElement,
    FigureContentElement,
    FigureContentTypes,
}                                                       from '../plugins/figuresPlugin';
import {
    withBlockQuotes,
    BlockQuoteHelpers,
    BlockQuoteElement,
}                                                       from '../plugins/blockQuotesPlugin';
import {
    handleEditorClipboardPaste,
    withRichTextClipboard,
}                                                       from '../plugins/richTextClipboardPlugin';
import HandleOnKeyDown                                  from '../plugins/handleHotKeys';

import {
    BlockQuote,
    BottomBuffer,
    Caption,
    Divider,
    AudioNote,
    Figure,
    Gallery,
    HeaderOne,
    HeaderTwo,
    Image,
    InlineLink,
    List,
    ListItem,
    Paragraph,
    YouTubeEmbed,
    TwitterEmbed,
    SpotifyEmbed,
    VimeoEmbed,
    ZoomLevel,
}                                                       from '../renderers';

// ===== Services =====

import {
    getTouchMouseEventHandlers,
    getPressEventHandlers,
    playAudio,
    uploadToCloudStorage,
    getMediaStorageBucket,
    getStorageErrorMessage,
    updateAnnotation,
    createMedia,
    recordUserAction,
    deepCopy,
    deepEqual,
    isTestMode,
    buildAudioTextSyncMap,
    createPostCollaborativeSession,
    ensureNonEmptySlateValue,
    getTranscriptionWordTimestampMs,
    getCollaborativeCursorColor,
    getCollaborativeCursorName,
    getRemoteCursorSelectionBackground,
    replaceSlateEditorValue,
    resolvePostContentDictationSourceTextsByAudioNoteId,
    serializeSlateSelectionForClipboard,
    stripBottomBufferNodes,
    buildInternalPostLinkHref,
    resolveInternalPostLinkRevealBlockCount,
}                                                       from '../../../services';
import buildAnnotationQuote                              from '../../../services/buildAnnotationQuote';
import {
    buildReadOptimizedPostValueHash,
    READ_OPTIMIZED_POST_VALUE_VERSION,
}                                                        from '../../../services/buildReadOptimizedPostValueHash';
import getOrCreateAudioClip                              from '../../../services/getOrCreateAudioClip';
import normalizePostSelectionPath, {
    resolvePostSelectionCandidates,
}                                                        from '../../../services/normalizePostSelectionPath';
import readOnlyAudioTextTouchGestureRepresentsTap, {
    touchGestureMovedBeyondTapThreshold,
}                                                        from '../../../services/readOnlyAudioTextTouchGesture';
import resolveUpdatedAnnotationSelection                 from '../../../services/resolveUpdatedAnnotationSelection';

// ===== Hooks =====

import {
    useEventListener,
    useTimeout,
}                                                       from '../../../hooks';

// ===== Debug Flags =====

import {
    INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY,
    shouldEnableInternalPostLinkConsoleLogs,
}                                                       from '../../../constants/debugFlags';

// ===== Enums =====

import {
    ANNOTATION_DETACHED_REASON,
    BUTTON_TYPE,
    EDITOR_TOOLBAR_TYPE,
    EDITOR_TOOLBAR_TOOL_GROUP,
    EDITOR_CONTEXT_TYPE,
    INTERNAL_POST_LINK_SCOPE_LABEL,
    INTERNAL_POST_LINK_SELECTION_MODE,
    STORAGE_ENTITY,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    POST_COLLAB_DOC_CHECKPOINT_REASON,
    SLATE_EDITOR_REPLACEMENT_SELECTION,
    STORAGE_ERROR_CODE,
    MEDIA_TYPE,
    USER_ACTION_TYPE,
    POST_CONTENT_SCOPE,
}                                                       from '../../../enums';
import { EMPHASIZER_MARK }                              from '../plugins/enums';
import {
    ZOOM_LEVEL,
    ZOOM_LEVEL_TYPE,
}                                                       from '../elements/enums';

// ===== Interfaces =====

import {
    ICoord,
    IAudioTextSyncMatch,
    IAudioTextSyncResult,
    IAnnotationDecorationTarget,
    IAnnotationItemDetached,
    IEmoji,
    IMediaItem,
    IModalMessage,
    IAnnotationQuote,
    IAnnotationTextAnchor,
    IEditorDecoration,
    IAnnotationItem,
    IPostQuoteDecorationTarget,
    IPartialUserItem,
    IRestorePostValueRequest,
    ISnackbarItem,
    IInternalPostLinkTarget,
    IReadOnlyRevealTargetResolution,
    IReadPostChapterItem,
    IReadPostSectionItem,
    IZoomLevelItem,
    ICollaborativeCursorData,
    IReadOnlyAudioPlaybackControls,
    IReadOnlyAudioNotePlaybackState,
}                                                       from '../../../interfaces';
import {
    AudioNoteNode,
    WebLinkNode,
}                                                       from '../interfaces';

// ===== Sounds =====

import InputClick                                       from '../../../sounds/button_click.mp3';

// ===== Images =====

import ArrowIcon                                        from '../../../images/editor/arrow.svg';
import FileIcon                                         from '../../../images/file.svg';
import CrossIcon                                        from '../../../images/editor/cross.svg';
import EditIcon                                         from '../../../images/editor/pencil.svg';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    HIDDEN_VOID_CONTENT_STYLE,
    POST_EDITOR_MAX_WIDTH,
    INITIAL_EDITOR_VALUE,
    HOVER_TARGET_CLASSNAME,
    DEFAULT_AUDIO_VOLUME,
    SLATE_EDITOR_CLASSNAME,
    ANNOTATION_DECORATION_PREFIX,
    REVEAL_QUOTE_DECORATION_PREFIX,
    SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME,
    SLATE_EDITOR_SELECT_ALL_TEXT_TIMEOUT_DURATION,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    SUPPORT_EMAIL_ADDRESS,
    MILLISECONDS_IN_A_SECOND,
}                                                       from '../../../constants/generalConstants';
import CURSOR_SIGN                                      from '../../../constants/cursorSigns';
import { SAVE_POST_DURING_AUDIO_RECORDING_ERROR }       from '../../../constants/notificationMessages';

// ===== Styles =====

import { theme as themeObj }                            from '../../../themes/theme-context';
import {
    HistoryAccessPane,
    HistoryAccessSection,
    HistoryEmptyState,
    HistoryModalCloseButtonContainer,
    HistoryModalContainer,
    HistoryModalHeader,
    HistoryModalSubtitle,
    HistoryModalTitle,
    HistoryPublishSummaryActions,
    HistoryPublishSummaryCard,
    HistoryPublishSummarySubtitle,
    HistoryPublishSummaryTitle,
    HistoryPreviewActions,
    HistoryPreviewPane,
    HistoryScopeLabel,
    HistoryScopeSelect,
    HistoryScopeSeparator,
    HistorySinglePaneBody,
    HistoryTargetButtonGroup,
    HistoryTargetControls,
    HistoryTargetRow,
    HistoryTargetSelectGroup,
}                                                       from '../../PostPublishingDialog/styles';

const NORMALIZED_POST_VALUE_CACHE_MAX_SIZE = 8;
const normalizedPostValueCache = new Map<string, Descendant[]>();
const READ_ONLY_AUDIO_WORD_CLICK_SLACK = 2;

function getNormalizedPostValueCacheKey(data: {
    postContent?: string,
    postValueNodes?: Descendant[],
    readOnly: boolean,
    readOnlyTrustedValueVersion?: number,
    readOnlyTrustedValueHash?: string,
}): string | null {
    const {
        postContent,
        postValueNodes,
        readOnly,
        readOnlyTrustedValueVersion,
        readOnlyTrustedValueHash,
    } = data;
    if (!readOnly) {
        return postContent != null ? `write::${postContent}` : null;
    }

    const hasTrustedReadOnlyMetadata = (
        readOnlyTrustedValueVersion != null
        && typeof readOnlyTrustedValueHash === 'string'
        && readOnlyTrustedValueHash.trim().length > 0
    );
    const modeKey = hasTrustedReadOnlyMetadata
        ? `read-trusted:${readOnlyTrustedValueVersion}:${readOnlyTrustedValueHash}`
        : 'read-fallback';

    if (postContent != null) {
        return `${modeKey}::${postContent}`;
    }
    if (hasTrustedReadOnlyMetadata && postValueNodes != null) {
        return modeKey;
    }

    return null;
}

function getCachedNormalizedPostValue(cacheKey: string | null): Descendant[] | null {
    if (cacheKey == null) {
        return null;
    }

    const cachedValue = normalizedPostValueCache.get(cacheKey);
    if (!cachedValue) {
        return null;
    }

    normalizedPostValueCache.delete(cacheKey);
    normalizedPostValueCache.set(cacheKey, cachedValue);
    return deepCopy(cachedValue);
}

function setCachedNormalizedPostValue(cacheKey: string | null, normalizedValue: Descendant[]): void {
    if (cacheKey == null) {
        return;
    }

    if (normalizedPostValueCache.has(cacheKey)) {
        normalizedPostValueCache.delete(cacheKey);
    }

    normalizedPostValueCache.set(cacheKey, deepCopy(normalizedValue));
    if (normalizedPostValueCache.size > NORMALIZED_POST_VALUE_CACHE_MAX_SIZE) {
        const oldestCacheKey = normalizedPostValueCache.keys().next().value;
        if (oldestCacheKey) {
            normalizedPostValueCache.delete(oldestCacheKey);
        }
    }
}

/**
 * Read-only renders can reuse the stored serialized tree directly when the
 * backend proves the current payload still matches the write-time digest.
 */
function canUseTrustedReadOnlySlateValue(data: {
    readOnly: boolean,
    postContent?: string,
    parsedValue: unknown,
    readOnlyTrustedValueVersion?: number,
    readOnlyTrustedValueHash?: string,
}): data is {
    readOnly: true,
    postContent: string,
    parsedValue: Descendant[],
    readOnlyTrustedValueVersion: number,
    readOnlyTrustedValueHash: string,
} {
    const {
        readOnly,
        postContent,
        parsedValue,
        readOnlyTrustedValueVersion,
        readOnlyTrustedValueHash,
    } = data;

    if (!readOnly) {
        return false;
    }
    if (readOnlyTrustedValueVersion !== READ_OPTIMIZED_POST_VALUE_VERSION) {
        return false;
    }
    if (
        typeof readOnlyTrustedValueHash !== 'string'
        || readOnlyTrustedValueHash.trim().length === 0
    ) {
        return false;
    }
    if (!Array.isArray(parsedValue)) {
        return false;
    }

    if (postContent == null) {
        return true;
    }

    return buildReadOptimizedPostValueHash(postContent) === readOnlyTrustedValueHash;
}

/**
 * Trusted read-only chapter and section payloads can skip normalization
 * entirely, which means older saved value nodes may reach the renderer without
 * the editor-owned bottom buffer. Reattach that spacer in-memory for zoom
 * level five so read mode keeps its expected landing space without forfeiting
 * the trusted fast path.
 */
function ensureReadOnlyZoomFiveBottomBuffer(data: {
    slateValue: Descendant[],
    readOnly: boolean,
    currentZoomLevel: IZoomLevelItem | null,
}): Descendant[] {
    const {
        slateValue,
        readOnly,
        currentZoomLevel,
    } = data;

    if (
        !readOnly
        || currentZoomLevel?.level !== ZOOM_LEVEL.five
    ) {
        return slateValue;
    }

    const hasBottomBuffer = slateValue.some((node) => BottomBufferElement.isBottomBufferElement(node as Node));
    if (hasBottomBuffer) {
        return slateValue;
    }

    const nextSlateValue = deepCopy(slateValue);
    nextSlateValue.push(BottomBufferElement.newBottomBufferElement() as Descendant);
    return nextSlateValue;
}

/**
 * ReaderView uses the full paragraph count to keep the contents-table progress
 * bar stable while PostEditor reveals the body in chunks.
 */
function countParagraphNodesInSlateValue(nodes: Descendant[]): number {
    const countParagraphNodes = (currentNodes: Descendant[]): number => currentNodes.reduce((count, node) => {
        if (!node || typeof node !== 'object') {
            return count;
        }

        const selfCount = (node as { isLine?: boolean }).isLine === true ? 1 : 0;
        const childCount = Array.isArray((node as { children?: Descendant[] }).children)
            ? countParagraphNodes((node as { children: Descendant[] }).children)
            : 0;

        return count + selfCount + childCount;
    }, 0);

    return countParagraphNodes(nodes);
}

/**
 * Internal post-link destinations only target visible headings and paragraphs,
 * so treat empty placeholder lines and media-only scopes as unavailable.
 */
function slateValueHasSelectableInternalPostLinkTargets(nodes: Descendant[]): boolean {
    return nodes.some((node) => {
        if (!node || typeof node !== 'object') {
            return false;
        }

        if ((node as { isLine?: boolean }).isLine === true) {
            return Node.string(node).trim().length > 0;
        }

        const childNodes = Array.isArray((node as { children?: Descendant[] }).children)
            ? (node as { children: Descendant[] }).children
            : [];
        if (childNodes.length === 0) {
            return false;
        }

        return slateValueHasSelectableInternalPostLinkTargets(childNodes);
    });
}

/**
 * Normalizes a scope's stored Slate value into the same node shape the editor
 * renders so internal-link availability checks stay consistent with the preview.
 */
function resolveInternalPostLinkSelectableTargetNodes(
    value?: string,
    valueNodes?: Descendant[],
): Descendant[] | null {
    if (valueNodes != null) {
        return stripBottomBufferNodes(
            ensureNonEmptySlateValue(valueNodes, INITIAL_EDITOR_VALUE),
            (node) => BottomBufferElement.isBottomBufferElement(node as Node),
        );
    }

    if (!value) {
        return null;
    }

    try {
        return stripBottomBufferNodes(
            ensureNonEmptySlateValue(JSON.parse(value) as Descendant[], INITIAL_EDITOR_VALUE),
            (node) => BottomBufferElement.isBottomBufferElement(node as Node),
        );
    } catch (_error) {
        return null;
    }
}

/**
 * Reader mode keeps the active chapter/section hydrated separately from the
 * outline-level `chapters` array. Merge the current scope back into that tree
 * before we hand it to inline links so same-scope previews can resolve text
 * for destinations that live beyond the currently visible chunk.
 */
function hydrateCurrentScopeIntoInternalPostLinkChapters(data: {
    chapters?: IReadPostChapterItem[],
    selectedPostValuePath: number[],
    postValue?: string,
    postValueNodes?: Descendant[],
    readOnlyUsesPreviewSource: boolean,
}): IReadPostChapterItem[] | undefined {
    const {
        chapters,
        selectedPostValuePath,
        postValue,
        postValueNodes,
        readOnlyUsesPreviewSource,
    } = data;
    if (!chapters?.length || selectedPostValuePath.length === 0) {
        return chapters;
    }

    const currentChapterIndex = selectedPostValuePath[0];
    if (
        currentChapterIndex == null
        || currentChapterIndex < 0
        || currentChapterIndex >= chapters.length
    ) {
        return chapters;
    }

    const currentScopeValuePatch = readOnlyUsesPreviewSource
        ? {
            ...(postValue !== undefined ? { previewValue: postValue } : {}),
            ...(postValueNodes !== undefined ? { previewValueNodes: postValueNodes } : {}),
        }
        : {
            ...(postValue !== undefined ? { value: postValue } : {}),
            ...(postValueNodes !== undefined ? { valueNodes: postValueNodes } : {}),
        };
    if (Object.keys(currentScopeValuePatch).length === 0) {
        return chapters;
    }

    const nextChapters = [...chapters];
    const currentChapter = nextChapters[currentChapterIndex];
    if (!currentChapter) {
        return chapters;
    }

    if (selectedPostValuePath.length === 1) {
        nextChapters[currentChapterIndex] = {
            ...currentChapter,
            ...currentScopeValuePatch,
        };
        return nextChapters;
    }

    const currentSectionIndex = selectedPostValuePath[1];
    if (
        currentSectionIndex == null
        || currentSectionIndex < 0
        || !currentChapter.sections?.[currentSectionIndex]
    ) {
        return chapters;
    }

    const nextSections = [...(currentChapter.sections || [])];
    nextSections[currentSectionIndex] = {
        ...nextSections[currentSectionIndex],
        ...currentScopeValuePatch,
    };
    nextChapters[currentChapterIndex] = {
        ...currentChapter,
        sections: nextSections,
    };

    return nextChapters;
}

/**
 * Child audio notes report read-only playback snapshots upward. Ignore
 * identical snapshots so stable playback state does not trigger a render loop
 * between AudioNote and PostEditor.
 */
function buildReadOnlyAudioTranscriptSignature(
    transcriptWords: IReadOnlyAudioNotePlaybackState['transcriptWords'],
): string {
    if (transcriptWords.length === 0) {
        return '0';
    }

    const firstWord = transcriptWords[0];
    const lastWord = transcriptWords[transcriptWords.length - 1];

    return [
        transcriptWords.length,
        firstWord.word,
        firstWord.startTime.seconds,
        firstWord.startTime.nanos || 0,
        lastWord.word,
        lastWord.endTime.seconds,
        lastWord.endTime.nanos || 0,
    ].join(':');
}

function areReadOnlyAudioPlaybackStatesEqual(
    previousState: IReadOnlyAudioNotePlaybackState,
    nextState: IReadOnlyAudioNotePlaybackState,
): boolean {
    return previousState.title === nextState.title
        && previousState.isPostContentDictation === nextState.isPostContentDictation
        && buildReadOnlyAudioTranscriptSignature(previousState.transcriptWords) === buildReadOnlyAudioTranscriptSignature(nextState.transcriptWords)
        && previousState.activeTranscriptWordIndex === nextState.activeTranscriptWordIndex
        && previousState.hasStartedPlayback === nextState.hasStartedPlayback
        && previousState.isStartingPlayback === nextState.isStartingPlayback
        && previousState.playbackSpeed === nextState.playbackSpeed
        && previousState.currentTimeMs === nextState.currentTimeMs
        && previousState.durationMs === nextState.durationMs
        && previousState.isPlaying === nextState.isPlaying
        && previousState.isVisibleInViewport === nextState.isVisibleInViewport;
}

function resolveInteractionCenter(
    event: React.MouseEvent | React.TouchEvent,
): ICoord | null {
    if ('touches' in event && event.touches.length > 0) {
        return {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
        };
    }

    if ('changedTouches' in event && event.changedTouches.length > 0) {
        return {
            x: event.changedTouches[0].clientX,
            y: event.changedTouches[0].clientY,
        };
    }

    if ('clientX' in event && 'clientY' in event) {
        return {
            x: event.clientX,
            y: event.clientY,
        };
    }

    return null;
}

/**
 * Clicks can land on punctuation or the small whitespace around a word. Match
 * the nearest synced word on the same text node so the reader can tap near a
 * word and still jump to the expected audio position.
 */
function resolveAudioTextSyncMatchFromPoint(
    point: Point,
    syncResult: IAudioTextSyncResult,
): IAudioTextSyncMatch | null {
    const pointPathKey = point.path.join(',');
    const candidateMatches = syncResult.matches.filter((match) => match.pathKey === pointPathKey);
    const containingMatch = candidateMatches.find((match) => (
        point.offset >= match.startOffset
        && point.offset < match.endOffset
    ));
    if (containingMatch) {
        return containingMatch;
    }

    let nearestMatch: IAudioTextSyncMatch | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    candidateMatches.forEach((match) => {
        let distanceToWord = 0;
        if (point.offset < match.startOffset) {
            distanceToWord = match.startOffset - point.offset;
        } else if (point.offset > match.endOffset) {
            distanceToWord = point.offset - match.endOffset;
        }

        if (distanceToWord < nearestDistance) {
            nearestMatch = match;
            nearestDistance = distanceToWord;
        }
    });

    if (nearestDistance > READ_ONLY_AUDIO_WORD_CLICK_SLACK) {
        return null;
    }

    return nearestMatch;
}

/**
 * Builds a path-to-order lookup for visible headers so reader-only heading
 * markers can render numbered parts without mutating persisted Slate content.
 */
function buildHeaderPartNumberByPath(
    nodes: Descendant[],
    matchHeader: (node: Node) => boolean,
): Map<string, number> {
    const headerPartNumbersByPath = new Map<string, number>();
    let currentPartNumber = 0;

    const visitNodes = (currentNodes: Descendant[], parentPath: number[] = []): void => {
        currentNodes.forEach((currentNode, index) => {
            const currentPath = [...parentPath, index];

            if (matchHeader(currentNode as Node)) {
                currentPartNumber += 1;
                headerPartNumbersByPath.set(currentPath.join(','), currentPartNumber);
            }

            if ('children' in currentNode && Array.isArray(currentNode.children)) {
                visitNodes(currentNode.children as Descendant[], currentPath);
            }
        });
    };

    visitNodes(nodes);
    return headerPartNumbersByPath;
}

const EDITOR_PLUGINS = [
    withFigures,
    withLists,
    withHeaders,
    withLines,
    withWebLinks,
    withZoomLevels,
    withBlockQuotes,
    withBottomBuffers,
    withDividers,
    withReact,
    withHistory,
    withRichTextClipboard,
    withAudioNotes,
];

const DEFAULT_REMOTE_CURSOR_COLOR = themeObj.verascopeColor.blue200;
const READ_ONLY_INITIAL_BLOCK_COUNT = 80;
const READ_ONLY_BATCH_BLOCK_COUNT = 120;
const READ_ONLY_INTERNAL_LINK_REVEAL_BUFFER_BLOCK_COUNT = 12;
const READ_ONLY_PREFETCH_DISTANCE = 2000;
const READ_ONLY_CHUNK_PLACEHOLDER_MAX_WIDTH = 35 * BODY_FONT_SIZE;
const READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_COUNT = 3;
const READ_ONLY_CHUNK_PLACEHOLDER_LINES_PER_BLOCK = 5;
const READ_ONLY_CHUNK_PLACEHOLDER_WIDTH = 'calc(100% - 2.5rem)';
const READ_ONLY_CHUNK_PLACEHOLDER_LINE_GAP = 0.3125 * BODY_FONT_SIZE;
const READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_GAP = 1.25 * BODY_FONT_SIZE;
const READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_HEIGHT = (
    (READ_ONLY_CHUNK_PLACEHOLDER_LINES_PER_BLOCK * BODY_FONT_SIZE)
    + ((READ_ONLY_CHUNK_PLACEHOLDER_LINES_PER_BLOCK - 1) * READ_ONLY_CHUNK_PLACEHOLDER_LINE_GAP)
);
// Read-only chunk growth should never let Slate scroll a hidden caret back
// into view while the reader is simply consuming progressively loaded content.
const SUPPRESS_SCROLL_SELECTION_INTO_VIEW = (): undefined => undefined;

// Stores the exact scroll container mode and scrollTop we need to preserve when
// a same-scope read-only refresh reveals another chunk.
interface IReadOnlyAppendScrollLock {
    top: number,
    useWindowScroll: boolean,
}

const COLLABORATIVE_CURSOR_COLORS = [
    themeObj.verascopeColor.orange200,
    themeObj.verascopeColor.yellow200,
    themeObj.verascopeColor.red200,
    themeObj.verascopeColor.blue200,
    themeObj.verascopeColor.green200,
    themeObj.verascopeColor.purple400,
];

function CollaborativeEditable({
    id,
    readOnly,
    placeholder,
    customCursorActive,
    containerRef,
    decorate,
    renderElement,
    renderLeaf,
    handleOnKeyDown,
    onReadOnlyAudioTextSeekInteraction,
    handleReadOnlyAudioTextTouchStart,
    handleReadOnlyAudioTextTouchMove,
    clearReadOnlyAudioTextTouchGesture,
    onEditableEnter,
    onEditableLeave,
    onEditableFocus,
    onEditableBlur,
    onEditablePointerDownCapture,
    handleEditableCopy,
    handleEditablePaste,
    usePointerCursor,
}: {
    id: string,
    readOnly: boolean,
    placeholder: string,
    customCursorActive: boolean,
    containerRef: React.RefObject<HTMLDivElement>,
    decorate: (entry: NodeEntry) => IEditorDecoration[],
    renderElement: (props: any) => JSX.Element,
    renderLeaf: (props: any) => JSX.Element,
    handleOnKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void,
    onReadOnlyAudioTextSeekInteraction: (e: React.MouseEvent | React.TouchEvent) => void,
    handleReadOnlyAudioTextTouchStart: (e: React.TouchEvent) => void,
    handleReadOnlyAudioTextTouchMove: (e: React.TouchEvent) => void,
    clearReadOnlyAudioTextTouchGesture: () => void,
    onEditableEnter: any,
    onEditableLeave: any,
    onEditableFocus: any,
    onEditableBlur: any,
    onEditablePointerDownCapture: (e: React.MouseEvent | React.TouchEvent) => void,
    handleEditableCopy: (event: React.ClipboardEvent<HTMLDivElement>) => void,
    handleEditablePaste: (event: React.ClipboardEvent<HTMLDivElement>) => boolean,
    usePointerCursor: boolean,
}): JSX.Element {
    const decorateRemoteCursors = useDecorateRemoteCursors<ICollaborativeCursorData>({
        carets: false,
    });
    const [remoteCursorOverlays] = useRemoteCursorOverlayPositions<ICollaborativeCursorData, HTMLDivElement>({
        containerRef,
    });

    const collaborativeDecorate = useCallback((entry: NodeEntry) => [
        ...decorate(entry),
        ...decorateRemoteCursors(entry),
    ], [decorate, decorateRemoteCursors]);

    return (
        <>
            <Editable
                {...(readOnly
                    ? {
                        readOnly: true,
                    } : {}
                )}
                {...(!customCursorActive
                    ? {
                        style: {
                            cursor: usePointerCursor ? 'pointer' : 'text',
                        },
                    } : {}
                )}
                data-id={id}
                className={`${HOVER_TARGET_CLASSNAME} ${SLATE_EDITOR_CLASSNAME} ${SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME}`}
                spellCheck
                decorate={collaborativeDecorate}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder={!readOnly ? placeholder : undefined}
                scrollSelectionIntoView={readOnly ? SUPPRESS_SCROLL_SELECTION_INTO_VIEW : undefined}
                onKeyDown={handleOnKeyDown}
                onTouchStart={handleReadOnlyAudioTextTouchStart}
                onTouchMove={handleReadOnlyAudioTextTouchMove}
                onMouseUp={onReadOnlyAudioTextSeekInteraction}
                onTouchEnd={onReadOnlyAudioTextSeekInteraction}
                onTouchCancel={clearReadOnlyAudioTextTouchGesture}
                onMouseDownCapture={onEditablePointerDownCapture}
                onTouchStartCapture={onEditablePointerDownCapture}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onEditableEnter,
                    onMouseLeave: onEditableLeave,
                })}
                onFocus={onEditableFocus}
                onBlur={onEditableBlur}
                onCopy={handleEditableCopy}
                onPaste={handleEditablePaste}
            />
            <RemoteCursorLayer>
                {remoteCursorOverlays.map((remoteCursorOverlay) => {
                    if (!remoteCursorOverlay.caretPosition) return null;

                    const remoteCursorColor = remoteCursorOverlay.data?.color || DEFAULT_REMOTE_CURSOR_COLOR;
                    const remoteCursorName = remoteCursorOverlay.data?.name || 'Editor';
                    return (
                        <RemoteCursorOverlay
                            key={`remote-cursor-${remoteCursorOverlay.clientId}`}
                            top={remoteCursorOverlay.caretPosition.top}
                            left={remoteCursorOverlay.caretPosition.left}
                            height={remoteCursorOverlay.caretPosition.height}
                        >
                            <RemoteCursorLabel backgroundColor={remoteCursorColor}>
                                {remoteCursorName}
                            </RemoteCursorLabel>
                            <RemoteCursorCaret color={remoteCursorColor} />
                        </RemoteCursorOverlay>
                    );
                })}
            </RemoteCursorLayer>
        </>
    );
}

PostEditor.defaultProps = {
    setRef: undefined,
    hide: false,
    enableGallery: true,
    postValue: undefined,
    postValueNodes: undefined,
    rootPostTitle: undefined,
    rootPostValue: undefined,
    rootPostValueNodes: undefined,
    rootPostPreviewValueNodes: undefined,
    userIsAdmin: false,
    targetAnnotationID: null,
    chapters: undefined,
    onInitializedValueChange: undefined,
    disableProgressiveReadOnly: false,
    disableMergedWriteRouting: false,
    collaborativeDocId: undefined,
    collaborativeAuthToken: undefined,
    collaborativeTestMode: false,
    onCollaborativeParticipantsChange: undefined,
    restorePostValueRequest: undefined,
    onRestorePostValueApplied: undefined,
    onBodyPainted: undefined,
    onReadOnlyChunkingIdleChange: undefined,
    onReadOnlyRenderedBlockCountChange: undefined,
    onReadOnlyParagraphCountChange: undefined,
    onReadOnlyFullRenderReadyChange: undefined,
    onReadOnlyAudioPlaybackStateChange: undefined,
    onNavigateInternalPostLink: undefined,
    readOnlyRevealParagraphId: null,
    onReadOnlyRevealTargetResolutionChange: undefined,
    activeInternalPostLinkNavigationHref: null,
    readOnlyUsesPreviewSource: false,
    readOnlyTrustedValueHash: undefined,
    readOnlyTrustedValueVersion: undefined,
    previewAudioEndPromptText: '',
    readOnlyChapterId: undefined,
    readOnlySectionId: undefined,
    showHeaderTwoPartMarkers: false,
    headerTwoPartCountOverride: null,
    // Callers outside the reader, like version-history preview, can safely
    // omit this and fall back to window scroll.
    scrollParentRef: null,
    forcePointerCursor: false,
};
interface Props {
    customCursorActive: boolean,
    id: string,
    user: IPartialUserItem | null,
    userIsAdmin?: boolean,
    hasSound: boolean,
    currentSessionId: string | null,
    postValue?: string,
    postValueNodes?: Descendant[],
    rootPostTitle?: string,
    rootPostValue?: string,
    rootPostValueNodes?: Descendant[],
    rootPostPreviewValueNodes?: Descendant[],
    type: EDITOR_CONTEXT_TYPE,
    color: string,
    readOnly: boolean,
    readOnlyUsesPreviewSource?: boolean,
    readOnlyTrustedValueHash?: string,
    readOnlyTrustedValueVersion?: number,
    previewAudioEndPromptText?: string,
    readOnlyChapterId?: string,
    readOnlySectionId?: string,
    showHeaderTwoPartMarkers?: boolean,
    headerTwoPartCountOverride?: number | null,
    placeholder: string,
    setRef?: (ref: Editor) => void,
    hide?: boolean,
    bufferHeight: number,
    parentRef: HTMLElement | null,
    scrollParentRef?: React.RefObject<HTMLDivElement | null> | null,
    forcePointerCursor?: boolean,
    isAuthor: boolean,
    enableGallery?: boolean, // Whether we all users to generate image galleries in the post.
    acceptedFiles: File[],
    uploadingMedia: Map<string, IMediaItem>,
    setUploadingMedia: (mediaItem: IMediaItem) => void,
    updateUploadingMedia: (mediaItem: IMediaItem) => void,
    removeUploadingMedia: (id: string) => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    postValueIsSaving: boolean,
    savePost: (
        value: string,
        path?: number[],
        checkpointReason?: POST_COLLAB_DOC_CHECKPOINT_REASON,
    ) => void,
    setEditor: (editor: Editor | null) => void,
    postQuote: IAnnotationQuote | null,
    annotations: IAnnotationItem[],
    setAnnotations: React.Dispatch<React.SetStateAction<Map<string, IAnnotationItem>>>,
    chapters?: IReadPostChapterItem[],
    setAnnotationHighlightsRendered: React.Dispatch<React.SetStateAction<boolean>>,
    annotationHighlightsRendered: boolean,
    targetAnnotationID?: string | null,
    setRemeasurePostHeight: React.Dispatch<React.SetStateAction<boolean>>,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    currentZoomLevel: IZoomLevelItem | null,
    hasSuccess: boolean,
    hasError: boolean,
    selectedPostValuePath: number[],
    disableProgressiveReadOnly?: boolean,
    disableMergedWriteRouting?: boolean,
    collaborativeDocId?: string,
    collaborativeAuthToken?: string,
    collaborativeTestMode?: boolean,
    unusedZoomLevelCount: number,
    zoomLevelIcons: Map<ZOOM_LEVEL, string> | null,
    resizingAnnotationView: boolean,
    onInitializedValueChange?: (initialized: boolean) => void,
    onBodyPainted?: () => void,
    onReadOnlyChunkingIdleChange?: (idle: boolean) => void,
    onReadOnlyRenderedBlockCountChange?: (blockCount: number) => void,
    onReadOnlyParagraphCountChange?: (count: number) => void,
    onReadOnlyFullRenderReadyChange?: (ready: boolean) => void,
    onReadOnlyAudioPlaybackStateChange?: (playbackState: IReadOnlyAudioNotePlaybackState | null) => void,
    onNavigateInternalPostLink?: (target: IInternalPostLinkTarget) => void,
    readOnlyRevealParagraphId?: string | null,
    onReadOnlyRevealTargetResolutionChange?: (
        resolution: IReadOnlyRevealTargetResolution | null,
    ) => void,
    activeInternalPostLinkNavigationHref?: string | null,
    restorePostValueRequest?: IRestorePostValueRequest,
    onRestorePostValueApplied?: (requestId: string) => void,
    onCollaborativeParticipantsChange?: (participants: ICollaborativeCursorData[]) => void,
}
function PostEditor({
    customCursorActive,
    id,
    user,
    userIsAdmin = false,
    hasSound,
    currentSessionId,
    postValue,
    postValueNodes = undefined,
    rootPostTitle = undefined,
    rootPostValue = undefined,
    rootPostValueNodes = undefined,
    rootPostPreviewValueNodes = undefined,
    type,
    hide = false,
    color,
    setRef,
    isAuthor = false,
    readOnly = false,
    readOnlyUsesPreviewSource = false,
    readOnlyTrustedValueHash = undefined,
    readOnlyTrustedValueVersion = undefined,
    previewAudioEndPromptText = '',
    readOnlyChapterId = undefined,
    readOnlySectionId = undefined,
    showHeaderTwoPartMarkers = false,
    headerTwoPartCountOverride = null,
    parentRef,
    scrollParentRef = null,
    forcePointerCursor = false,
    placeholder = 'Type post...',
    bufferHeight = 0,
    enableGallery = true,
    acceptedFiles,
    uploadingMedia,
    setUploadingMedia,
    updateUploadingMedia,
    removeUploadingMedia,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    savePost,
    postValueIsSaving,
    setEditor,
    postQuote,
    annotations,
    setAnnotations,
    chapters,
    setAnnotationHighlightsRendered,
    annotationHighlightsRendered,
    targetAnnotationID,
    setRemeasurePostHeight,
    setSnackbarData,
    currentZoomLevel,
    hasSuccess,
    hasError,
    selectedPostValuePath,
    disableProgressiveReadOnly = false,
    disableMergedWriteRouting = false,
    collaborativeDocId,
    collaborativeAuthToken,
    collaborativeTestMode = false,
    unusedZoomLevelCount,
    zoomLevelIcons,
    resizingAnnotationView,
    onInitializedValueChange,
    onBodyPainted,
    onReadOnlyChunkingIdleChange,
    onReadOnlyRenderedBlockCountChange,
    onReadOnlyParagraphCountChange,
    onReadOnlyFullRenderReadyChange,
    onReadOnlyAudioPlaybackStateChange,
    onNavigateInternalPostLink,
    readOnlyRevealParagraphId = null,
    onReadOnlyRevealTargetResolutionChange,
    activeInternalPostLinkNavigationHref = null,
    restorePostValueRequest,
    onRestorePostValueApplied,
    onCollaborativeParticipantsChange,
}: Props): JSX.Element {
    // ===== General Information =====
    //
    // 1. Although there is logic that modifies the selection paths of annotation quotes
    // when it is subsumed within a zoom level creation or deletion, there is no
    // logic to return the selection path to its original state if a user undoes their
    // command via the keyboard versus manipulating the toolbars.
    //
    // 2. If there are already annotations within a selection when you add or remove a
    // zoom level, the annotations will be removed. This is because the path of
    // the annotations becomes out of sync with the path of the selection after the
    // zoom level is added or removed.

    // ===== General Constants =====

    const PORTABLE_TOOLBAR_BUTTON_LENGTH = 1.875 * BODY_FONT_SIZE;
    const POST_EDITOR_AUTOSAVE_DEBOUNCE_DURATION = 0.5 * MILLISECONDS_IN_A_SECOND;
    const POST_EDITOR_ANNOTATION_QUOTE_RECONCILIATION_DEBOUNCE_DURATION = 0.25 * MILLISECONDS_IN_A_SECOND;
    const shouldEmitInternalPostLinkConsoleLogs = useMemo(() => {
        if (process.env.NODE_ENV === 'production') {
            return false;
        }

        if (typeof window === 'undefined') {
            return shouldEnableInternalPostLinkConsoleLogs({
                envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
            });
        }

        let storedValue: string | null = null;
        try {
            storedValue = window.localStorage.getItem(INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY);
        } catch (_error) {
            storedValue = null;
        }

        return shouldEnableInternalPostLinkConsoleLogs({
            search: window.location.search,
            storedValue,
            envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
        });
    }, []);
    const logInternalPostLink = useCallback((
        event: string,
        payload?: Record<string, unknown>,
    ): void => {
        if (!shouldEmitInternalPostLinkConsoleLogs) return;

        // eslint-disable-next-line no-console
        console.info('[internal-post-link]', event, payload || {});
    }, [shouldEmitInternalPostLinkConsoleLogs]);

    // ===== Ref =====

    const containerRef = useRef<HTMLDivElement>(null);
    const selectionToolbarRef = useRef<HTMLDivElement | null>(null);
    const internalPostLinkScopePickerPreviewRef = useRef<HTMLDivElement | null>(null);

    // ----- Sound Clips
    const inputClickClip = useRef<HTMLAudioElement | null>(null);

    // ===== React Router =====

    const params = useParams();
    const collaborativeSession = useMemo(() => {
        if (
            readOnly
            || !collaborativeDocId
            || !collaborativeAuthToken
            || !process.env.REACT_APP_POST_COLLAB_WEBSOCKET_URL
        ) {
            return null;
        }

        return createPostCollaborativeSession({
            docId: collaborativeDocId,
            authToken: collaborativeAuthToken,
            test: collaborativeTestMode,
        });
    }, [
        readOnly,
        collaborativeDocId,
        collaborativeAuthToken,
        collaborativeTestMode,
    ]);
    const sharedRoot = useMemo(() => (
        collaborativeSession?.doc.get('sharedRoot', Y.XmlText) as Y.XmlText | undefined
    ), [collaborativeSession]);
    const usingCollaborativeDoc = !!sharedRoot;
    const collaborativeCursorData = useMemo(() => ({
        userId: user?.id,
        name: getCollaborativeCursorName(user),
        color: getCollaborativeCursorColor({
            seed: user?.id || getCollaborativeCursorName(user),
            colors: COLLABORATIVE_CURSOR_COLORS,
            defaultColor: DEFAULT_REMOTE_CURSOR_COLOR,
        }),
        photoURL: user?.photoURL,
    }), [
        user?.id,
        user?.firstName,
        user?.lastName,
        user?.photoURL,
    ]);

    // ===== State =====
    // Stores the position of the block toolbar
    const [blockToolbarPosition, setBlockToolbarPosition] = useState<ICoord>({
        y: 0,
        x: -52,
    });
    // Stores the position of the selection toolbar
    const [selectionToolbarPosition, setSelectionToolbarPosition] = useState<ICoord>({
        y: 24,
        x: -52,
    });
    // Stores whether we should update toolbar positions
    // Value updated everytime change occurs to editor
    const [updateToolbars, setUpdateToolbars] = useState<boolean>(false);
    // Stores whether selection toolbar is revealed (set in PortableToolbar)
    // Selection Toolbar is revealed when text is selected
    // Value is used to hide Block Toolbar when Selection Toolbar is active
    const [showSelectionToolbar, setShowSelectionToolbar] = useState<boolean>(false);
    // Drives the "select in post" link flow from the portable toolbar without
    // letting Slate move the original text selection while the author picks a target.
    const [internalPostLinkSelectionActive, setInternalPostLinkSelectionActive] = useState<boolean>(false);
    const [pendingInternalPostLinkTarget, setPendingInternalPostLinkTarget] = useState<IInternalPostLinkTarget | null>(null);
    const [internalPostLinkSelectionMode, setInternalPostLinkSelectionMode] = useState<INTERNAL_POST_LINK_SELECTION_MODE>(
        INTERNAL_POST_LINK_SELECTION_MODE.inline,
    );
    /**
     * The modal accepts through the main toolbar so we reuse its preserved text
     * selection and existing create/update-link flow without duplicating it here.
     */
    const [internalPostLinkSelectionSubmitKey, setInternalPostLinkSelectionSubmitKey] = useState<number>(0);
    const [internalPostLinkScopePickerVisible, setInternalPostLinkScopePickerVisible] = useState<boolean>(false);
    const [internalPostLinkScopePickerSelection, setInternalPostLinkScopePickerSelection] = useState<{
        scope: POST_CONTENT_SCOPE,
        chapterId: string | null,
        sectionId: string | null,
    }>({
        scope: POST_CONTENT_SCOPE.root,
        chapterId: null,
        sectionId: null,
    });
    // Stores whether we should rerender elements that have measuring logic
    // This occurs when we need to wait for rendering to complete, such as with images
    // in figures.
    const [rerenderMeasuringElements, setRerenderMeasuringElements] = useState<boolean>(false);
    // Stores whether audio is being recorded
    // Makes sure we record one audio note at a time
    const [isRecordingAudioNote, setIsRecordingAudioNote] = useState<boolean>(false);
    // Indicates whether we need to execute a save operation
    // Occurs when we try to save while already perfoming save operation
    const [queueSave, setQueueSave] = useState<boolean>(false);
    // Stores the state of the editor modal
    const [modal, setModal] = useState<{
        visible: boolean,
        message: IModalMessage,
        proceedCallback?:() => void,
        rejectCallback?: () => void,
            }>({
                visible: false,
                message: {},
            });
    // Determines the width of it's parent in order to modify width of editor
    const [parentWidth, setParentWidth] = useState<number | string | null>(parentRef
        ? parentRef.clientWidth
        : null);
    // Stores media item that is currently uploading
    const [uploadingStateChangeEvent, setUploadingStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    // Stores media item that has completed uploading
    const [uploadingCompleteEvent, setUploadingCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);
    const hasIncomingPostValue = postValue !== undefined || postValueNodes !== undefined;
    // Seed the cached path on first hydration so same-scope read-only updates
    // can take the append-safe refresh path immediately.
    const [cachedSelectedPostValuePath, setCachedSelectedPostValuePath] = useState<number[] | null>(() => (
        hasIncomingPostValue
            ? selectedPostValuePath
            : null
    ));
    // Restore requests intentionally skip quote reconciliation during the same
    // render pass. This tick schedules one follow-up pass so moved annotations
    // can still be re-anchored after the restore settles.
    const [annotationQuoteReconciliationTick, setAnnotationQuoteReconciliationTick] = useState<number>(0);
    const pendingCheckpointReasonRef = useRef<POST_COLLAB_DOC_CHECKPOINT_REASON | null>(null);
    const skipNextAnnotationQuoteReconciliationRef = useRef<boolean>(false);
    const pendingDeferredAnnotationQuoteReconciliationRef = useRef<boolean>(false);
    const appliedRestoreRequestIdRef = useRef<string | null>(null);
    const readOnlyEnhancementsResetKeyRef = useRef<string | null>(null);
    const autosaveTimeoutRef = useRef<number | null>(null);
    const suppressNextAutosaveRef = useRef<boolean>(hasIncomingPostValue);
    const suppressContentChangeCountRef = useRef<number>(hasIncomingPostValue ? 1 : 0);
    const collaborativeSeededDocIdRef = useRef<string | null>(null);
    const latestSlateValueRef = useRef<Descendant[]>(INITIAL_EDITOR_VALUE);
    const latestSelectedPostValuePathRef = useRef<number[]>(selectedPostValuePath);
    /**
     * Resolves the currently edited scope so internal post links can either use
     * the fast same-scope picker or seed the cross-scope modal with a sensible
     * starting location.
     */
    const currentInternalPostLinkScopeSelection = useMemo(() => {
        if (!chapters || selectedPostValuePath.length === 0) {
            return {
                scope: POST_CONTENT_SCOPE.root,
                chapterId: null,
                sectionId: null,
            };
        }

        const selectedChapter = chapters[selectedPostValuePath[0]];
        if (!selectedChapter) {
            return {
                scope: POST_CONTENT_SCOPE.root,
                chapterId: null,
                sectionId: null,
            };
        }

        if (selectedPostValuePath.length < 2) {
            return {
                scope: POST_CONTENT_SCOPE.chapter,
                chapterId: selectedChapter.id,
                sectionId: null,
            };
        }

        const selectedSection = selectedChapter.sections?.[selectedPostValuePath[1]];
        return {
            scope: POST_CONTENT_SCOPE.section,
            chapterId: selectedChapter.id,
            sectionId: selectedSection?.id || null,
        };
    }, [
        chapters,
        selectedPostValuePath,
    ]);
    /**
     * The cross-scope picker should only surface scopes that contain at least
     * one selectable heading, paragraph, or block quote destination.
     */
    const resolveInternalPostLinkScopeContent = useCallback((
        path: number[],
        fallbackValue?: string,
        fallbackValueNodes?: Descendant[],
    ): {
        postValue?: string,
        postValueNodes?: Descendant[],
    } => {
        const matchesCurrentPath = (
            path.length === selectedPostValuePath.length
            && path.every((segment, index) => segment === selectedPostValuePath[index])
        );

        if (matchesCurrentPath) {
            return {
                postValue,
                postValueNodes,
            };
        }

        return {
            postValue: fallbackValue,
            postValueNodes: fallbackValueNodes,
        };
    }, [
        selectedPostValuePath,
        postValue,
        postValueNodes,
    ]);
    const internalPostLinkScopePickerHasRootScopeTargets = useMemo(() => {
        const {
            postValue: rootScopeValue,
            postValueNodes: rootScopeValueNodes,
        } = resolveInternalPostLinkScopeContent(
            [],
            rootPostValue,
            rootPostValueNodes,
        );
        const selectableNodes = resolveInternalPostLinkSelectableTargetNodes(
            rootScopeValue,
            rootScopeValueNodes,
        );

        return selectableNodes != null
            && slateValueHasSelectableInternalPostLinkTargets(selectableNodes);
    }, [
        resolveInternalPostLinkScopeContent,
        rootPostValue,
        rootPostValueNodes,
    ]);
    const internalPostLinkScopePickerAvailableChapters = useMemo(() => (
        (chapters || []).filter((chapter, chapterIndex) => {
            const {
                postValue: chapterValue,
                postValueNodes: chapterValueNodes,
            } = resolveInternalPostLinkScopeContent(
                [chapterIndex],
                chapter.value,
                chapter.valueNodes,
            );
            const selectableNodes = resolveInternalPostLinkSelectableTargetNodes(
                chapterValue,
                chapterValueNodes,
            );

            return selectableNodes != null
                && slateValueHasSelectableInternalPostLinkTargets(selectableNodes);
        })
    ), [
        chapters,
        resolveInternalPostLinkScopeContent,
    ]);
    const resolveInternalPostLinkScopePickerAvailableSections = useCallback((
        chapterId: string | null,
    ): IReadPostSectionItem[] => {
        if (!chapterId) {
            return [];
        }

        const chapterIndex = (chapters || []).findIndex((chapter) => chapter.id === chapterId);
        if (chapterIndex < 0) {
            return [];
        }

        const selectedChapter = chapters?.[chapterIndex];
        if (!selectedChapter) {
            return [];
        }

        return (selectedChapter.sections || []).filter((section, sectionIndex) => {
            const {
                postValue: sectionValue,
                postValueNodes: sectionValueNodes,
            } = resolveInternalPostLinkScopeContent(
                [chapterIndex, sectionIndex],
                section.value,
                section.valueNodes,
            );
            const selectableNodes = resolveInternalPostLinkSelectableTargetNodes(
                sectionValue,
                sectionValueNodes,
            );

            return selectableNodes != null
                && slateValueHasSelectableInternalPostLinkTargets(selectableNodes);
        });
    }, [
        chapters,
        resolveInternalPostLinkScopeContent,
    ]);
    const internalPostLinkScopePickerAvailableSectionChapters = useMemo(() => (
        (chapters || []).filter((chapter) => (
            resolveInternalPostLinkScopePickerAvailableSections(chapter.id).length > 0
        ))
    ), [
        chapters,
        resolveInternalPostLinkScopePickerAvailableSections,
    ]);
    const canShowInternalPostLinkChapterScopeButton = internalPostLinkScopePickerAvailableChapters.length > 0;
    const canShowInternalPostLinkSectionScopeButton = internalPostLinkScopePickerAvailableSectionChapters.length > 0;
    const currentInternalPostLinkScopeLabel = useMemo(() => {
        if (currentInternalPostLinkScopeSelection.scope === POST_CONTENT_SCOPE.section) {
            return INTERNAL_POST_LINK_SCOPE_LABEL.section;
        }

        if (currentInternalPostLinkScopeSelection.scope === POST_CONTENT_SCOPE.chapter) {
            return INTERNAL_POST_LINK_SCOPE_LABEL.chapter;
        }

        return INTERNAL_POST_LINK_SCOPE_LABEL.post;
    }, [currentInternalPostLinkScopeSelection.scope]);
    const internalPostLinkScopePickerSelectedChapterAvailableSections = useMemo(() => (
        resolveInternalPostLinkScopePickerAvailableSections(internalPostLinkScopePickerSelection.chapterId)
    ), [
        internalPostLinkScopePickerSelection.chapterId,
        resolveInternalPostLinkScopePickerAvailableSections,
    ]);
    const internalPostLinkScopePickerChapterScopeOptions = useMemo(() => (
        internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.section
            ? internalPostLinkScopePickerAvailableSectionChapters
            : internalPostLinkScopePickerAvailableChapters
    ), [
        internalPostLinkScopePickerAvailableChapters,
        internalPostLinkScopePickerAvailableSectionChapters,
        internalPostLinkScopePickerSelection.scope,
    ]);
    const editableUsesPointerCursor = internalPostLinkSelectionActive || forcePointerCursor;

    /**
     * The custom cursor system needs the actual Slate editable hover target,
     * not the outer post-editor shell, when we force a cursor transition
     * programmatically.
     */
    const resolveEditableCursorTarget = useCallback((): HTMLElement | undefined => {
        const editableElement = containerRef.current?.querySelector?.(
            `.${SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME}`,
        ) as HTMLElement | null;

        return editableElement || undefined;
    }, []);

    /**
     * Entering internal-link destination mode can briefly clear the toolbar
     * hover cursor before the editor owns the interaction again, so provide
     * one shared way to force the editor-surface pointer state back on.
     */
    const activateEditorPointerCursor = useCallback((): void => {
        if (
            !customCursorActive
            || resizingAnnotationView
        ) {
            return;
        }

        onCursorEnter(
            CURSOR_TARGET.editorButton,
            [CURSOR_SIGN.click],
            resolveEditableCursorTarget(),
        );
    }, [
        customCursorActive,
        onCursorEnter,
        resolveEditableCursorTarget,
        resizingAnnotationView,
    ]);

    /**
     * Starting destination-pick mode clears the toolbar hover cursor first, so
     * explicitly re-arm the editor pointer cursor even if the mouse is already
     * resting over the editable surface and no fresh mouseenter fires. We also
     * repeat that on the next animation frame so any toolbar unmount mouseleave
     * cleanup cannot win the race and clear the cursor again.
     */
    useEffect(() => {
        if (!editableUsesPointerCursor) {
            return;
        }

        activateEditorPointerCursor();
        const frameId = window.requestAnimationFrame(() => {
            activateEditorPointerCursor();
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [
        activateEditorPointerCursor,
        editableUsesPointerCursor,
    ]);

    /**
     * Keeps the scope picker aligned with either the current editing location
     * or the last accepted target when the author wants to redo a cross-scope link.
     */
    const resolveInternalPostLinkScopePickerSelection = useCallback((
        target?: IInternalPostLinkTarget | null,
    ): {
        scope: POST_CONTENT_SCOPE,
        chapterId: string | null,
        sectionId: string | null,
    } => {
        if (target?.sectionId) {
            return {
                scope: POST_CONTENT_SCOPE.section,
                chapterId: target.chapterId || null,
                sectionId: target.sectionId,
            };
        }

        if (target?.chapterId) {
            return {
                scope: POST_CONTENT_SCOPE.chapter,
                chapterId: target.chapterId,
                sectionId: null,
            };
        }

        return currentInternalPostLinkScopeSelection;
    }, [currentInternalPostLinkScopeSelection]);
    /**
     * Resolves the preview content shown inside the cross-scope picker modal.
     * The active editor scope always wins so the modal reflects unsaved edits
     * from the currently open draft when possible.
     */
    const internalPostLinkScopePickerPreview = useMemo((): {
        path: number[],
        postValue?: string,
        postValueNodes?: Descendant[],
        available: boolean,
    } => {
        const selection = internalPostLinkScopePickerSelection;
        const matchesCurrentPath = (candidatePath: number[]): boolean => (
            candidatePath.length === selectedPostValuePath.length
            && candidatePath.every((segment, index) => segment === selectedPostValuePath[index])
        );
        const buildPreviewResult = (
            path: number[],
            value?: string,
            valueNodes?: Descendant[],
        ): {
            path: number[],
            postValue?: string,
            postValueNodes?: Descendant[],
        } => ({
            path,
            postValue: matchesCurrentPath(path) ? postValue : value,
            postValueNodes: matchesCurrentPath(path) ? postValueNodes : valueNodes,
        });

        if (selection.scope === POST_CONTENT_SCOPE.root) {
            const rootPreviewValue = selectedPostValuePath.length === 0
                ? postValue
                : rootPostValue;
            const rootPreviewValueNodes = selectedPostValuePath.length === 0
                ? postValueNodes
                : rootPostValueNodes;
            const preview = buildPreviewResult([], rootPreviewValue, rootPreviewValueNodes);
            return {
                ...preview,
                available: preview.postValue != null || preview.postValueNodes != null,
            };
        }

        const chapterIndex = (chapters || []).findIndex((chapter) => chapter.id === selection.chapterId);
        if (chapterIndex < 0) {
            return {
                path: [],
                postValue: undefined,
                postValueNodes: undefined,
                available: false,
            };
        }

        const selectedChapter = chapters?.[chapterIndex];
        if (!selectedChapter) {
            return {
                path: [],
                postValue: undefined,
                postValueNodes: undefined,
                available: false,
            };
        }

        if (selection.scope === POST_CONTENT_SCOPE.chapter || !selection.sectionId) {
            const preview = buildPreviewResult(
                [chapterIndex],
                selectedChapter.value,
                selectedChapter.valueNodes,
            );
            return {
                ...preview,
                available: preview.postValue != null || preview.postValueNodes != null,
            };
        }

        const sectionIndex = (selectedChapter.sections || []).findIndex((section) => section.id === selection.sectionId);
        if (sectionIndex < 0) {
            return {
                path: [chapterIndex],
                postValue: undefined,
                postValueNodes: undefined,
                available: false,
            };
        }

        const selectedSection = selectedChapter.sections?.[sectionIndex];
        const preview = buildPreviewResult(
            [chapterIndex, sectionIndex],
            selectedSection?.value,
            selectedSection?.valueNodes,
        );
        return {
            ...preview,
            available: preview.postValue != null || preview.postValueNodes != null,
        };
    }, [
        internalPostLinkScopePickerSelection,
        chapters,
        selectedPostValuePath,
        postValue,
        postValueNodes,
        rootPostValue,
        rootPostValueNodes,
    ]);
    const internalPostLinkScopePickerStagedTargetSummary = useMemo((): {
        title: string,
        scopeLabel: string,
    } | null => {
        if (
            internalPostLinkSelectionMode !== INTERNAL_POST_LINK_SELECTION_MODE.modal
            || !pendingInternalPostLinkTarget
        ) {
            return null;
        }

        const targetTitle = `${pendingInternalPostLinkTarget.label || ''}`.trim() || 'Destination selected';
        if (!pendingInternalPostLinkTarget.chapterId) {
            return {
                title: targetTitle,
                scopeLabel: 'Post',
            };
        }

        const targetChapter = chapters?.find((chapter) => chapter.id === pendingInternalPostLinkTarget.chapterId);
        if (!pendingInternalPostLinkTarget.sectionId) {
            return {
                title: targetTitle,
                scopeLabel: targetChapter?.title || 'Chapter',
            };
        }

        const targetSection = targetChapter?.sections?.find(
            (section) => section.id === pendingInternalPostLinkTarget.sectionId,
        );

        return {
            title: targetTitle,
            scopeLabel: [targetChapter?.title || 'Chapter', targetSection?.title || 'Section']
                .filter((segment) => segment.trim().length > 0)
                .join(' / '),
        };
    }, [
        chapters,
        internalPostLinkSelectionMode,
        pendingInternalPostLinkTarget,
    ]);
    const suppressNextProgrammaticContentChange = useCallback((): void => {
        suppressContentChangeCountRef.current += 1;
    }, []);
    const getSavedSlateValue = (): Descendant[] => {
        if (sharedRoot && sharedRoot.toDelta().length > 0) {
            return ensureNonEmptySlateValue(
                stripBottomBufferNodes(
                    yTextToSlateElement(sharedRoot).children as Descendant[],
                    (node) => BottomBufferElement.isBottomBufferElement(node as Node),
                ),
                INITIAL_EDITOR_VALUE,
            );
        }

        const postContent = postValue;
        const serializedPostContentAvailable = !!(
            postContent
            && postContent.length > 0
        );
        const incomingPostValueNodes = postValueNodes;
        if (!serializedPostContentAvailable && incomingPostValueNodes == null) {
            return INITIAL_EDITOR_VALUE;
        }

        const normalizedPostValueCacheKey = getNormalizedPostValueCacheKey({
            postContent,
            postValueNodes: incomingPostValueNodes,
            readOnly,
            readOnlyTrustedValueVersion,
            readOnlyTrustedValueHash,
        });
        const cachedNormalizedPostValue = getCachedNormalizedPostValue(normalizedPostValueCacheKey);
        if (cachedNormalizedPostValue) {
            return cachedNormalizedPostValue;
        }

        let contentTree;
        if (incomingPostValueNodes != null) {
            contentTree = incomingPostValueNodes;
        } else {
            try {
                contentTree = JSON.parse(postContent as string);
            } catch (error) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `There was a problem interpreting the post content. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        },
                    });
                }
                throw Error(`There was a problem interpreting the post content. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
            }
        }

        if (canUseTrustedReadOnlySlateValue({
            readOnly,
            postContent,
            parsedValue: contentTree,
            readOnlyTrustedValueVersion,
            readOnlyTrustedValueHash,
        })) {
            const trustedSlateValue = ensureNonEmptySlateValue(contentTree, INITIAL_EDITOR_VALUE);
            setCachedNormalizedPostValue(normalizedPostValueCacheKey, trustedSlateValue);
            return trustedSlateValue;
        }

        const tempEditor = EDITOR_PLUGINS
            .reduce((
                compositeEditor: Editor,
                withPlugin: (editor: Editor, readOnly?: boolean) => Editor,
            ) => withPlugin(compositeEditor, readOnly), createEditor());
        tempEditor.children = contentTree;
        Editor.normalize(tempEditor, { force: true });
        const parsedSlateValue = usingCollaborativeDoc
            ? stripBottomBufferNodes(
                tempEditor.children as Descendant[],
                (node) => BottomBufferElement.isBottomBufferElement(node as Node),
            )
            : (tempEditor.children as Descendant[]);
        const normalizedSlateValue = ensureNonEmptySlateValue(parsedSlateValue, INITIAL_EDITOR_VALUE);
        setCachedNormalizedPostValue(normalizedPostValueCacheKey, normalizedSlateValue);
        return normalizedSlateValue;
    };
    const savedSlateValue = useMemo(getSavedSlateValue, [
        postValue,
        postValueNodes,
        readOnly,
        readOnlyTrustedValueVersion,
        readOnlyTrustedValueHash,
        sharedRoot,
    ]);
    const slateValue = useMemo(() => ensureReadOnlyZoomFiveBottomBuffer({
        slateValue: savedSlateValue,
        readOnly,
        currentZoomLevel,
    }), [
        savedSlateValue,
        readOnly,
        currentZoomLevel,
    ]);
    const fullReadOnlyParagraphCount = useMemo(() => (
        countParagraphNodesInSlateValue(slateValue)
    ), [slateValue]);
    latestSlateValueRef.current = slateValue;
    latestSelectedPostValuePathRef.current = selectedPostValuePath;
    useEffect(() => {
        setInternalPostLinkSelectionActive(false);
        setPendingInternalPostLinkTarget(null);
        setInternalPostLinkScopePickerVisible(false);
        setInternalPostLinkSelectionMode(INTERNAL_POST_LINK_SELECTION_MODE.inline);
    }, [selectedPostValuePath]);
    const internalPostLinkPreviewChapters = useMemo(() => (
        hydrateCurrentScopeIntoInternalPostLinkChapters({
            chapters,
            selectedPostValuePath,
            postValue,
            postValueNodes,
            readOnlyUsesPreviewSource,
        })
    ), [
        chapters,
        selectedPostValuePath,
        postValue,
        postValueNodes,
        readOnlyUsesPreviewSource,
    ]);
    const progressiveReadOnlyEnabled = readOnly && !usingCollaborativeDoc && !disableProgressiveReadOnly;
    const [visibleReadOnlyBlockCount, setVisibleReadOnlyBlockCount] = useState<number>(READ_ONLY_INITIAL_BLOCK_COUNT);
    const displaySlateValue = useMemo(() => (
        progressiveReadOnlyEnabled
            ? slateValue.slice(0, visibleReadOnlyBlockCount)
            : slateValue
    ), [
        progressiveReadOnlyEnabled,
        slateValue,
        visibleReadOnlyBlockCount,
    ]);
    const readOnlyRevealTargetResolution = useMemo((): IReadOnlyRevealTargetResolution | null => {
        const normalizedParagraphId = `${readOnlyRevealParagraphId || ''}`.trim();
        if (!normalizedParagraphId) {
            return null;
        }

        const requiredVisibleBlockCount = resolveInternalPostLinkRevealBlockCount({
            slateValue,
            paragraphId: normalizedParagraphId,
            bufferBlockCount: READ_ONLY_INTERNAL_LINK_REVEAL_BUFFER_BLOCK_COUNT,
        });
        return {
            paragraphId: normalizedParagraphId,
            found: requiredVisibleBlockCount != null,
            requiredVisibleBlockCount,
        };
    }, [
        readOnlyRevealParagraphId,
        slateValue,
    ]);
    useEffect(() => {
        if (!readOnlyRevealParagraphId) {
            return;
        }

        logInternalPostLink('postEditorRevealResolutionComputed', {
            paragraphId: readOnlyRevealParagraphId,
            found: readOnlyRevealTargetResolution?.found ?? null,
            requiredVisibleBlockCount: readOnlyRevealTargetResolution?.requiredVisibleBlockCount ?? null,
            visibleReadOnlyBlockCount,
            totalBlockCount: slateValue.length,
            readOnlyUsesPreviewSource,
            selectedPostValuePath,
        });
    }, [
        logInternalPostLink,
        readOnlyRevealParagraphId,
        readOnlyRevealTargetResolution,
        visibleReadOnlyBlockCount,
        slateValue.length,
        readOnlyUsesPreviewSource,
        selectedPostValuePath,
    ]);
    // Keep the memoized Slate editor pointed at the latest visible read-only
    // slice without recreating the editor instance on every chunk release.
    const latestDisplaySlateValueRef = useRef(displaySlateValue);
    latestDisplaySlateValueRef.current = displaySlateValue;
    const previousRenderedReadOnlyLengthRef = useRef<number>(displaySlateValue.length);
    // Indicates whether we've set initial post value
    const [initializedValue, setInitializedValue] = useState<boolean>(hasIncomingPostValue);
    // Stores the slate editor value
    const [value, setValue] = useState(displaySlateValue);
    const [readOnlyEnhancementsReady, setReadOnlyEnhancementsReady] = useState<boolean>(!readOnly);
    // Tracks read-only audio note playback snapshots so the active note can
    // drive both the inline word highlight and the reader's floating mini-player.
    const [readOnlyAudioPlaybackStatesById, setReadOnlyAudioPlaybackStatesById] = useState<Map<string, IReadOnlyAudioNotePlaybackState>>(
        new Map(),
    );
    const readOnlyAudioPlaybackControlsRef = useRef<Map<string, IReadOnlyAudioPlaybackControls>>(new Map());
    const readOnlyAudioTextSyncCacheRef = useRef<Map<string, IAudioTextSyncResult>>(new Map());
    const readOnlyAudioWarmupTargetsRef = useRef<Array<{
        id: string,
        cacheKey: string,
        transcriptWords: IReadOnlyAudioNotePlaybackState['transcriptWords'],
    }>>([]);
    const [readOnlyAudioTextSyncCacheVersion, setReadOnlyAudioTextSyncCacheVersion] = useState<number>(0);
    const readOnlyPrefetchSentinelRef = useRef<HTMLDivElement>(null);
    const readOnlyChunkLoaderRef = useRef<HTMLDivElement>(null);
    // Share one RAF throttle between the initial prefetch check and the scroll/resize
    // listeners so read-only chunk growth never schedules duplicate appends.
    const readOnlyPrefetchAnimationFrameRef = useRef<number | null>(null);
    // Stores the scroll position we must preserve across the next append-sized
    // read-only rerender.
    const pendingReadOnlyAppendScrollLockRef = useRef<IReadOnlyAppendScrollLock | null>(null);
    const [readOnlyChunkPlaceholderBlockCount, setReadOnlyChunkPlaceholderBlockCount] = useState<number>(
        READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_COUNT,
    );
    const selectedPostValuePathKey = JSON.stringify(selectedPostValuePath || []);
    const readOnlyEnhancementsResetKey = JSON.stringify({
        postValue,
        readOnly,
        selectedPostValuePathKey,
        usingCollaborativeDoc,
        disableProgressiveReadOnly,
    });
    const lightweightReadOnly = readOnly && !readOnlyEnhancementsReady;
    // ReaderView sometimes scrolls its own container instead of the window, so
    // read-only chunk locking has to normalize whichever scroll surface is live.
    const getReadOnlyActiveScrollTop = useCallback((): number => {
        const scrollContainer = scrollParentRef?.current;
        return scrollContainer
            ? scrollContainer.scrollTop
            : Math.max(
                window.scrollY || 0,
                document.documentElement.scrollTop || 0,
                document.body.scrollTop || 0,
            );
    }, [scrollParentRef]);

    useEffect(() => {
        if (!progressiveReadOnlyEnabled) {
            if (visibleReadOnlyBlockCount !== READ_ONLY_INITIAL_BLOCK_COUNT) {
                setVisibleReadOnlyBlockCount(READ_ONLY_INITIAL_BLOCK_COUNT);
            }
            return;
        }

        setVisibleReadOnlyBlockCount(Math.min(
            READ_ONLY_INITIAL_BLOCK_COUNT,
            slateValue.length,
        ));
    }, [
        progressiveReadOnlyEnabled,
        postValue,
        selectedPostValuePathKey,
        slateValue.length,
    ]);

    useEffect(() => {
        if (
            !progressiveReadOnlyEnabled
            || !initializedValue
            || visibleReadOnlyBlockCount >= slateValue.length
        ) {
            return undefined;
        }
        const maybePrefetchNextBatch = (): void => {
            if (readOnlyPrefetchAnimationFrameRef.current !== null) return;
            readOnlyPrefetchAnimationFrameRef.current = window.requestAnimationFrame(() => {
                readOnlyPrefetchAnimationFrameRef.current = null;
                if (!readOnlyPrefetchSentinelRef.current) return;

                const rect = readOnlyPrefetchSentinelRef.current.getBoundingClientRect();
                if (rect.top <= window.innerHeight + READ_ONLY_PREFETCH_DISTANCE) {
                    setVisibleReadOnlyBlockCount((currentCount) => Math.min(
                        currentCount + READ_ONLY_BATCH_BLOCK_COUNT,
                        slateValue.length,
                    ));
                }
            });
        };

        maybePrefetchNextBatch();

        return () => {
            if (readOnlyPrefetchAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(readOnlyPrefetchAnimationFrameRef.current);
                readOnlyPrefetchAnimationFrameRef.current = null;
            }
        };
    }, [
        initializedValue,
        progressiveReadOnlyEnabled,
        scrollParentRef,
        slateValue.length,
        visibleReadOnlyBlockCount,
    ]);
    // ReaderView may scroll inside its own container instead of the window, so the
    // progressive read-only prefetch listener has to subscribe to the real scroll parent.
    const readOnlyPrefetchScrollTarget = progressiveReadOnlyEnabled
        && initializedValue
        && visibleReadOnlyBlockCount < slateValue.length
        ? (scrollParentRef?.current || window)
        : null;
    const readOnlyChunkAppendPending = (
        progressiveReadOnlyEnabled
        && visibleReadOnlyBlockCount < slateValue.length
    );
    const shouldShowReadOnlyChunkPlaceholder = readOnlyChunkAppendPending;
    const updateReadOnlyChunkPlaceholderBlockCount = useCallback(
        () => {
            if (!shouldShowReadOnlyChunkPlaceholder || !readOnlyChunkLoaderRef.current) return;

            const loaderRect = readOnlyChunkLoaderRef.current.getBoundingClientRect();
            const scrollViewportBottom = scrollParentRef?.current?.getBoundingClientRect().bottom
                || window.innerHeight;
            const visibleHeight = Math.max(
                scrollViewportBottom - loaderRect.top,
                READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_HEIGHT,
            );
            const nextBlockCount = Math.max(
                READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_COUNT,
                Math.ceil(
                    (visibleHeight + READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_GAP)
                    / (
                        READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_HEIGHT
                        + READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_GAP
                    ),
                ),
            );

            // Never shrink the placeholder during an append; collapsing it in
            // the same frame as new text lands causes visible upward jumps.
            setReadOnlyChunkPlaceholderBlockCount((currentCount) => (
                Math.max(currentCount, nextBlockCount)
            ));
        },
        [
            shouldShowReadOnlyChunkPlaceholder,
            scrollParentRef,
        ],
    );
    const maybePrefetchNextReadOnlyBatch = useCallback(
        () => {
            if (!readOnlyPrefetchScrollTarget) return;
            if (readOnlyPrefetchAnimationFrameRef.current !== null) return;
            readOnlyPrefetchAnimationFrameRef.current = window.requestAnimationFrame(() => {
                readOnlyPrefetchAnimationFrameRef.current = null;
                if (!readOnlyPrefetchSentinelRef.current) return;

                const rect = readOnlyPrefetchSentinelRef.current.getBoundingClientRect();
                if (rect.top <= window.innerHeight + READ_ONLY_PREFETCH_DISTANCE) {
                    setVisibleReadOnlyBlockCount((currentCount) => Math.min(
                        currentCount + READ_ONLY_BATCH_BLOCK_COUNT,
                        slateValue.length,
                    ));
                }
            });
        },
        [
            readOnlyPrefetchScrollTarget,
            slateValue.length,
        ],
    );
    useLayoutEffect(() => {
        if (!shouldShowReadOnlyChunkPlaceholder) return;
        updateReadOnlyChunkPlaceholderBlockCount();
    }, [
        shouldShowReadOnlyChunkPlaceholder,
        visibleReadOnlyBlockCount,
        updateReadOnlyChunkPlaceholderBlockCount,
    ]);
    useEffect(() => {
        if (
            !shouldShowReadOnlyChunkPlaceholder
            && readOnlyChunkPlaceholderBlockCount !== READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_COUNT
        ) {
            setReadOnlyChunkPlaceholderBlockCount(READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_COUNT);
        }
    }, [
        shouldShowReadOnlyChunkPlaceholder,
        readOnlyChunkPlaceholderBlockCount,
    ]);
    // Keep the listener registration declarative so target swaps between window and a
    // ReaderView scroll container do not leave behind stale manual listeners.
    useEventListener(
        'scroll',
        maybePrefetchNextReadOnlyBatch,
        readOnlyPrefetchScrollTarget,
    );
    useEventListener(
        'resize',
        maybePrefetchNextReadOnlyBatch,
        readOnlyPrefetchScrollTarget ? window : null,
    );
    useEventListener(
        'scroll',
        updateReadOnlyChunkPlaceholderBlockCount,
        shouldShowReadOnlyChunkPlaceholder ? (scrollParentRef?.current || window) : null,
    );
    useEventListener(
        'resize',
        updateReadOnlyChunkPlaceholderBlockCount,
        shouldShowReadOnlyChunkPlaceholder ? window : null,
    );
    const editor = useMemo(() => {
        let baseEditor = createEditor();

        if (sharedRoot && collaborativeSession) {
            baseEditor = withCursors(
                withYjs(baseEditor, sharedRoot, { autoConnect: false }),
                collaborativeSession.provider.awareness,
                {
                    data: collaborativeCursorData,
                },
            );
        } else if (sharedRoot) {
            baseEditor = withYjs(baseEditor, sharedRoot, { autoConnect: false });
        }

        const nextEditor = EDITOR_PLUGINS
            .reduce((compositeEditor, withPlugin) => withPlugin(compositeEditor, readOnly), baseEditor);
        nextEditor.children = ensureNonEmptySlateValue(
            latestDisplaySlateValueRef.current,
            INITIAL_EDITOR_VALUE,
        );
        return nextEditor;
    }, [readOnly, sharedRoot, collaborativeSession]);
    // Capture the reader's current position before a same-scope read-only
    // refresh grows the rendered tree.
    const captureReadOnlyAppendScrollLock = useCallback((): void => {
        if (!readOnly) return;

        const scrollContainer = scrollParentRef?.current;
        pendingReadOnlyAppendScrollLockRef.current = {
            top: getReadOnlyActiveScrollTop(),
            useWindowScroll: !scrollContainer,
        };
    }, [getReadOnlyActiveScrollTop, readOnly, scrollParentRef]);
    // Reapply the exact pre-append scrollTop during layout so new chunks commit
    // without the reader ever seeing an intermediate jump.
    const applyReadOnlyAppendScrollLock = useCallback((scrollLock: IReadOnlyAppendScrollLock): void => {
        if (scrollLock.useWindowScroll) {
            const { documentElement, body } = document;
            const previousDocumentScrollBehavior = documentElement.style.scrollBehavior;
            const previousBodyScrollBehavior = body.style.scrollBehavior;

            documentElement.style.scrollBehavior = 'auto';
            body.style.scrollBehavior = 'auto';
            documentElement.scrollTop = scrollLock.top;
            body.scrollTop = scrollLock.top;
            window.scrollTo({
                top: scrollLock.top,
                behavior: 'auto',
            });
            documentElement.style.scrollBehavior = previousDocumentScrollBehavior;
            body.style.scrollBehavior = previousBodyScrollBehavior;
            return;
        }

        const scrollContainer = scrollParentRef?.current;
        if (!scrollContainer) {
            return;
        }

        const previousScrollBehavior = scrollContainer.style.scrollBehavior;
        scrollContainer.style.scrollBehavior = 'auto';
        scrollContainer.scrollTop = scrollLock.top;
        scrollContainer.style.scrollBehavior = previousScrollBehavior;
    }, [scrollParentRef]);
    // Remove collapsed selections before chunk growth so Slate and the browser
    // do not try to chase a hidden caret in read-only mode.
    const clearCollapsedReadOnlySelection = useCallback((): void => {
        if (!readOnly) return;

        const nativeSelection = window.getSelection();
        const hasExpandedSelection = !!nativeSelection
            && nativeSelection.rangeCount > 0
            && !nativeSelection.isCollapsed;

        if (hasExpandedSelection) {
            return;
        }
        if (ReactEditor.isFocused(editor)) {
            ReactEditor.blur(editor);
        }
        if (nativeSelection && nativeSelection.rangeCount > 0) {
            nativeSelection.removeAllRanges();
        }
        if (editor.selection) {
            try {
                Transforms.deselect(editor);
            } catch (_error) {
                // Ignore stale read-only selection races while chunking is appending.
            }
            editor.selection = null;
        }
    }, [editor, readOnly]);
    useEffect(() => {
        if (
            !progressiveReadOnlyEnabled
            || !initializedValue
            || !readOnlyRevealTargetResolution?.found
            || readOnlyRevealTargetResolution.requiredVisibleBlockCount == null
            || readOnlyRevealTargetResolution.requiredVisibleBlockCount <= visibleReadOnlyBlockCount
        ) {
            return;
        }

        logInternalPostLink('postEditorRevealGrowingVisibleBlockCount', {
            paragraphId: readOnlyRevealTargetResolution.paragraphId,
            fromVisibleBlockCount: visibleReadOnlyBlockCount,
            toVisibleBlockCount: readOnlyRevealTargetResolution.requiredVisibleBlockCount,
            totalBlockCount: slateValue.length,
            readOnlyUsesPreviewSource,
            selectedPostValuePath,
        });
        captureReadOnlyAppendScrollLock();
        clearCollapsedReadOnlySelection();
        setVisibleReadOnlyBlockCount((currentCount) => Math.max(
            currentCount,
            readOnlyRevealTargetResolution.requiredVisibleBlockCount || currentCount,
        ));
    }, [
        captureReadOnlyAppendScrollLock,
        clearCollapsedReadOnlySelection,
        initializedValue,
        progressiveReadOnlyEnabled,
        readOnlyRevealTargetResolution,
        visibleReadOnlyBlockCount,
        logInternalPostLink,
        readOnlyUsesPreviewSource,
        selectedPostValuePath,
        slateValue.length,
    ]);

    /**
     * Browsers can restore a stale collapsed DOM selection into the read-only
     * editor subtree while ReaderView is mounting. Clearing that caret before
     * Slate reacts prevents intermittent "Cannot resolve a Slate point from DOM
     * point" crashes on initial load.
     */
    useLayoutEffect(() => {
        clearCollapsedReadOnlySelection();
    }, [
        clearCollapsedReadOnlySelection,
        displaySlateValue,
        readOnly,
    ]);
    // Render from the live normalized tree so transient callback payloads
    // cannot drift from Slate's actual children during local edits.
    const renderedSlateValue = useMemo(() => (
        ensureNonEmptySlateValue(editor.children as Descendant[], INITIAL_EDITOR_VALUE)
    ), [editor, value]);
    // Read-only progressive chunking only renders a prefix of the post at
    // first, but the reader-facing Part counts should always reflect the full
    // normalized post instead of the currently released slice.
    const headerTwoPartNumberSourceValue = useMemo(() => (
        readOnly
            ? slateValue
            : renderedSlateValue
    ), [
        readOnly,
        renderedSlateValue,
        slateValue,
    ]);
    const headerTwoPartNumbersByPath = useMemo(() => (
        buildHeaderPartNumberByPath(headerTwoPartNumberSourceValue, (node) => (
            HeaderElement.isH2(node)
            && !HeaderElement.hidesPartMarker(node)
        ))
    ), [headerTwoPartNumberSourceValue]);
    const headerTwoPartCount = headerTwoPartCountOverride ?? headerTwoPartNumbersByPath.size;
    // Apply the captured scroll lock in layout immediately after a read-only
    // append grows the rendered tree.
    useLayoutEffect(() => {
        const previousRenderedLength = previousRenderedReadOnlyLengthRef.current;
        previousRenderedReadOnlyLengthRef.current = renderedSlateValue.length;

        if (!readOnly) {
            pendingReadOnlyAppendScrollLockRef.current = null;
            return;
        }
        if (renderedSlateValue.length <= previousRenderedLength) {
            return;
        }

        const scrollLock = pendingReadOnlyAppendScrollLockRef.current;
        if (!scrollLock) return;

        applyReadOnlyAppendScrollLock(scrollLock);
        pendingReadOnlyAppendScrollLockRef.current = null;
    }, [
        applyReadOnlyAppendScrollLock,
        readOnly,
        renderedSlateValue.length,
    ]);
    const readOnlyFullRenderReady = useMemo(() => (
        !readOnly
        || (
            !progressiveReadOnlyEnabled
            && renderedSlateValue.length >= slateValue.length
        )
        || (
            progressiveReadOnlyEnabled
            && visibleReadOnlyBlockCount >= slateValue.length
            && renderedSlateValue.length >= slateValue.length
        )
    ), [
        readOnly,
        progressiveReadOnlyEnabled,
        renderedSlateValue.length,
        slateValue.length,
        visibleReadOnlyBlockCount,
    ]);
    const readOnlyChunkingIdle = useMemo(() => (
        !readOnly
        || (
            initializedValue
            && (
                !progressiveReadOnlyEnabled
                || visibleReadOnlyBlockCount >= slateValue.length
            )
        )
    ), [
        readOnly,
        initializedValue,
        progressiveReadOnlyEnabled,
        slateValue.length,
        visibleReadOnlyBlockCount,
    ]);

    const handleReadOnlyAudioPlaybackStateChange = useCallback((
        audioNoteId: string,
        playbackState: IReadOnlyAudioNotePlaybackState | null,
    ): void => {
        setReadOnlyAudioPlaybackStatesById((currentStates) => {
            const currentPlaybackState = currentStates.get(audioNoteId) || null;

            if (!playbackState) {
                readOnlyAudioPlaybackControlsRef.current.delete(audioNoteId);

                if (!currentPlaybackState) {
                    return currentStates;
                }

                const nextStates = new Map(currentStates);
                nextStates.delete(audioNoteId);
                return nextStates;
            }

            readOnlyAudioPlaybackControlsRef.current.set(audioNoteId, {
                setPlaying: playbackState.setPlaying,
                setPlaybackSpeed: playbackState.setPlaybackSpeed,
                seekToTimeMs: playbackState.seekToTimeMs,
            });

            if (
                currentPlaybackState
                && areReadOnlyAudioPlaybackStatesEqual(currentPlaybackState, playbackState)
            ) {
                return currentStates;
            }

            const nextStates = new Map(currentStates);
            nextStates.set(audioNoteId, playbackState);
            return nextStates;
        });
    }, []);

    /**
     * Picks the read-only audio note that most recently changed playback state.
     * This keeps the reader text and floating mini-player focused on whichever
     * note the reader is actively listening to.
     */
    const activeReadOnlyAudioPlaybackState = useMemo(() => {
        const activePlaybackStates = Array.from(readOnlyAudioPlaybackStatesById.values())
            .filter((playbackState) => playbackState.hasStartedPlayback);

        if (activePlaybackStates.length === 0) {
            return null;
        }

        const playingPlaybackStates = activePlaybackStates.filter((playbackState) => playbackState.isPlaying);
        const candidatePlaybackStates = playingPlaybackStates.length > 0
            ? playingPlaybackStates
            : activePlaybackStates;

        const latestPlaybackState = candidatePlaybackStates.reduce((latestState, playbackState) => (
            playbackState.updatedAt > latestState.updatedAt
                ? playbackState
                : latestState
        ));

        return {
            ...latestPlaybackState,
            setPlaying: (play: boolean): void => {
                const playbackControl = readOnlyAudioPlaybackControlsRef.current.get(latestPlaybackState.id);
                if (playbackControl) {
                    playbackControl.setPlaying(play);
                }
            },
            setPlaybackSpeed: (speed: number): void => {
                const playbackControl = readOnlyAudioPlaybackControlsRef.current.get(latestPlaybackState.id);
                if (playbackControl) {
                    playbackControl.setPlaybackSpeed(speed);
                }
            },
            seekToTimeMs: (timestampMs: number): void => {
                const playbackControl = readOnlyAudioPlaybackControlsRef.current.get(latestPlaybackState.id);
                if (playbackControl) {
                    playbackControl.seekToTimeMs(timestampMs);
                }
            },
        };
    }, [readOnlyAudioPlaybackStatesById]);
    const getReadOnlyAudioTextSyncCacheKey = useCallback((
        playbackState: Pick<IReadOnlyAudioNotePlaybackState, 'id' | 'transcriptWords'>,
    ): string => (
        `${playbackState.id}:${buildReadOnlyAudioTranscriptSignature(playbackState.transcriptWords)}`
    ), []);
    const readOnlyPostContentDictationWarmupTargets = useMemo(() => (
        Array.from(readOnlyAudioPlaybackStatesById.values())
            .filter((playbackState) => (
                playbackState.isPostContentDictation
                && playbackState.transcriptWords.length > 0
            ))
            /**
             * Warm the active note first, then any notes already visible in the
             * viewport, before spending time on colder off-screen candidates.
             */
            .sort((leftPlaybackState, rightPlaybackState) => {
                const leftIsActive = leftPlaybackState.id === activeReadOnlyAudioPlaybackState?.id;
                const rightIsActive = rightPlaybackState.id === activeReadOnlyAudioPlaybackState?.id;

                if (leftIsActive !== rightIsActive) {
                    return leftIsActive ? -1 : 1;
                }
                if (leftPlaybackState.isVisibleInViewport !== rightPlaybackState.isVisibleInViewport) {
                    return leftPlaybackState.isVisibleInViewport ? -1 : 1;
                }

                return leftPlaybackState.id.localeCompare(rightPlaybackState.id);
            })
            .map((playbackState) => ({
                id: playbackState.id,
                cacheKey: getReadOnlyAudioTextSyncCacheKey(playbackState),
                transcriptWords: playbackState.transcriptWords,
            }))
    ), [
        activeReadOnlyAudioPlaybackState?.id,
        getReadOnlyAudioTextSyncCacheKey,
        readOnlyAudioPlaybackStatesById,
    ]);
    const readOnlyPostContentDictationWarmupSignature = useMemo(() => (
        readOnlyPostContentDictationWarmupTargets
            .map((target) => target.cacheKey)
            .join('|')
    ), [readOnlyPostContentDictationWarmupTargets]);
    readOnlyAudioWarmupTargetsRef.current = readOnlyPostContentDictationWarmupTargets;
    const postContentDictationSourceTextsByAudioNoteId = useMemo(() => resolvePostContentDictationSourceTextsByAudioNoteId({
        slateValue,
        includeNonDictationAudioNotes: true,
    }), [slateValue]);
    const readOnlyPostContentDictationStartPathsByAudioNoteId = useMemo(() => {
        const startPathsByAudioNoteId: Record<string, number[]> = {};

        const collectAudioNotePaths = (
            nodes: Descendant[],
            parentPath: number[] = [],
        ): void => {
            nodes.forEach((node, index) => {
                const path = [...parentPath, index];

                if (AudioNoteElement.isAudioNoteElement(node)) {
                    const audioNoteNode = node as AudioNoteNode;

                    // Media hydration is the source of truth for legacy nodes whose
                    // embedded dictation flag was normalized to false.
                    if (typeof audioNoteNode.id === 'string' && audioNoteNode.id.trim().length > 0) {
                        startPathsByAudioNoteId[audioNoteNode.id] = path;
                    }
                }

                if ('children' in node && Array.isArray(node.children)) {
                    collectAudioNotePaths(node.children as Descendant[], path);
                }
            });
        };

        collectAudioNotePaths(slateValue);

        return startPathsByAudioNoteId;
    }, [slateValue]);
    /**
     * Read-along sync maps are expensive to derive from a long Slate tree.
     * Cache them per note/transcript so playback can reuse warmed results
     * instead of rebuilding on the play click.
     */
    const buildAndCacheReadOnlyAudioTextSyncResult = useCallback((
        playbackState: Pick<IReadOnlyAudioNotePlaybackState, 'id' | 'isPostContentDictation' | 'transcriptWords'>,
    ): IAudioTextSyncResult | null => {
        if (
            !playbackState.isPostContentDictation
            || playbackState.transcriptWords.length === 0
        ) {
            return null;
        }

        const cacheKey = getReadOnlyAudioTextSyncCacheKey(playbackState);
        const cachedSyncResult = readOnlyAudioTextSyncCacheRef.current.get(cacheKey);
        if (cachedSyncResult) {
            return cachedSyncResult;
        }

        const nextSyncResult = buildAudioTextSyncMap({
            slateValue,
            transcriptWords: playbackState.transcriptWords,
            textStartPath: readOnlyPostContentDictationStartPathsByAudioNoteId[playbackState.id],
        });
        readOnlyAudioTextSyncCacheRef.current.set(cacheKey, nextSyncResult);
        return nextSyncResult;
    }, [
        getReadOnlyAudioTextSyncCacheKey,
        readOnlyPostContentDictationStartPathsByAudioNoteId,
        slateValue,
    ]);

    useEffect(() => {
        readOnlyAudioTextSyncCacheRef.current.clear();
        setReadOnlyAudioTextSyncCacheVersion((currentVersion) => currentVersion + 1);
    }, [slateValue]);

    /**
     * Warm dictation sync maps in the background as read-only audio notes
     * report their transcript state. This keeps the first play interaction from
     * paying the full sync-map build cost on the critical path.
     */
    useEffect(() => {
        if (
            !readOnly
            || readOnlyPostContentDictationWarmupSignature.length === 0
        ) {
            return undefined;
        }

        let cancelled = false;
        let timeoutId: number | null = null;
        let targetIndex = 0;

        const warmNextReadAlongTarget = (): void => {
            if (cancelled) {
                return;
            }

            const nextTarget = readOnlyAudioWarmupTargetsRef.current[targetIndex];
            if (!nextTarget) {
                return;
            }
            targetIndex += 1;

            if (!readOnlyAudioTextSyncCacheRef.current.has(nextTarget.cacheKey)) {
                const warmedSyncResult = buildAndCacheReadOnlyAudioTextSyncResult({
                    id: nextTarget.id,
                    isPostContentDictation: true,
                    transcriptWords: nextTarget.transcriptWords,
                });
                if (warmedSyncResult) {
                    setReadOnlyAudioTextSyncCacheVersion((currentVersion) => currentVersion + 1);
                }
            }

            timeoutId = window.setTimeout(warmNextReadAlongTarget, 0);
        };

        timeoutId = window.setTimeout(warmNextReadAlongTarget, 0);

        return () => {
            cancelled = true;
            if (timeoutId != null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [
        buildAndCacheReadOnlyAudioTextSyncResult,
        readOnly,
        readOnlyPostContentDictationWarmupSignature,
    ]);
    const activeReadOnlyAudioTextSyncCacheKey = useMemo<string | null>(() => {
        if (
            !readOnly
            || !activeReadOnlyAudioPlaybackState
            || !activeReadOnlyAudioPlaybackState.hasStartedPlayback
            || !activeReadOnlyAudioPlaybackState.isPostContentDictation
            || activeReadOnlyAudioPlaybackState.transcriptWords.length === 0
        ) {
            return null;
        }

        return getReadOnlyAudioTextSyncCacheKey(activeReadOnlyAudioPlaybackState);
    }, [
        getReadOnlyAudioTextSyncCacheKey,
        readOnly,
        activeReadOnlyAudioPlaybackState?.id,
        activeReadOnlyAudioPlaybackState?.hasStartedPlayback,
        activeReadOnlyAudioPlaybackState?.isPostContentDictation,
        activeReadOnlyAudioPlaybackState?.transcriptWords,
    ]);

    /**
     * Speculative warmup is best-effort. Once a reader actually starts
     * playback, make sure that note's sync map exists even if the background
     * warmer has not reached it yet.
     */
    useEffect(() => {
        if (
            !activeReadOnlyAudioTextSyncCacheKey
            || !activeReadOnlyAudioPlaybackState
        ) {
            return;
        }
        if (readOnlyAudioTextSyncCacheRef.current.has(activeReadOnlyAudioTextSyncCacheKey)) {
            return;
        }

        const nextSyncResult = buildAndCacheReadOnlyAudioTextSyncResult({
            id: activeReadOnlyAudioPlaybackState.id,
            isPostContentDictation: activeReadOnlyAudioPlaybackState.isPostContentDictation,
            transcriptWords: activeReadOnlyAudioPlaybackState.transcriptWords,
        });

        if (nextSyncResult) {
            setReadOnlyAudioTextSyncCacheVersion((currentVersion) => currentVersion + 1);
        }
    }, [
        activeReadOnlyAudioPlaybackState?.id,
        activeReadOnlyAudioPlaybackState?.isPostContentDictation,
        activeReadOnlyAudioPlaybackState?.transcriptWords,
        activeReadOnlyAudioTextSyncCacheKey,
        buildAndCacheReadOnlyAudioTextSyncResult,
    ]);

    /**
     * Only enable read-along highlighting when the transcript genuinely looks
     * like the visible section/chapter text instead of an unrelated voice memo.
     */
    const activeReadOnlyAudioTextSyncResult = useMemo<IAudioTextSyncResult | null>(() => {
        if (!activeReadOnlyAudioTextSyncCacheKey) {
            return null;
        }

        return readOnlyAudioTextSyncCacheRef.current.get(activeReadOnlyAudioTextSyncCacheKey) || null;
    }, [
        activeReadOnlyAudioTextSyncCacheKey,
        readOnlyAudioTextSyncCacheVersion,
    ]);

    const currentReadOnlyDictationDecorationTarget = useMemo(() => {
        if (
            !activeReadOnlyAudioPlaybackState
            || !activeReadOnlyAudioTextSyncResult?.enabled
            || activeReadOnlyAudioPlaybackState.activeTranscriptWordIndex == null
        ) {
            return null;
        }

        return activeReadOnlyAudioTextSyncResult.matchesByTranscriptWordIndex[
            activeReadOnlyAudioPlaybackState.activeTranscriptWordIndex
        ] || null;
    }, [
        activeReadOnlyAudioPlaybackState,
        activeReadOnlyAudioPlaybackState?.activeTranscriptWordIndex,
        activeReadOnlyAudioTextSyncResult,
    ]);
    const readOnlyAudioTextTouchGestureRef = useRef<{
        x: number,
        y: number,
        moved: boolean,
    } | null>(null);
    const readOnlyAudioTextSeekEnabled = useMemo(() => (
        !!readOnly
        && !!activeReadOnlyAudioPlaybackState?.hasStartedPlayback
        && !!activeReadOnlyAudioTextSyncResult?.enabled
    ), [
        readOnly,
        activeReadOnlyAudioPlaybackState?.hasStartedPlayback,
        activeReadOnlyAudioTextSyncResult?.enabled,
    ]);
    const clearReadOnlyAudioTextTouchGesture = useCallback((): void => {
        readOnlyAudioTextTouchGestureRef.current = null;
    }, []);
    /**
     * Keep the initial touch point so read-only post audio seeking can tell the
     * difference between a deliberate tap and a scroll that begins on text.
     */
    const handleReadOnlyAudioTextTouchStart = useCallback((event: React.TouchEvent): void => {
        if (!readOnlyAudioTextSeekEnabled) {
            clearReadOnlyAudioTextTouchGesture();
            return;
        }

        const touch = event.touches[0];
        if (!touch) {
            clearReadOnlyAudioTextTouchGesture();
            return;
        }

        readOnlyAudioTextTouchGestureRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            moved: false,
        };
    }, [
        clearReadOnlyAudioTextTouchGesture,
        readOnlyAudioTextSeekEnabled,
    ]);
    /**
     * Mobile scroll gestures share the same touch lifecycle as taps. Mark the
     * gesture as moved as soon as it exceeds the tap threshold so touchend does
     * not seek the active read-only audio note.
     */
    const handleReadOnlyAudioTextTouchMove = useCallback((event: React.TouchEvent): void => {
        const touchGesture = readOnlyAudioTextTouchGestureRef.current;
        const touch = event.touches[0];

        if (!touchGesture || !touch || touchGesture.moved) {
            return;
        }

        if (!touchGestureMovedBeyondTapThreshold({
            touchGesture,
            nextPoint: {
                x: touch.clientX,
                y: touch.clientY,
            },
        })) {
            return;
        }

        readOnlyAudioTextTouchGestureRef.current = {
            ...touchGesture,
            moved: true,
        };
    }, []);
    /**
     * During active read-along playback, quote-style highlights can momentarily
     * win a Slate leaf merge and look like a phrase-wide transcript highlight.
     * Suppress those competing decorations so playback only paints the synced
     * dictation word.
     */
    const shouldSuppressReadOnlyDecorationHighlights = useMemo(() => (
        !!readOnly
        && !!activeReadOnlyAudioPlaybackState?.isPlaying
        && !!activeReadOnlyAudioTextSyncResult?.enabled
    ), [
        readOnly,
        activeReadOnlyAudioPlaybackState?.isPlaying,
        activeReadOnlyAudioTextSyncResult?.enabled,
    ]);
    /**
     * Resolve read-only post clicks on mouseup instead of click so seeking
     * still works when Slate/browser selection updates suppress the click
     * event on the editable surface. Drag-created selections still bail out.
     */
    const handleReadOnlyAudioTextSeekInteraction = useCallback((
        event: React.MouseEvent | React.TouchEvent,
    ): void => {
        const touchInteraction = 'changedTouches' in event;

        try {
            if (!readOnlyAudioTextSeekEnabled) {
                return;
            }

            const resolvedPlaybackState = activeReadOnlyAudioPlaybackState;
            const resolvedTextSyncResult = activeReadOnlyAudioTextSyncResult;

            if (
                !resolvedPlaybackState
                || !resolvedTextSyncResult?.enabled
            ) {
                return;
            }

            if (
                touchInteraction
                && !readOnlyAudioTextTouchGestureRepresentsTap({
                    touchGesture: readOnlyAudioTextTouchGestureRef.current,
                    touchPoint: event.changedTouches[0]
                        ? {
                            x: event.changedTouches[0].clientX,
                            y: event.changedTouches[0].clientY,
                        }
                        : null,
                })
            ) {
                return;
            }

            if ('button' in event && event.button !== 0) {
                return;
            }

            const target = event.target as HTMLElement | null;
            if (
                !target
                || !!target.closest('a, button, input, textarea, select, [role="button"], [data-audio-note-id]')
            ) {
                return;
            }

            const nativeSelection = window.getSelection();
            if (nativeSelection && !nativeSelection.isCollapsed) {
                return;
            }

            const interactionCenter = resolveInteractionCenter(event);
            if (!interactionCenter) {
                return;
            }

            const targetDocument = (target.ownerDocument || document) as Document & {
                caretPositionFromPoint?: (x: number, y: number) => {
                    offset: number,
                    offsetNode: globalThis.Node | null,
                } | null,
                caretRangeFromPoint?: (x: number, y: number) => globalThis.Range | null,
            };
            let domPoint: [globalThis.Node, number] | null = null;

            if (targetDocument.caretPositionFromPoint) {
                const caretPosition = targetDocument.caretPositionFromPoint(
                    interactionCenter.x,
                    interactionCenter.y,
                );
                if (caretPosition?.offsetNode) {
                    domPoint = [caretPosition.offsetNode, caretPosition.offset];
                }
            } else if (targetDocument.caretRangeFromPoint) {
                const caretRange = targetDocument.caretRangeFromPoint(
                    interactionCenter.x,
                    interactionCenter.y,
                );
                if (caretRange?.startContainer) {
                    domPoint = [caretRange.startContainer, caretRange.startOffset];
                }
            }

            if (!domPoint) {
                return;
            }

            let slatePoint: Point | null = null;
            try {
                slatePoint = ReactEditor.toSlatePoint(
                    editor,
                    domPoint,
                    { exactMatch: false, suppressThrow: true },
                ) as Point | null;
            } catch (error) {
                slatePoint = null;
            }

            if (!slatePoint) {
                return;
            }

            const matchedWord = resolveAudioTextSyncMatchFromPoint(
                slatePoint,
                resolvedTextSyncResult,
            );
            const transcriptWord = matchedWord
                ? resolvedPlaybackState.transcriptWords[matchedWord.transcriptWordIndex]
                : null;
            if (!transcriptWord) {
                return;
            }

            event.preventDefault();
            resolvedPlaybackState.seekToTimeMs(getTranscriptionWordTimestampMs({
                word: transcriptWord,
                edge: 'start',
            }));
        } finally {
            if (touchInteraction) {
                clearReadOnlyAudioTextTouchGesture();
            }
        }
    }, [
        editor,
        readOnlyAudioTextSeekEnabled,
        activeReadOnlyAudioPlaybackState,
        activeReadOnlyAudioPlaybackState?.transcriptWords,
        activeReadOnlyAudioPlaybackState?.seekToTimeMs,
        activeReadOnlyAudioTextSyncResult,
        clearReadOnlyAudioTextTouchGesture,
    ]);

    // ===== Animation Constants =====

    const HIGHLIGHT_TRANSITION_DURATION = 150;

    // ===== Hotkeys =====

    const handleOnKeyDown = useMemo(() => HandleOnKeyDown({
        tabCallback: () => {
            ListHelpers.indentSelectionLists(editor);
            LineHelpers.indentLineElements(editor);
        },
        shiftTabCallback: () => {
            ListHelpers.unindentSelectionLists(editor);
            LineHelpers.unindentLineElements(editor);
        },
        modBCallback: () => EmphasizerHelpers.toggleBold(editor),
        modICallback: () => EmphasizerHelpers.toggleItalicize(editor),
        modUCallback: () => EmphasizerHelpers.toggleUnderline(editor),
        modACallback: () => timeoutSelectAllText(),
        modShiftSCallback: () => EmphasizerHelpers.toggleStrikethrough(editor),
    }), []);

    // ===== Decorations =====

    /**
     * Detached annotations stay in editor state for repair flows, but they
     * should not render highlights until an author reattaches them.
     */
    const renderableAnnotations = useMemo(() => (
        annotations.filter((annotation) => (
            !annotation.comment
            && !annotation.detached?.detached
        ))
    ), [annotations]);
    const annotationDecorationDependency = useMemo(() => (
        renderableAnnotations
            .map((annotation) => `${annotation.id}:${annotation.quoteHistory[0]?.id || ''}`)
            .join('|')
    ), [renderableAnnotations]);
    const hasRenderableAnnotationHighlights = useMemo(() => (
        renderableAnnotations.length > 0
    ), [renderableAnnotations]);

    useEffect(() => {
        if (!readOnly) {
            if (!readOnlyEnhancementsReady) setReadOnlyEnhancementsReady(true);
            readOnlyEnhancementsResetKeyRef.current = null;
            return;
        }
        if (readOnlyEnhancementsResetKeyRef.current !== readOnlyEnhancementsResetKey) {
            readOnlyEnhancementsResetKeyRef.current = readOnlyEnhancementsResetKey;
            setReadOnlyEnhancementsReady(false);
        }
    }, [
        readOnly,
        readOnlyEnhancementsReady,
        readOnlyEnhancementsResetKey,
    ]);

    /**
     * Reader highlight open only ever targets a tiny subset of text leaves, so
     * resolve the active quote range once and let `decorate` cheaply skip every
     * rendered node path that falls outside those bounds.
     */
    const postQuoteDecorationTargets = useMemo<IPostQuoteDecorationTarget[]>(() => {
        if (!postQuote) {
            return [];
        }

        return postQuote.selections.reduce((targets, selection) => {
            try {
                const range = Editor.range(editor, selection as Location);
                targets.push({
                    range,
                    startPath: Range.start(range).path,
                    endPath: Range.end(range).path,
                });
            } catch (_error) {
                return targets;
            }

            return targets;
        }, [] as IPostQuoteDecorationTarget[]);
    }, [
        editor,
        postQuote,
    ]);

    /**
     * Resolve annotation quote ranges once so deep read-only posts can skip
     * most nodes with fast path comparisons instead of rebuilding candidate
     * ranges for every rendered leaf.
     */
    const annotationDecorationTargets = useMemo<IAnnotationDecorationTarget[]>(() => {
        if (shouldSuppressReadOnlyDecorationHighlights || renderableAnnotations.length === 0) {
            return [];
        }

        return renderableAnnotations.reduce((targets, annotation) => {
            const revealQuote = !!(
                (!!targetAnnotationID && targetAnnotationID === annotation.id)
                || !readOnly
                || (params.annotationId && params.annotationId === annotation.id)
            );

            annotation.quoteHistory[0].selections.forEach((selection) => {
                const annotationSelections = resolvePostSelectionCandidates(
                    selection!,
                    chapters,
                    currentZoomLevel,
                    selectedPostValuePath,
                    annotation.chapterId,
                    annotation.sectionId,
                );
                const annotationRange = annotationSelections.reduce<Range | null>((resolvedRange, candidateSelection) => {
                    if (resolvedRange) {
                        return resolvedRange;
                    }

                    try {
                        return Editor.range(editor, candidateSelection as Location);
                    } catch (_error) {
                        return null;
                    }
                }, null);

                if (!annotationRange) {
                    return;
                }

                targets.push({
                    annotationId: annotation.id,
                    range: annotationRange,
                    startPath: Range.start(annotationRange).path,
                    endPath: Range.end(annotationRange).path,
                    revealQuote,
                });
            });

            return targets;
        }, [] as IAnnotationDecorationTarget[]);
    }, [
        chapters,
        currentZoomLevel,
        editor,
        params.annotationId,
        readOnly,
        renderableAnnotations,
        selectedPostValuePath,
        shouldSuppressReadOnlyDecorationHighlights,
        targetAnnotationID,
    ]);

    const decorate = useCallback(([node, path]: NodeEntry) => {
        if (lightweightReadOnly) return [];
        const decorations: IEditorDecoration[] = [];
        // A collaborative document can momentarily render before Yjs has seeded any
        // text descendants into the editor, so range lookups against `[]` must fail soft.
        const getDecorationRange = (location: Location): Range | null => {
            try {
                return Editor.range(editor, location);
            } catch (_error) {
                return null;
            }
        };
        let cachedNodeRange: Range | null | undefined;
        const getNodeRange = (): Range | null => {
            if (typeof cachedNodeRange === 'undefined') {
                cachedNodeRange = getDecorationRange(path);
            }

            return cachedNodeRange;
        };

        if (!shouldSuppressReadOnlyDecorationHighlights && annotationDecorationTargets.length > 0) {
            annotationDecorationTargets.forEach((annotationDecorationTarget) => {
                if (
                    Path.compare(path, annotationDecorationTarget.startPath) < 0
                    || Path.compare(path, annotationDecorationTarget.endPath) > 0
                ) {
                    return;
                }

                const nodeRange = getNodeRange();
                if (!nodeRange) return;
                const intersection = Range.intersection(nodeRange, annotationDecorationTarget.range);
                if (intersection) {
                    decorations.push({
                        anchor: intersection.anchor,
                        focus: intersection.focus,
                        [`${ANNOTATION_DECORATION_PREFIX}${annotationDecorationTarget.annotationId}`]:
                            annotationDecorationTarget.annotationId,
                        annotationQuote: true,
                        [`${REVEAL_QUOTE_DECORATION_PREFIX}${annotationDecorationTarget.annotationId}`]:
                            annotationDecorationTarget.revealQuote,
                        highlightColor: themeObj.verascopeColor.purple400,
                    });
                }
            });
        }

        // We place after annotations highlight logic so that it is
        // prioritized when displayed
        // i.e. we show a text highlight on top of an annotation highlight
        if (!shouldSuppressReadOnlyDecorationHighlights && postQuoteDecorationTargets.length > 0) {
            postQuoteDecorationTargets.forEach((postQuoteDecorationTarget) => {
                if (
                    Path.compare(path, postQuoteDecorationTarget.startPath) < 0
                    || Path.compare(path, postQuoteDecorationTarget.endPath) > 0
                ) {
                    return;
                }

                const nodeRange = getNodeRange();
                if (!nodeRange) return;
                const intersection = Range.intersection(nodeRange, postQuoteDecorationTarget.range);

                if (intersection) {
                    decorations.push({
                        anchor: intersection.anchor,
                        focus: intersection.focus,
                        postQuote: true,
                        highlightColor: themeObj.verascopeColor.orange200,
                    });
                }
            });
        }

        if (
            currentReadOnlyDictationDecorationTarget
            && (
                activeReadOnlyAudioPlaybackState?.isPlaying
                || (
                    activeReadOnlyAudioPlaybackState?.currentTimeMs
                    && activeReadOnlyAudioPlaybackState.currentTimeMs > 0
                )
            )
            && Text.isText(node)
            && currentReadOnlyDictationDecorationTarget.pathKey === path.join(',')
        ) {
            decorations.push({
                anchor: {
                    path,
                    offset: currentReadOnlyDictationDecorationTarget.startOffset,
                },
                focus: {
                    path,
                    offset: currentReadOnlyDictationDecorationTarget.endOffset,
                },
                dictationHighlight: true,
                highlightColor: themeObj.verascopeColor.purple400,
            });
        }

        return decorations;
    }, [
        annotationDecorationTargets,
        postQuoteDecorationTargets,
        annotationDecorationDependency,
        lightweightReadOnly,
        activeReadOnlyAudioPlaybackState?.currentTimeMs,
        activeReadOnlyAudioPlaybackState?.isPlaying,
        currentReadOnlyDictationDecorationTarget,
        shouldSuppressReadOnlyDecorationHighlights,
    ]);

    // ===== Side Effects =====

    useEffect(() => {
        if (
            !annotationHighlightsRendered
            && !lightweightReadOnly
            && hasRenderableAnnotationHighlights
        ) {
            setAnnotationHighlightsRendered(true);
        }
    }, [
        annotationHighlightsRendered,
        lightweightReadOnly,
        hasRenderableAnnotationHighlights,
        setAnnotationHighlightsRendered,
    ]);

    /**
     * Set reference to editor to parent
     */
    useEffect(() => {
        setEditor(editor);
        return () => {
            // Clear both DOM and Slate selection on teardown so scope deletes
            // do not leave Slate pointing at nodes that no longer exist.
            const nativeSelection = window.getSelection();
            if (nativeSelection && nativeSelection.rangeCount > 0) {
                nativeSelection.removeAllRanges();
            }
            try {
                Transforms.deselect(editor);
            } catch (error) {
                // Ignore teardown races while Slate is unmounting.
            }
            editor.selection = null;
            setEditor(null);
        };
    }, [editor, setEditor]);

    useEffect(() => {
        if (CursorEditor.isCursorEditor(editor)) {
            CursorEditor.sendCursorData(editor, collaborativeCursorData);
        }
    }, [editor, collaborativeCursorData]);

    useEffect(() => {
        if (!onCollaborativeParticipantsChange) return undefined;
        if (
            !usingCollaborativeDoc
            || !collaborativeSession
            || !CursorEditor.isCursorEditor(editor)
        ) {
            onCollaborativeParticipantsChange([]);
            return undefined;
        }

        const { awareness } = collaborativeSession.provider;
        const syncParticipants = (): void => {
            const nextParticipants = Array
                .from(awareness.getStates().values())
                .map((state: any) => {
                    const { [editor.cursorDataField]: participant } = state;
                    return participant as ICollaborativeCursorData | undefined;
                })
                .filter((participant): participant is ICollaborativeCursorData => !!participant && !!participant.name)
                .reduce((participants, participant) => {
                    const participantKey = participant.userId || participant.name;
                    if (!participants.has(participantKey)) {
                        participants.set(participantKey, participant);
                    }
                    return participants;
                }, new Map<string, ICollaborativeCursorData>());

            onCollaborativeParticipantsChange(Array.from(nextParticipants.values()));
        };

        awareness.on('change', syncParticipants);
        syncParticipants();

        return () => {
            awareness.off('change', syncParticipants);
            onCollaborativeParticipantsChange([]);
        };
    }, [
        collaborativeSession,
        editor,
        onCollaborativeParticipantsChange,
        usingCollaborativeDoc,
    ]);

    useEffect(() => () => {
        collaborativeSession?.destroy();
    }, [collaborativeSession]);

    useEffect(() => {
        if (
            !usingCollaborativeDoc
            || !sharedRoot
            || !YjsEditor.isYjsEditor(editor)
            || !collaborativeSession
        ) {
            return undefined;
        }

        if (!YjsEditor.connected(editor)) {
            YjsEditor.connect(editor);
        }

        const handleSync = (isSynced: boolean): void => {
            if (!isSynced) return;

            // Wait for the first provider sync before seeding so multiple clients
            // do not all inject the initial document into an apparently-empty local
            // Yjs tree before remote state has arrived.
            if (
                collaborativeDocId
                && collaborativeSeededDocIdRef.current !== collaborativeDocId
                && sharedRoot.toDelta().length === 0
            ) {
                sharedRoot.doc?.transact(() => {
                    sharedRoot.applyDelta(slateNodesToInsertDelta(latestSlateValueRef.current), {
                        sanitize: false,
                    });
                });
                collaborativeSeededDocIdRef.current = collaborativeDocId;
            }

            suppressNextProgrammaticContentChange();
            suppressNextAutosaveRef.current = true;
            setValue(ensureNonEmptySlateValue(editor.children as Descendant[], INITIAL_EDITOR_VALUE));
            setInitializedValue(true);
            setCachedSelectedPostValuePath(latestSelectedPostValuePathRef.current);
            setRemeasurePostHeight(true);
        };

        collaborativeSession.provider.on('sync', handleSync);
        handleSync(collaborativeSession.provider.synced);

        return () => {
            collaborativeSession.provider.off('sync', handleSync);
            if (YjsEditor.connected(editor)) {
                YjsEditor.disconnect(editor);
            }
        };
    }, [
        collaborativeDocId,
        collaborativeSession,
        editor,
        sharedRoot,
        suppressNextProgrammaticContentChange,
        usingCollaborativeDoc,
    ]);

    /**
     * Set initial post value
     */
    useEffect(() => {
        if (usingCollaborativeDoc) return;
        if (!initializedValue && hasIncomingPostValue && displaySlateValue) {
            suppressNextProgrammaticContentChange();
            suppressNextAutosaveRef.current = true;
            setValue(displaySlateValue);
            setInitializedValue(true);
            setCachedSelectedPostValuePath(selectedPostValuePath);
            setRemeasurePostHeight(true);
        }
    }, [
        hasIncomingPostValue,
        displaySlateValue,
        initializedValue,
        suppressNextProgrammaticContentChange,
        postValueNodes,
    ]);

    /**
     * Set change post value when chapter or section change
     */
    useEffect(() => {
        if (usingCollaborativeDoc) return;
        if (
            initializedValue
            && hasIncomingPostValue
            && displaySlateValue
            && (
                readOnly
                || !deepEqual(selectedPostValuePath, cachedSelectedPostValuePath)
            )) {
            // User has switched chapters or sections
            // Inject new post value into editor
            const sameScopeReadOnlyRefresh = readOnly
                && deepEqual(selectedPostValuePath, cachedSelectedPostValuePath);
            const nextSlateValue = ensureNonEmptySlateValue(displaySlateValue, INITIAL_EDITOR_VALUE);
            const currentEditorValue = ensureNonEmptySlateValue(
                editor.children as Descendant[],
                INITIAL_EDITOR_VALUE,
            );
            if (deepEqual(currentEditorValue, nextSlateValue)) {
                return;
            }
            const appendPrefixMatches = (
                nextSlateValue.length >= currentEditorValue.length
                && deepEqual(currentEditorValue, nextSlateValue.slice(0, currentEditorValue.length))
            );
            const readOnlyRefreshGrew = nextSlateValue.length > currentEditorValue.length;
            if (sameScopeReadOnlyRefresh) {
                if (readOnlyRefreshGrew) {
                    captureReadOnlyAppendScrollLock();
                }
                clearCollapsedReadOnlySelection();
                // Same-scope read-mode refreshes should avoid live insert/remove
                // Slate transforms, because those DOM/selection mutations can
                // visibly yank the reader viewport during progressive growth.
                editor.children = nextSlateValue;
                suppressNextProgrammaticContentChange();
                suppressNextAutosaveRef.current = true;
                setValue(nextSlateValue);
                if (!appendPrefixMatches) {
                    setRemeasurePostHeight(true);
                }
                return;
            }

            replaceSlateEditorValue({
                editor,
                value: JSON.stringify(nextSlateValue),
                selection: readOnly
                    ? SLATE_EDITOR_REPLACEMENT_SELECTION.none
                    : SLATE_EDITOR_REPLACEMENT_SELECTION.end,
            });
            suppressNextProgrammaticContentChange();
            suppressNextAutosaveRef.current = true;
            setValue(nextSlateValue);
            if (!readOnly) {
                if (!deepEqual(selectedPostValuePath, cachedSelectedPostValuePath)) {
                    setCachedSelectedPostValuePath(selectedPostValuePath);
                }
            }
            setRemeasurePostHeight(true);
        } else if (initializedValue && !hasIncomingPostValue && slateValue) {
            // User has created a new chapter or section
            // Reset initialization so new post normalized post value can be injected
            // via getSavedSlateValue and 'Set initial post value' effect
            setInitializedValue(false);
        }
    }, [
        selectedPostValuePath,
        postValue,
        postValueNodes,
        hasIncomingPostValue,
        displaySlateValue,
        captureReadOnlyAppendScrollLock,
        suppressNextProgrammaticContentChange,
        usingCollaborativeDoc,
        clearCollapsedReadOnlySelection,
    ]);

    useEffect(() => {
        if (
            !restorePostValueRequest
            || restorePostValueRequest.id === appliedRestoreRequestIdRef.current
        ) {
            return;
        }

        let nextValue: Descendant[] = INITIAL_EDITOR_VALUE;
        try {
            const parsed = JSON.parse(restorePostValueRequest.value);
            if (Array.isArray(parsed) && parsed.length > 0) {
                nextValue = parsed as Descendant[];
            }
        } catch (error) {
            nextValue = INITIAL_EDITOR_VALUE;
        }

        skipNextAnnotationQuoteReconciliationRef.current = true;
        pendingDeferredAnnotationQuoteReconciliationRef.current = true;
        pendingCheckpointReasonRef.current = POST_COLLAB_DOC_CHECKPOINT_REASON.restore;
        appliedRestoreRequestIdRef.current = restorePostValueRequest.id;

        suppressNextProgrammaticContentChange();
        replaceSlateEditorValue({
            editor,
            value: JSON.stringify(nextValue),
        });

        setValue(nextValue);
        setRemeasurePostHeight(true);
        if (!readOnly) {
            ReactEditor.focus(editor);
        }
        if (onRestorePostValueApplied) {
            onRestorePostValueApplied(restorePostValueRequest.id);
        }
    }, [
        editor,
        onRestorePostValueApplied,
        readOnly,
        restorePostValueRequest,
        setRemeasurePostHeight,
        suppressNextProgrammaticContentChange,
    ]);

    useEffect(() => {
        if (onInitializedValueChange) {
            onInitializedValueChange(initializedValue);
        }
    }, [
        initializedValue,
        onInitializedValueChange,
    ]);

    useEffect(() => {
        if (onReadOnlyChunkingIdleChange) {
            onReadOnlyChunkingIdleChange(readOnlyChunkingIdle);
        }
    }, [
        readOnlyChunkingIdle,
        onReadOnlyChunkingIdleChange,
    ]);

    useEffect(() => {
        if (onReadOnlyRenderedBlockCountChange) {
            onReadOnlyRenderedBlockCountChange(renderedSlateValue.length);
        }
    }, [
        onReadOnlyRenderedBlockCountChange,
        renderedSlateValue.length,
    ]);

    useEffect(() => {
        if (onReadOnlyParagraphCountChange) {
            onReadOnlyParagraphCountChange(fullReadOnlyParagraphCount);
        }
    }, [
        fullReadOnlyParagraphCount,
        onReadOnlyParagraphCountChange,
    ]);

    useEffect(() => {
        if (onReadOnlyFullRenderReadyChange) {
            onReadOnlyFullRenderReadyChange(initializedValue && readOnlyFullRenderReady);
        }
    }, [
        initializedValue,
        readOnlyFullRenderReady,
        onReadOnlyFullRenderReadyChange,
    ]);

    useEffect(() => {
        if (onReadOnlyRevealTargetResolutionChange) {
            onReadOnlyRevealTargetResolutionChange(readOnlyRevealTargetResolution);
        }
    }, [
        onReadOnlyRevealTargetResolutionChange,
        readOnlyRevealTargetResolution,
    ]);

    useEffect(() => {
        if (onReadOnlyAudioPlaybackStateChange) {
            onReadOnlyAudioPlaybackStateChange(activeReadOnlyAudioPlaybackState);
        }
    }, [
        activeReadOnlyAudioPlaybackState,
        onReadOnlyAudioPlaybackStateChange,
    ]);

    useEffect(() => () => {
        if (onReadOnlyAudioPlaybackStateChange) {
            onReadOnlyAudioPlaybackStateChange(null);
        }
    }, [onReadOnlyAudioPlaybackStateChange]);

    useLayoutEffect(() => {
        if (!initializedValue) return undefined;
        let frameOneId: number | null = null;
        let frameTwoId: number | null = null;
        frameOneId = window.requestAnimationFrame(() => {
            frameTwoId = window.requestAnimationFrame(() => {
                if (onBodyPainted) {
                    onBodyPainted();
                }
                if (readOnly && !readOnlyEnhancementsReady) {
                    setReadOnlyEnhancementsReady(true);
                }
            });
        });
        return () => {
            if (frameOneId != null) {
                window.cancelAnimationFrame(frameOneId);
            }
            if (frameTwoId != null) {
                window.cancelAnimationFrame(frameTwoId);
            }
        };
    }, [
        initializedValue,
        readOnly,
        readOnlyEnhancementsReady,
        onBodyPainted,
    ]);

    /**
     * Set Slate Editor Reference
     * and focus editor
     */
    useEffect(() => {
        if (!readOnly && initializedValue) {
            setSlateEditorRef(editor);
            moveCursorToEndOfDocument();
        }
    }, [
        initializedValue,
    ]);

    useEffect(() => {
        const cleanup = (): void => {
            if (inputClickClip.current) {
                inputClickClip.current.pause();
                inputClickClip.current.remove();
                inputClickClip.current = null;
            }
        };

        return cleanup;
    }, []);

    /**
     * Execute queued save
     */
    useEffect(() => {
        if (!postValueIsSaving && queueSave) {
            setQueueSave(false);
            handleAutoSave();
        }
    }, [postValueIsSaving]);

    /**
     * Determine Initial Editor Width
     */
    useEffect(() => {
        const currentParentWidth = parentRef
            ? parentRef.clientWidth
            : '100%';
        if (parentWidth !== currentParentWidth) {
            setParentWidth(currentParentWidth);
        }
    }, [parentRef]);

    /**
     * Determine initial position of block toolbar
     */
    useEffect(() => {
        if (
            !readOnly
            && containerRef.current
            && !blockToolbarPosition.y
        ) {
            updateBlockToolbarPosition();
        }
    }, [
        containerRef,
    ]);

    /**
     * Handle Autosave
     */
    useEffect(() => {
        if (autosaveTimeoutRef.current != null) {
            window.clearTimeout(autosaveTimeoutRef.current);
            autosaveTimeoutRef.current = null;
        }
        if (suppressNextAutosaveRef.current) {
            suppressNextAutosaveRef.current = false;
            return undefined;
        }
        if (
            !readOnly
            && postValue
            && initializedValue
        ) {
            // Debounce normal typing so we do not exceed Cloud Storage mutation
            // limits by rewriting the same latest collab object on every keystroke.
            if (pendingCheckpointReasonRef.current != null) {
                handleAutoSave();
                return undefined;
            }

            autosaveTimeoutRef.current = window.setTimeout(() => {
                autosaveTimeoutRef.current = null;
                handleAutoSave();
            }, POST_EDITOR_AUTOSAVE_DEBOUNCE_DURATION);
        }

        return () => {
            if (autosaveTimeoutRef.current != null) {
                window.clearTimeout(autosaveTimeoutRef.current);
                autosaveTimeoutRef.current = null;
            }
        };
    }, [value]);

    /**
     * Update Position of Toolbars
     */
    useEffect(() => {
        const nativeSelection = window.getSelection();
        if (
            !readOnly
            && updateToolbars
            && nativeSelection
        ) {
            const range = nativeSelection.rangeCount > 0
                ? nativeSelection.getRangeAt(0)
                : null;
            const rect = nativeSelection.rangeCount > 0
                ? range?.getBoundingClientRect()
                : null;

            if (
                rect
                && containerRef.current
                && (rect.top - containerRef.current.getBoundingClientRect().top - 7) !== blockToolbarPosition.y
            ) {
                // Only update block toolbar if it's vertical position has changed
                // This is to avoid a rerender for the block toolbar
                updateBlockToolbarPosition();
            }
            setUpdateToolbars(false);
        }
    }, [updateToolbars]);

    /**
     * Confirmation of leave page
     */
    useEffect(() => {
        if (!readOnly) {
            // Add a dialog that prevents reloading page if changes unsaved
            window.addEventListener('beforeunload', handleUnsavedAudioNoteDialog);
        }

        return function cleanup() {
            // Remove a dialog that prevents reloading page if changes unsaved
            if (!readOnly) window.removeEventListener('beforeunload', handleUnsavedAudioNoteDialog);
        };
    }, []);

    /**
     * pdate Positions of Toolbars on Resize
     */
    useEventListener(
        'resize',
        () => {
            if (!readOnly) {
                setUpdateToolbars(true);
            }
        },
    );

    /**
     * Update Parent Width on Resize
     */
    useEventListener(
        'resize',
        () => {
            const currentParentWidth = parentRef
                ? parentRef.clientWidth
                : '100%';
            if (parentWidth !== currentParentWidth) {
                setParentWidth(currentParentWidth);
            }
        },
    );

    /**
     * Upload dropzone files to storage
     * and create figure or audio note
     * WARNING: Accepted Files don't clear so this could be called again
     */
    useEffect(() => {
        if (!readOnly) {
            const uploadTasks: UploadTask[] = [];
            const mediaItems: IMediaItem[] = [];
            for (let i = 0; i < acceptedFiles.length; i += 1) {
                const file = acceptedFiles[i];
                const mediaId = uuidv4();
                const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
                const mediaType = file.type.startsWith('audio/')
                    ? MEDIA_TYPE.audioNote
                    : MEDIA_TYPE.image;
                const mediaBucket = getMediaStorageBucket(mediaType);
                const fileName = file.name.toLowerCase().split('.')[0].replace(' ', '_');
                const fileExtension = mime.getExtension(file.type);
                if (fileExtension == null) {
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        icon: FileIcon,
                        hasFailure: true,
                    });
                    return;
                }
                const storageEntity = isTestMode()
                    ? STORAGE_ENTITY.stagingPosts
                    : STORAGE_ENTITY.posts;
                const filePath = `${storageEntity}/${mediaBucket}/${mediaId}/${fileName}-${uniqueId}.${fileExtension}`;
                const mediaItem: IMediaItem = {
                    id: mediaId,
                    userId: user!.id,
                    file,
                    filePath,
                    type: MEDIA_TYPE.image,
                    uploadProgress: 0,
                };
                mediaItems.push(mediaItem);
                setUploadingMedia(mediaItem);

                // make db entry of media
                // execute before file upload so upload cloud function has a place to write to
                createMedia({
                    test: isTestMode(),
                    postId: id,
                    mediaId,
                    type: mediaType,
                    storageEntity,
                    fileName: `${fileName}-${uniqueId}`,
                    fileExtension: fileExtension.toString(),
                });

                // upload file to cloud storage
                // url will be set by cloud function
                const uploadTask = uploadToCloudStorage(
                    file,
                    filePath,
                    user!.id,
                );
                uploadTasks.push(uploadTask);
            }

            uploadTasks.forEach((task: UploadTask, index: number) => {
                task.on(
                    'state_changed',
                    (snapshot: UploadTaskSnapshot) => {
                        // Observe state change events such as progress, pause, and resume
                        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setUploadingStateChangeEvent({
                            mediaItem: mediaItems[index],
                            snapshot,
                            progress,
                        });
                    },
                    (error: StorageError) => {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                                },
                            });
                        }
                        throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
                    },
                    () => {
                        setUploadingCompleteEvent({
                            mediaItem: mediaItems[index],
                            storageRef: task.snapshot.ref,
                        });
                    },
                );
            });
        }
    }, [acceptedFiles]);

    /**
     * Processes progress updates for uploading media
     */
    useEffect(() => {
        if (uploadingStateChangeEvent) {
            handleUploadingMediaStateChange(
                uploadingStateChangeEvent.mediaItem,
                uploadingStateChangeEvent.snapshot,
                uploadingStateChangeEvent.progress,
            );
        }
    }, [uploadingStateChangeEvent]);

    /**
     * Processes completion of uploading media
     */
    useEffect(() => {
        if (uploadingCompleteEvent) {
            // Handle successful uploads on complete
            getDownloadURL(uploadingCompleteEvent.storageRef).then((downloadURL: string) => {
                handleUploadingMediaComplete(uploadingCompleteEvent.mediaItem, downloadURL);
            });
        }
    }, [uploadingCompleteEvent]);

    /**
     * Reconciles saved annotation quotes after post edits settle. The flow is:
     * 1. If the current stored selection still resolves to the same quote text, keep it.
     * 2. Otherwise, try to recover the quote from saved text anchors.
     * 3. If recovery proves the quote text was deleted, remove that selection.
     * 4. If recovery is ambiguous, preserve the stored selection rather than deleting data.
     *
     * Reconciliation is debounced so rapid typing batches into one update pass.
     */
    useEffect(() => {
        if (skipNextAnnotationQuoteReconciliationRef.current) {
            skipNextAnnotationQuoteReconciliationRef.current = false;
            if (pendingDeferredAnnotationQuoteReconciliationRef.current) {
                pendingDeferredAnnotationQuoteReconciliationRef.current = false;
                setAnnotationQuoteReconciliationTick((currentTick) => currentTick + 1);
            }
            return;
        }

        if (
            readOnly
            || annotations.length === 0
            || editor.children.length === 0
        ) {
            return;
        }

        /**
         * Preserve annotations whose quote can no longer be anchored by marking
         * them detached instead of deleting the user's content.
         */
        const detachAnnotation = (
            annotation: Extract<IAnnotationItem, { isComment: false }>,
            updatedAnnotations: IAnnotationItem[],
        ): IAnnotationItem[] => {
            if (!user) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: 'Missing user data while attempting to detach an orphaned annotation.',
                        },
                    });
                }
                throw Error('Missing user data while attempting to detach an orphaned annotation.');
            }

            const annotationIndex = updatedAnnotations.findIndex((a) => a.id === annotation.id);
            if (annotationIndex === -1) {
                return updatedAnnotations;
            }

            const detachedState: IAnnotationItemDetached = {
                detached: true,
                timestamp: Date.now(),
                detachedBy: user.id,
                reason: ANNOTATION_DETACHED_REASON.quoteRemoved,
            };
            updatedAnnotations.splice(annotationIndex, 1, {
                ...annotation,
                detached: detachedState,
            });

            updateAnnotation({
                test: isTestMode(),
                annotationId: annotation.id,
                detached: detachedState,
            });

            return [...updatedAnnotations];
        };
        const reconcileAnnotationQuotes = (): void => {
            let updatedAnnotations = [...annotations];
            // Indicates whether we need to update annotations locally
            let annotationsUpdated = false;
            const getSelectionText = (location: Location | null | undefined): string | null => {
                if (!location) {
                    return null;
                }
                try {
                    return Editor.string(editor, location);
                } catch (_error) {
                    return null;
                }
            };
            /**
             * Keeps the fast path cheap: if the current selection still resolves
             * to the same quote text with the same surrounding context, there is
             * nothing to reconcile.
             */
            const selectionMatchesStoredTextAnchor = ({
                normalizedSelection,
                textAnchor,
                quoteText,
            }: {
                normalizedSelection: Range | null,
                textAnchor?: IAnnotationTextAnchor,
                quoteText: string,
            }): boolean => {
                if (!normalizedSelection) {
                    return false;
                }

                const selectionText = getSelectionText(normalizedSelection as Location);
                if (selectionText !== quoteText) {
                    return false;
                }

                if (!textAnchor) {
                    return true;
                }

                const prefixText = typeof textAnchor.prefixText === 'string'
                    ? textAnchor.prefixText
                    : '';
                const suffixText = typeof textAnchor.suffixText === 'string'
                    ? textAnchor.suffixText
                    : '';

                if (!prefixText && !suffixText) {
                    return true;
                }

                try {
                    const [startPoint, endPoint] = Range.edges(normalizedSelection);
                    const currentPrefixText = prefixText.length > 0
                        ? getSelectionText({
                            anchor: Editor.before(editor, startPoint, {
                                distance: prefixText.length,
                                unit: 'character',
                            }) || Editor.start(editor, []),
                            focus: startPoint,
                        })
                        : '';
                    const currentSuffixText = suffixText.length > 0
                        ? getSelectionText({
                            anchor: endPoint,
                            focus: Editor.after(editor, endPoint, {
                                distance: suffixText.length,
                                unit: 'character',
                            }) || Editor.end(editor, []),
                        })
                        : '';

                    return currentPrefixText === prefixText && currentSuffixText === suffixText;
                } catch (_error) {
                    return false;
                }
            };
            annotations.filter((annotation): annotation is Extract<IAnnotationItem, { isComment: false }> => (
                !annotation.comment
                && !annotation.detached?.detached
            )).forEach((annotation) => {
                // We cache selections and texts so that updates to updated annotations selections
                // dont interfere with selection indices (e.g. delete a selection and cause indexing issues)
                const { selections } = annotation.quoteHistory[0];
                const { texts } = annotation.quoteHistory[0];
                const textAnchors = annotation.quoteHistory[0].textAnchors || [];
                const updatedQuote: IAnnotationQuote = {
                    id: uuidv4(),
                    timestamp: Date.now(),
                    selections: [],
                    texts: [],
                };
                // Indicates whether we need to update annotations locally and in DB
                let annotationUpdated = false;
                // Indicates whether the annotation no longer maps to post text
                let detachAnnotationOnSave = false;
                for (let i = 0; i < annotation.quoteHistory[0].selections.length; i += 1) {
                    const normalizedSelection = normalizePostSelectionPath(
                        selections[i],
                        chapters,
                        currentZoomLevel,
                        selectedPostValuePath,
                        annotation.chapterId,
                        annotation.sectionId,
                    );
                    const quoteText = texts[i];
                    const textAnchor = textAnchors[i];

                    /**
                     * Keeps this loop readable while still making the update rules
                     * explicit at each call site.
                     */
                    // eslint-disable-next-line no-loop-func
                    const pushSelectionUpdate = (nextSelection: Range, nextText: string): void => {
                        updatedQuote.selections.push(nextSelection);
                        updatedQuote.texts.push(nextText);

                        const selectionChanged = !deepEqual(nextSelection, selections[i]);
                        const textChanged = nextText !== quoteText;
                        if (!selectionChanged && !textChanged) {
                            return;
                        }

                        annotationUpdated = true;
                        annotationsUpdated = true;
                    };

                    /**
                     * When reconciliation cannot prove a safe move, keep the
                     * existing stored quote instead of deleting user data.
                     */
                    const preserveExistingSelection = (): void => {
                        updatedQuote.selections.push(selections[i]);
                        updatedQuote.texts.push(quoteText);
                    };

                    const selectionStillMatchesStoredTextAnchor = selectionMatchesStoredTextAnchor({
                        normalizedSelection: normalizedSelection as Range | null,
                        textAnchor,
                        quoteText,
                    });

                    if (selectionStillMatchesStoredTextAnchor) {
                        preserveExistingSelection();
                    } else if (!normalizedSelection) {
                        preserveExistingSelection();
                    } else {
                        const recoveredSelection = resolveUpdatedAnnotationSelection({
                            editor,
                            storedSelection: selections[i] as Range,
                            normalizedSelection: normalizedSelection as Range,
                            textAnchor,
                            quoteText,
                        });

                        if (!recoveredSelection) {
                            preserveExistingSelection();
                        } else if (recoveredSelection.text === '') {
                            if (annotation.quoteHistory[0].selections.length === 1) {
                                detachAnnotationOnSave = true;
                            }
                            annotationUpdated = true;
                            annotationsUpdated = true;
                        } else {
                            pushSelectionUpdate(
                                recoveredSelection.selection,
                                recoveredSelection.text,
                            );
                        }
                    }
                }

                if (annotationUpdated) {
                    if (detachAnnotationOnSave) {
                        updatedAnnotations = detachAnnotation(annotation, updatedAnnotations);
                    } else if (user) {
                        // Determine if any selections can be merged
                        const mergedQuoteSelections = [];
                        const mergedQuoteTexts = [];
                        for (let i = 0; i < updatedQuote.selections.length; i += 1) {
                            const selection = updatedQuote.selections[i];
                            const selectionText = updatedQuote.texts[i];

                            if (i > 0) {
                                const previousSelection: Range = mergedQuoteSelections[mergedQuoteSelections.length - 1]!;
                                const previousSelectionText: string = mergedQuoteTexts[mergedQuoteSelections.length - 1];
                                const previousSelectionEnd = Point.isBefore(previousSelection!.anchor as Point, previousSelection!.focus)
                                    ? previousSelection!.focus.offset
                                    : previousSelection!.anchor.offset;
                                const selectionStart = Point.isBefore(selection!.anchor as Point, selection!.focus)
                                    ? selection!.anchor.offset
                                    : selection!.focus.offset;
                                if (previousSelectionEnd === selectionStart) {
                                    // Merge selections
                                    const mergedSelection = {
                                        anchor: Point.isBefore(previousSelection!.anchor as Point, previousSelection!.focus as Point)
                                            ? previousSelection!.anchor
                                            : previousSelection!.focus,
                                        focus: Point.isBefore(selection!.anchor as Point, selection!.focus as Point)
                                            ? selection!.focus
                                            : selection!.anchor,
                                    };
                                    mergedQuoteSelections[mergedQuoteSelections.length - 1] = mergedSelection;
                                    mergedQuoteTexts[mergedQuoteSelections.length - 1] = `${previousSelectionText}${selectionText}`;
                                } else {
                                    mergedQuoteSelections.push(selection);
                                    mergedQuoteTexts.push(selectionText);
                                }
                            } else {
                                // First elements in merge arrays
                                mergedQuoteSelections.push(selection);
                                mergedQuoteTexts.push(selectionText);
                            }
                        }

                        if (mergedQuoteSelections.length === 0) {
                            // Annotation had multiple selections, but the edited
                            // content removed every anchored quote.
                            updatedAnnotations = detachAnnotation(annotation, updatedAnnotations);
                            return;
                        }

                        const newQuote: IAnnotationQuote = buildAnnotationQuote({
                            editor,
                            id: updatedQuote.id,
                            timestamp: updatedQuote.timestamp,
                            selections: mergedQuoteSelections,
                            texts: mergedQuoteTexts,
                        });
                        const updatedQuoteHistory: IAnnotationQuote[] = [
                            newQuote,
                            ...annotation.quoteHistory,
                        ];
                        updatedAnnotations.splice(
                            updatedAnnotations.findIndex((a) => a.id === annotation.id),
                            1,
                            {
                                ...annotation,
                                quoteHistory: updatedQuoteHistory,
                            },
                        );
                        updateAnnotation({
                            test: isTestMode(),
                            annotationId: annotation.id,
                            quoteHistoryToAdd: [newQuote],
                        });
                    }
                }
            });
            if (annotationsUpdated) {
                const updatedAnnotationsMap = new Map(updatedAnnotations.map((a) => [a.id, a]));
                setAnnotations(updatedAnnotationsMap);
            }
        };

        if (process.env.NODE_ENV === 'test') {
            reconcileAnnotationQuotes();
            return;
        }

        const timeoutId = window.setTimeout(
            reconcileAnnotationQuotes,
            POST_EDITOR_ANNOTATION_QUOTE_RECONCILIATION_DEBOUNCE_DURATION,
        );

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        value,
        annotationQuoteReconciliationTick,
    ]);

    /**
     * Delay editor select all text so it takes
     * place after default selection behavior
     */
    const {
        start: timeoutSelectAllText,
    } = useTimeout(() => {
        // Select entire contents of the editor
        Transforms.select(editor, {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
        });
    }, SLATE_EDITOR_SELECT_ALL_TEXT_TIMEOUT_DURATION);

    // ===== Methods =====

    /**
     * Manages logic to save the state of the editor
     */
    const handleAutoSave = (): void => {
        const checkpointReason = pendingCheckpointReasonRef.current || undefined;
        const savePostValue = (
            nextValue: string,
            path?: number[],
        ): void => {
            const sanitizedValue = usingCollaborativeDoc
                ? JSON.stringify(
                    stripBottomBufferNodes(
                        JSON.parse(nextValue) as Descendant[],
                        (node) => BottomBufferElement.isBottomBufferElement(node as Node),
                    ),
                )
                : nextValue;
            savePost(sanitizedValue, path, checkpointReason);
            if (checkpointReason != null) {
                pendingCheckpointReasonRef.current = null;
            }
        };
        if (!readOnly && postValue) {
            let parsedBeforeJSON;
            try {
                parsedBeforeJSON = JSON.parse(postValue);
            } catch (error) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `There was a problem interpreting the post content. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        },
                    });
                }
                throw Error(`There was a problem interpreting the post content. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
            }

            const before = usingCollaborativeDoc
                ? JSON.stringify(
                    stripBottomBufferNodes(
                        parsedBeforeJSON as Descendant[],
                        (node) => BottomBufferElement.isBottomBufferElement(node as Node),
                    ),
                )
                : JSON.stringify(parsedBeforeJSON);
            const after = usingCollaborativeDoc
                ? JSON.stringify(
                    stripBottomBufferNodes(
                        value,
                        (node) => BottomBufferElement.isBottomBufferElement(node as Node),
                    ),
                )
                : JSON.stringify(value);

            if (
                before !== after
                && !postValueIsSaving
            ) {
                let chapterIndex: number | undefined;
                let sectionIndex: number | undefined;
                if (
                    chapters
                    && !disableMergedWriteRouting
                    && currentZoomLevel
                    && currentZoomLevel.level < ZOOM_LEVEL.four
                ) {
                    // Determine which post value changed
                    let priorNodes = 0;
                    for (let i = 0; i < chapters.length; i += 1) {
                        chapterIndex = i;
                        if (chapters[i].sections) {
                            for (let j = 0; j < chapters[i].sections!.length; j += 1) {
                                sectionIndex = j;
                                const sectionValueString = chapters[i].sections![j].value;
                                if (typeof sectionValueString !== 'string') {
                                    break;
                                }
                                const sectionValue = JSON.parse(sectionValueString);
                                const correspondingValueInPost = JSON.parse(after)
                                    .slice(priorNodes, priorNodes + sectionValue.length);
                                const correspondingValueInPostString = JSON.stringify(correspondingValueInPost);
                                if (sectionValueString !== correspondingValueInPostString) {
                                    break;
                                }
                                priorNodes += sectionValue.length;
                            }
                        } else {
                            // Nullify sectionIndex residue from previous iteration
                            sectionIndex = undefined;
                            const chapterValueString = chapters[i].value;
                            if (typeof chapterValueString !== 'string') {
                                break;
                            }
                            const chapterValue = JSON.parse(chapterValueString);
                            const correspondingValueInPost = JSON.parse(after)
                                .slice(priorNodes, priorNodes + chapterValue.length);
                            const correspondingValueInPostString = JSON.stringify(correspondingValueInPost);
                            if (chapterValueString !== correspondingValueInPostString) {
                                break;
                            }
                            priorNodes += chapterValue.length;
                        }
                    }
                    let specificValue = JSON.parse(after).slice(priorNodes);
                    const bufferIndex = specificValue.findIndex((node: Node) => BottomBufferElement.isBottomBufferElement(node));
                    if (bufferIndex !== -1) {
                        specificValue = specificValue.slice(0, bufferIndex + 1);
                        const specificValueString = JSON.stringify(specificValue);
                        if (chapterIndex == null) return;
                        if (sectionIndex != null) {
                            savePostValue(specificValueString, [chapterIndex, sectionIndex]);
                        } else {
                            savePostValue(specificValueString, [chapterIndex]);
                        }
                    } else {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: `There was a problem saving the post. Unable to locate post value bottom buffer. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                                },
                            });
                        }
                        // eslint-disable-next-line max-len
                        throw Error(`There was a problem saving the post. Unable to locate post value bottom buffer. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                    }
                } else if (
                    chapters
                    && !disableMergedWriteRouting
                    && currentZoomLevel
                    && currentZoomLevel.level === ZOOM_LEVEL.four
                    && selectedPostValuePath.length === 1
                ) {
                    // Determine which post value changed
                    let priorNodes = 0;
                    const chapter = chapters[selectedPostValuePath[0]];
                    [chapterIndex] = selectedPostValuePath;
                    if (chapter.sections) {
                        for (let i = 0; i < chapter.sections.length; i += 1) {
                            sectionIndex = i;
                            const sectionValueString = chapter.sections![i].value;
                            if (typeof sectionValueString !== 'string') {
                                break;
                            }
                            const sectionValue = JSON.parse(sectionValueString);
                            const correspondingValueInPost = JSON.parse(after)
                                .slice(priorNodes, priorNodes + sectionValue.length);
                            const correspondingValueInPostString = JSON.stringify(correspondingValueInPost);
                            if (sectionValueString !== correspondingValueInPostString) {
                                break;
                            }
                            priorNodes += sectionValue.length;
                        }
                    }
                    let specificValue = (JSON.parse(after)).slice(priorNodes);
                    const bufferIndex = specificValue.findIndex((node: Node) => BottomBufferElement.isBottomBufferElement(node));
                    if (bufferIndex !== -1) {
                        specificValue = specificValue.slice(0, bufferIndex + 1);
                        const specificValueString = JSON.stringify(specificValue);
                        if (chapterIndex == null) return;
                        if (sectionIndex != null) {
                            savePostValue(specificValueString, [chapterIndex, sectionIndex]);
                        } else {
                            savePostValue(specificValueString, [chapterIndex]);
                        }
                    } else {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: `There was a problem saving the post. Unable to locate post value bottom buffer. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                                },
                            });
                        }
                        // eslint-disable-next-line max-len
                        throw Error(`There was a problem saving the post. Unable to locate post value bottom buffer. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                    }
                } else if (selectedPostValuePath.length === 2) {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(
                        after,
                        [selectedPostValuePath[0], selectedPostValuePath[1]],
                    );
                } else if (selectedPostValuePath.length === 1) {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(
                        after,
                        [selectedPostValuePath[0]],
                    );
                } else {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(after);
                }
            } else if (postValueIsSaving && !queueSave) {
                setQueueSave(true);
            }
        } else {
            const val = JSON.stringify(value, null, 2);
            if (!postValueIsSaving) {
                let chapterIndex: number | undefined;
                let sectionIndex: number | undefined;
                if (
                    chapters
                    && !disableMergedWriteRouting
                    && currentZoomLevel
                    && currentZoomLevel.level < ZOOM_LEVEL.four
                ) {
                    // Determine which post value changed
                    chapterIndex = 0;
                    if (chapters[0].sections) {
                        // Change the first section
                        sectionIndex = 0;
                    } else {
                        // Nullify sectionIndex residue from previous iteration
                        sectionIndex = undefined;
                    }
                    if (chapterIndex == null) return;
                    if (sectionIndex != null) {
                        savePostValue(val, [chapterIndex, sectionIndex]);
                    } else {
                        savePostValue(val, [chapterIndex]);
                    }
                } else if (
                    chapters
                    && !disableMergedWriteRouting
                    && currentZoomLevel
                    && currentZoomLevel.level === ZOOM_LEVEL.four
                    && selectedPostValuePath.length === 1
                ) {
                    // Determine which post value annotation is associated with
                    const chapter = chapters[selectedPostValuePath[0]];
                    [chapterIndex] = selectedPostValuePath;
                    if (chapter.sections) {
                        // Change the first section
                        sectionIndex = 0;
                    }
                    if (chapterIndex == null) return;
                    if (sectionIndex != null) {
                        savePostValue(val, [chapterIndex, sectionIndex]);
                    } else {
                        savePostValue(val, [chapterIndex]);
                    }
                } else if (selectedPostValuePath.length === 2) {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(
                        val,
                        [selectedPostValuePath[0], selectedPostValuePath[1]],
                    );
                } else if (selectedPostValuePath.length === 1) {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(
                        val,
                        [selectedPostValuePath[0]],
                    );
                } else {
                    // Zoom Level 5
                    // Proceed as normal
                    savePostValue(val);
                }
            } else if (postValueIsSaving && !queueSave) {
                setQueueSave(true);
            }
        }
    };

    /**
     * Manages the logic to correctly position the block toolbar
     */
    const updateBlockToolbarPosition = (): void => {
        const nativeSelection = window.getSelection();
        let y = 24;
        let x = -52;
        if (
            nativeSelection
            && nativeSelection.rangeCount > 0
            && containerRef.current
        ) {
            const range = nativeSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            y = rect.top - containerRef.current.getBoundingClientRect().top - 5;
            x = -52;
        }

        setBlockToolbarPosition({
            x,
            y,
        });
    };

    /**
     * Manages the logic to correctly position the selection toolbar
     */
    const updateSelectionToolbarPosition = (): void => {
        const nativeSelection = window.getSelection();
        let y = 24;
        let x = -52;
        if (
            nativeSelection
            && nativeSelection.rangeCount > 0
            && containerRef.current
            && selectionToolbarRef.current
        ) {
            const range = nativeSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const toolbarWidth = selectionToolbarRef.current.children[0]
                ? selectionToolbarRef.current.children[0].clientWidth
                : 0;
            y = (rect.top - containerRef.current.getBoundingClientRect().top
                - (selectionToolbarRef.current.offsetHeight + 7)
            );
            x = Math.max(
                ((rect.left - containerRef.current.getBoundingClientRect().left + rect.width / 2)
                    - (toolbarWidth / 2)
                ),
                0,
            );
        }
        setSelectionToolbarPosition({
            x,
            y,
        });
    };

    /**
     * Is reactive to changes to the slate value (making links)
     * and changes to the selection (placing cursor on links)
     */
    const getSelectedLinkURL = useMemo(() => {
        if (editor.selection && !readOnly) {
            const links = WebLinkHelpers.getSelectionWebLinks(editor);
            if (links.length === 1) {
                const [link] = links;
                return (link[0]as WebLinkNode).href;
            }
        }
        return null;
    }, [
        value,
        editor.selection,
    ]);

    /**
     * Is reactive to changes to the slate value (making links)
     * and changes to the selection (placing cursor on links)
     */
    const getSelectedLinkID = useMemo(() => {
        if (editor.selection && !readOnly) {
            const links = WebLinkHelpers.getSelectionWebLinks(editor);
            if (links.length === 1) {
                const [link] = links;
                return (link[0]as WebLinkNode).id;
            }
        }
        return null;
    }, [
        value,
        editor.selection,
    ]);

    /**
     * Manages setting the slate editor reference
     * @param ref ref to the slate editor
     */
    const setSlateEditorRef = (ref: Editor): void => {
        if (ref && setRef) {
            setRef(ref);
        }
    };

    /**
     * Manages the logic to move the cursor to the end of the document
     */
    const moveCursorToEndOfDocument = (): void => {
        try {
            Transforms.select(editor, Editor.end(editor, []));
        } catch (err) {
            return;
        }
        BottomBufferHelpers.adjustSelectionAroundBottomBuffer(editor);
        ReactEditor.focus(editor);
    };

    /**
     * Places a specified emoji in the editor
     * @param emojiObj emoji object
     */
    const handleEmojiSelect = (emojiObj: IEmoji): void => {
        ReactEditor.focus(editor);
        Transforms.insertText(editor, emojiObj.char);
    };

    /**
     * Manages response to editor hovering over
     * @param e mouse event
     */
    const onEditableEnter = (e: React.MouseEvent): void => {
        if (onCursorEnter && !resizingAnnotationView) {
            const editableCursorSign = editableUsesPointerCursor || !readOnly
                ? CURSOR_SIGN.click
                : CURSOR_SIGN.highlight;
            onCursorEnter(
                editableUsesPointerCursor
                    ? CURSOR_TARGET.editorButton
                    : CURSOR_TARGET.editor,
                [editableCursorSign],
                e.currentTarget as HTMLElement,
            );
        }
    };

    /**
     * Manages response to editor no longer hovering over
     * @param e mouse event
     */
    const onEditableLeave = (e: React.MouseEvent): void => {
        if (onCursorLeave && !resizingAnnotationView) onCursorLeave(e);
    };

    /**
     * Keeps complex editor elements like figures and audio notes aligned with
     * the destination-picking cursor mode even when those renderers ask for
     * their usual element-specific cursor targets. We intentionally anchor
     * to the editor surface during pick mode so large void elements do not
     * change the pointer cursor presentation.
     */
    const onRenderableElementCursorEnter = useCallback((
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ): void => {
        if (editableUsesPointerCursor) {
            onCursorEnter(
                CURSOR_TARGET.editorButton,
                [CURSOR_SIGN.click],
                resolveEditableCursorTarget() || candidateTarget || undefined,
            );
            return;
        }

        onCursorEnter(
            targetType,
            actions,
            candidateTarget,
        );
    }, [
        editableUsesPointerCursor,
        onCursorEnter,
        resolveEditableCursorTarget,
    ]);
    const onRenderableElementCursorLeave = useCallback((
        e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent,
    ): void => {
        if (editableUsesPointerCursor) {
            onCursorEnter(
                CURSOR_TARGET.editorButton,
                [CURSOR_SIGN.click],
                resolveEditableCursorTarget(),
            );
            return;
        }

        onCursorLeave(e);
    }, [
        editableUsesPointerCursor,
        onCursorEnter,
        onCursorLeave,
        resolveEditableCursorTarget,
    ]);

    /**
     * Manages response to editor being focused
     */
    const onEditableFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        if (hasSound) {
            const audioClip = getOrCreateAudioClip({
                audioRef: inputClickClip,
                src: InputClick,
                volume: DEFAULT_AUDIO_VOLUME,
            });
            audioClip.pause();
            audioClip.currentTime = 0;
            playAudio(audioClip);
        }
    };

    /**
     * Manages response to editor being blurred
     */
    const onEditableBlur = (): void => {
        setInputFocused(false);
    };

    /**
     * While the portable toolbar is collecting an in-post link destination, keep
     * Slate from moving the original text selection and stage the clicked target
     * for later Accept/Redo/Cancel confirmation in the toolbar.
     */
    const onEditablePointerDownCapture = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        if (
            readOnly
            || !internalPostLinkSelectionActive
        ) {
            return;
        }

        const target = e.target as HTMLElement | null;
        const linkTargetElement = target?.closest?.('[data-post-link-target-id]') as HTMLElement | null;
        e.preventDefault();
        e.stopPropagation();

        if (!linkTargetElement) {
            return;
        }

        const paragraphId = `${linkTargetElement.dataset.postLinkTargetId || ''}`.trim();
        if (!paragraphId) {
            return;
        }

        const targetLabel = `${linkTargetElement.dataset.postLinkTargetLabel || linkTargetElement.textContent || ''}`
            .replace(/\s+/g, ' ')
            .trim();
        setPendingInternalPostLinkTarget({
            href: buildInternalPostLinkHref({
                chapterId: currentInternalPostLinkScopeSelection.chapterId || undefined,
                sectionId: currentInternalPostLinkScopeSelection.sectionId || undefined,
                paragraphId,
            }),
            paragraphId,
            ...(currentInternalPostLinkScopeSelection.chapterId
                ? {
                    chapterId: currentInternalPostLinkScopeSelection.chapterId,
                }
                : {}
            ),
            ...(currentInternalPostLinkScopeSelection.sectionId
                ? {
                    sectionId: currentInternalPostLinkScopeSelection.sectionId,
                }
                : {}
            ),
            ...(targetLabel
                ? {
                    label: targetLabel,
                }
                : {}
            ),
        });
        setInternalPostLinkSelectionMode(INTERNAL_POST_LINK_SELECTION_MODE.inline);
        setInternalPostLinkSelectionActive(false);
    }, [
        readOnly,
        internalPostLinkSelectionActive,
        currentInternalPostLinkScopeSelection,
    ]);
    const closeInternalPostLinkScopePicker = useCallback((): void => {
        setInternalPostLinkScopePickerVisible(false);
    }, []);
    /**
     * Keeps the cross-scope picker pointed at a scope that still has at least
     * one selectable heading, paragraph, or block quote destination.
     */
    const resolveValidInternalPostLinkScopePickerSelection = useCallback((
        selection: {
            scope: POST_CONTENT_SCOPE,
            chapterId: string | null,
            sectionId: string | null,
        },
    ): {
        scope: POST_CONTENT_SCOPE,
        chapterId: string | null,
        sectionId: string | null,
    } => {
        const rootSelection = {
            scope: POST_CONTENT_SCOPE.root,
            chapterId: null,
            sectionId: null,
        };
        const firstAvailableChapterId = internalPostLinkScopePickerAvailableChapters[0]?.id || null;
        const firstAvailableSectionChapterId = internalPostLinkScopePickerAvailableSectionChapters[0]?.id || null;

        if (
            selection.scope === POST_CONTENT_SCOPE.root
            && internalPostLinkScopePickerHasRootScopeTargets
        ) {
            return rootSelection;
        }

        if (selection.scope === POST_CONTENT_SCOPE.chapter) {
            const nextChapterId = internalPostLinkScopePickerAvailableChapters.find(
                (chapter) => chapter.id === selection.chapterId,
            )?.id || firstAvailableChapterId;
            if (nextChapterId) {
                return {
                    scope: POST_CONTENT_SCOPE.chapter,
                    chapterId: nextChapterId,
                    sectionId: null,
                };
            }
        }

        if (selection.scope === POST_CONTENT_SCOPE.section) {
            const nextChapterId = internalPostLinkScopePickerAvailableSectionChapters.find(
                (chapter) => chapter.id === selection.chapterId,
            )?.id || firstAvailableSectionChapterId;
            if (nextChapterId) {
                const availableSections = resolveInternalPostLinkScopePickerAvailableSections(nextChapterId);
                const nextSectionId = availableSections.find(
                    (section) => section.id === selection.sectionId,
                )?.id || availableSections[0]?.id || null;
                if (nextSectionId) {
                    return {
                        scope: POST_CONTENT_SCOPE.section,
                        chapterId: nextChapterId,
                        sectionId: nextSectionId,
                    };
                }
            }
        }

        if (internalPostLinkScopePickerHasRootScopeTargets) {
            return rootSelection;
        }

        if (firstAvailableChapterId) {
            return {
                scope: POST_CONTENT_SCOPE.chapter,
                chapterId: firstAvailableChapterId,
                sectionId: null,
            };
        }

        if (firstAvailableSectionChapterId) {
            const availableSections = resolveInternalPostLinkScopePickerAvailableSections(
                firstAvailableSectionChapterId,
            );
            const firstAvailableSectionId = availableSections[0]?.id || null;
            if (firstAvailableSectionId) {
                return {
                    scope: POST_CONTENT_SCOPE.section,
                    chapterId: firstAvailableSectionChapterId,
                    sectionId: firstAvailableSectionId,
                };
            }
        }

        return rootSelection;
    }, [
        internalPostLinkScopePickerAvailableChapters,
        internalPostLinkScopePickerAvailableSectionChapters,
        internalPostLinkScopePickerHasRootScopeTargets,
        resolveInternalPostLinkScopePickerAvailableSections,
    ]);
    const openInternalPostLinkScopePicker = useCallback((target?: IInternalPostLinkTarget | null): void => {
        setInternalPostLinkSelectionMode(INTERNAL_POST_LINK_SELECTION_MODE.modal);
        setInternalPostLinkSelectionActive(false);
        setPendingInternalPostLinkTarget(null);
        setInternalPostLinkScopePickerSelection(
            resolveValidInternalPostLinkScopePickerSelection(
                resolveInternalPostLinkScopePickerSelection(target),
            ),
        );
        setInternalPostLinkScopePickerVisible(true);
    }, [
        resolveInternalPostLinkScopePickerSelection,
        resolveValidInternalPostLinkScopePickerSelection,
    ]);
    useEffect(() => {
        if (!internalPostLinkScopePickerVisible) {
            return;
        }

        setInternalPostLinkScopePickerSelection((currentSelection) => {
            const nextSelection = resolveValidInternalPostLinkScopePickerSelection(currentSelection);
            if (
                nextSelection.scope === currentSelection.scope
                && nextSelection.chapterId === currentSelection.chapterId
                && nextSelection.sectionId === currentSelection.sectionId
            ) {
                return currentSelection;
            }

            return nextSelection;
        });
    }, [
        internalPostLinkScopePickerVisible,
        resolveValidInternalPostLinkScopePickerSelection,
    ]);
    const handleInternalPostLinkScopePickerPreviewPointerDownCapture = useCallback((
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        const target = e.target as HTMLElement | null;
        const linkTargetElement = target?.closest?.('[data-post-link-target-id]') as HTMLElement | null;
        if (!linkTargetElement) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const paragraphId = `${linkTargetElement.dataset.postLinkTargetId || ''}`.trim();
        if (!paragraphId) {
            return;
        }

        const targetLabel = `${linkTargetElement.dataset.postLinkTargetLabel || linkTargetElement.textContent || ''}`
            .replace(/\s+/g, ' ')
            .trim();
        setPendingInternalPostLinkTarget({
            href: buildInternalPostLinkHref({
                chapterId: internalPostLinkScopePickerSelection.chapterId || undefined,
                sectionId: internalPostLinkScopePickerSelection.sectionId || undefined,
                paragraphId,
            }),
            paragraphId,
            ...(internalPostLinkScopePickerSelection.chapterId
                ? {
                    chapterId: internalPostLinkScopePickerSelection.chapterId,
                }
                : {}
            ),
            ...(internalPostLinkScopePickerSelection.sectionId
                ? {
                    sectionId: internalPostLinkScopePickerSelection.sectionId,
                }
                : {}
            ),
            ...(targetLabel
                ? {
                    label: targetLabel,
                }
                : {}
            ),
        });
        setInternalPostLinkSelectionMode(INTERNAL_POST_LINK_SELECTION_MODE.modal);
        setInternalPostLinkSelectionActive(false);
    }, [internalPostLinkScopePickerSelection]);
    const handleInternalPostLinkScopePickerChapterChange = useCallback((
        e: React.ChangeEvent<HTMLSelectElement>,
    ): void => {
        const nextChapterId = e.target.value || null;
        const nextSectionId = resolveInternalPostLinkScopePickerAvailableSections(nextChapterId)[0]?.id || null;
        setInternalPostLinkScopePickerSelection((currentSelection) => ({
            scope: currentSelection.scope === POST_CONTENT_SCOPE.section
                && nextSectionId
                ? POST_CONTENT_SCOPE.section
                : POST_CONTENT_SCOPE.chapter,
            chapterId: nextChapterId,
            sectionId: nextSectionId,
        }));
    }, [resolveInternalPostLinkScopePickerAvailableSections]);
    const handleInternalPostLinkScopePickerSectionChange = useCallback((
        e: React.ChangeEvent<HTMLSelectElement>,
    ): void => {
        setInternalPostLinkScopePickerSelection((currentSelection) => ({
            ...currentSelection,
            scope: POST_CONTENT_SCOPE.section,
            sectionId: e.target.value || null,
        }));
    }, []);
    const noopCallback = useCallback((): void => undefined, []);
    const noopSetAnnotations = noopCallback as React.Dispatch<React.SetStateAction<Map<string, IAnnotationItem>>>;
    const noopSetBooleanState = noopCallback as React.Dispatch<React.SetStateAction<boolean>>;
    const noopSetSnackbarData = noopCallback as React.Dispatch<React.SetStateAction<ISnackbarItem>>;
    const noopSetInputFocused = noopCallback as React.Dispatch<React.SetStateAction<boolean>>;
    const noopSetEditor = noopCallback as (editor: Editor | null) => void;
    const noopSavePost = noopCallback as (
        value: string,
        path?: number[],
        checkpointReason?: POST_COLLAB_DOC_CHECKPOINT_REASON,
    ) => void;
    const startInlineInternalPostLinkSelection = useCallback((): void => {
        setPendingInternalPostLinkTarget(null);
        setInternalPostLinkSelectionMode(INTERNAL_POST_LINK_SELECTION_MODE.inline);
        setInternalPostLinkScopePickerVisible(false);
        setInternalPostLinkSelectionActive(true);
        activateEditorPointerCursor();
    }, [activateEditorPointerCursor]);
    const redoInternalPostLinkSelection = useCallback((): void => {
        if (internalPostLinkSelectionMode === INTERNAL_POST_LINK_SELECTION_MODE.modal) {
            openInternalPostLinkScopePicker(pendingInternalPostLinkTarget);
            return;
        }

        startInlineInternalPostLinkSelection();
    }, [
        internalPostLinkSelectionMode,
        openInternalPostLinkScopePicker,
        pendingInternalPostLinkTarget,
        startInlineInternalPostLinkSelection,
    ]);
    const handleInternalPostLinkScopePickerRedo = useCallback((
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        e.preventDefault();
        e.stopPropagation();
        redoInternalPostLinkSelection();
    }, [redoInternalPostLinkSelection]);
    const handleInternalPostLinkScopePickerAccept = useCallback((
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        e.preventDefault();
        e.stopPropagation();

        if (!pendingInternalPostLinkTarget?.href) {
            return;
        }

        setInternalPostLinkScopePickerVisible(false);
        setInternalPostLinkSelectionSubmitKey((currentKey) => currentKey + 1);
    }, [pendingInternalPostLinkTarget]);
    const clearInternalPostLinkSelection = useCallback((): void => {
        setPendingInternalPostLinkTarget(null);
        setInternalPostLinkSelectionActive(false);
        setInternalPostLinkScopePickerVisible(false);
    }, []);

    /**
     * Slate's default copy path clones the rendered editor DOM, which strips
     * our semantic formatting back down to presentation spans in many apps.
     * Keep Slate's fragment payload for same-editor paste, then overwrite the
     * plain-text and html clipboard entries with the shared semantic serializer.
     */
    const handleEditableCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>): void => {
        if (readOnly || !event.clipboardData || !editor.selection) {
            return;
        }

        const serializedSelection = serializeSlateSelectionForClipboard({
            editor,
            selection: editor.selection,
        });
        if (!serializedSelection) {
            return;
        }

        ReactEditor.setFragmentData(editor, event.clipboardData, 'copy');
        event.clipboardData.setData('text/plain', serializedSelection.text);
        event.clipboardData.setData('text/html', serializedSelection.html);
        event.preventDefault();
    }, [
        editor,
        readOnly,
    ]);

    /**
     * Rich clipboard payloads can be reduced by the browser before Slate's
     * beforeinput handler sees them, so paste directly from the original event
     * whenever that clipboardData is available.
     */
    const handleEditablePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>): boolean => {
        if (readOnly) {
            return false;
        }

        return handleEditorClipboardPaste({
            editor,
            event,
        });
    }, [
        editor,
        readOnly,
    ]);

    /**
     * Manages completion update of an uploading media
     * @param mediaItem media item that was uploaded
     * @param fileURL storage url of the uploaded file
     */
    const handleUploadingMediaComplete = (
        mediaItem: IMediaItem,
        fileURL: string,
    ): void => {
        // Select end of document if no selection
        if (!editor.selection) {
            moveCursorToEndOfDocument();
        }

        // Update uploading media item
        updateUploadingMedia({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ...uploadingMedia.get(mediaItem.id)!,
            url: fileURL,
        });

        const fileType = mediaItem.file.type.split('/')[0];
        switch (fileType) {
        case 'audio': {
            // Create Audio Note
            AudioNoteHelpers.insertAudioNote(editor, {
                id: mediaItem.id,
                filePath: mediaItem.filePath,
                title: mediaItem.file.name,
                uploaded: true,
            });
            break;
        }
        case 'image': {
            // Create Figure
            FigureHelpers.insertFigure(
                editor,
                mediaItem.id,
                undefined,
                mediaItem.filePath,
                mediaItem.file.name,
            );
            break;
        }
        default:
            break;
        }
    };

    /**
     * Manages progress update of an uploading media
     * @param mediaItem media item that is uploading
     * @param _snapshot upload task snapshot
     * @param progress perentage of upload completed
     */
    const handleUploadingMediaStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingMedia.has(mediaItem.id)) {
            updateUploadingMedia({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...uploadingMedia.get(mediaItem.id)!,
                uploadProgress: progress,
            });
        } else {
            setUploadingMedia(mediaItem);
        }
    };

    /**
     * Manages presenting a dialog to the user if they are about to abandon
     * an unsaved audio note, inquiring if they would like to save it
     * @param _e unused event
     */
    const handleUnsavedAudioNoteDialog = (_e: Event): void => {
        if (isRecordingAudioNote) {
            // Inform user that they are attempting to save while audio
            // recording in progress
            setModal({
                visible: true,
                message: SAVE_POST_DURING_AUDIO_RECORDING_ERROR,
                proceedCallback: () => {
                    handleAutoSave();
                    setModal({
                        visible: false,
                        message: modal.message,
                    });
                },
                rejectCallback: () => {
                    setModal({
                        visible: false,
                        message: modal.message,
                    });
                },
            });
        }
    };

    /**
     * Manages response to an image fully loading
     */
    const onImageLoad = (): void => {
        setRerenderMeasuringElements(true);
        setRemeasurePostHeight(true);
    };

    /**
     * Manages response to an embed fully loading
     */
    const onEmbedLoad = (): void => {
        setRerenderMeasuringElements(true);
        setRemeasurePostHeight(true);
    };

    /**
     * Signals to remeasure the height of the post
     */
    const onAudioNoteAdjustHeight = (): void => {
        setRerenderMeasuringElements(true);
        setRemeasurePostHeight(true);
    };

    // ===== Rendering =====

    const renderModal = (): React.ReactElement => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={modal.visible}
            closeModal={modal.rejectCallback}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={modal.message}
                callback={modal.proceedCallback}
                hideCancel={!modal.rejectCallback}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );
    const renderInternalPostLinkScopePickerModal = (): React.ReactElement => (
        <Modal
            hasContainer={false}
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={internalPostLinkScopePickerVisible}
            closeModal={closeInternalPostLinkScopePicker}
        >
            <HistoryModalContainer $compact>
                <HistoryModalHeader>
                    <HistoryModalCloseButtonContainer>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.floating}
                            background={themeObj.verascopeColor.purple300}
                            height={2.5 * BODY_FONT_SIZE}
                            width={2.5 * BODY_FONT_SIZE}
                            icon={CrossIcon}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: (e) => onCursorEnter(
                                    CURSOR_TARGET.modalCloseButton,
                                    [CURSOR_SIGN.click],
                                    e.currentTarget as HTMLElement,
                                ),
                                onMouseLeave: onCursorLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: closeInternalPostLinkScopePicker,
                                onMouseDown: closeInternalPostLinkScopePicker,
                            })}
                        />
                    </HistoryModalCloseButtonContainer>
                    <HistoryModalTitle>Link within this post</HistoryModalTitle>
                    <HistoryModalSubtitle>
                        Choose a chapter or section, then click the heading, paragraph, or block quote you want this link to jump to.
                    </HistoryModalSubtitle>
                </HistoryModalHeader>
                <HistorySinglePaneBody>
                    <HistoryPreviewPane>
                        <InternalPostLinkScopePickerActions>
                            <HistoryAccessSection data-testid="internal-post-link-scope-picker-controls">
                                <InternalPostLinkScopePickerControlsColumn>
                                    <HistoryScopeLabel>Destination scope</HistoryScopeLabel>
                                    <InternalPostLinkScopePickerTargetRow>
                                        <HistoryTargetControls $centered>
                                            <HistoryTargetButtonGroup>
                                                {internalPostLinkScopePickerHasRootScopeTargets && (
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        background={internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.root
                                                            ? themeObj.verascopeColor.purple300
                                                            : themeObj.color.cream400}
                                                        text="Post"
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: (e) => onCursorEnter(
                                                                CURSOR_TARGET.editorButton,
                                                                [CURSOR_SIGN.click],
                                                                    e.currentTarget as HTMLElement,
                                                            ),
                                                            onMouseLeave: onCursorLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => setInternalPostLinkScopePickerSelection({
                                                                scope: POST_CONTENT_SCOPE.root,
                                                                chapterId: null,
                                                                sectionId: null,
                                                            }),
                                                            onMouseDown: () => setInternalPostLinkScopePickerSelection({
                                                                scope: POST_CONTENT_SCOPE.root,
                                                                chapterId: null,
                                                                sectionId: null,
                                                            }),
                                                        })}
                                                    />
                                                )}
                                                {canShowInternalPostLinkChapterScopeButton && (
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        background={internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.chapter
                                                            ? themeObj.verascopeColor.purple300
                                                            : themeObj.color.cream400}
                                                        text="Chapter"
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: (e) => onCursorEnter(
                                                                CURSOR_TARGET.editorButton,
                                                                [CURSOR_SIGN.click],
                                                                    e.currentTarget as HTMLElement,
                                                            ),
                                                            onMouseLeave: onCursorLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => {
                                                                const nextChapterId = internalPostLinkScopePickerAvailableChapters.find(
                                                                    (chapter) => chapter.id === internalPostLinkScopePickerSelection.chapterId,
                                                                )?.id || internalPostLinkScopePickerAvailableChapters[0]?.id || null;
                                                                setInternalPostLinkScopePickerSelection({
                                                                    scope: POST_CONTENT_SCOPE.chapter,
                                                                    chapterId: nextChapterId,
                                                                    sectionId: null,
                                                                });
                                                            },
                                                            onMouseDown: () => {
                                                                const nextChapterId = internalPostLinkScopePickerAvailableChapters.find(
                                                                    (chapter) => chapter.id === internalPostLinkScopePickerSelection.chapterId,
                                                                )?.id || internalPostLinkScopePickerAvailableChapters[0]?.id || null;
                                                                setInternalPostLinkScopePickerSelection({
                                                                    scope: POST_CONTENT_SCOPE.chapter,
                                                                    chapterId: nextChapterId,
                                                                    sectionId: null,
                                                                });
                                                            },
                                                        })}
                                                    />
                                                )}
                                                {canShowInternalPostLinkSectionScopeButton && (
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        background={internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.section
                                                            ? themeObj.verascopeColor.purple300
                                                            : themeObj.color.cream400}
                                                        text="Section"
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: (e) => onCursorEnter(
                                                                CURSOR_TARGET.editorButton,
                                                                [CURSOR_SIGN.click],
                                                                    e.currentTarget as HTMLElement,
                                                            ),
                                                            onMouseLeave: onCursorLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => {
                                                                const resolvedChapterId = internalPostLinkScopePickerAvailableSectionChapters.find(
                                                                    (chapter) => chapter.id === internalPostLinkScopePickerSelection.chapterId,
                                                                )?.id || internalPostLinkScopePickerAvailableSectionChapters[0]?.id || null;
                                                                const nextSectionId = resolveInternalPostLinkScopePickerAvailableSections(
                                                                    resolvedChapterId,
                                                                )[0]?.id || null;
                                                                const nextChapterId = resolvedChapterId;
                                                                if (!nextChapterId || !nextSectionId) {
                                                                    return;
                                                                }
                                                                setInternalPostLinkScopePickerSelection({
                                                                    scope: POST_CONTENT_SCOPE.section,
                                                                    chapterId: nextChapterId,
                                                                    sectionId: nextSectionId,
                                                                });
                                                            },
                                                            onMouseDown: () => {
                                                                const resolvedChapterId = internalPostLinkScopePickerAvailableSectionChapters.find(
                                                                    (chapter) => chapter.id === internalPostLinkScopePickerSelection.chapterId,
                                                                )?.id || internalPostLinkScopePickerAvailableSectionChapters[0]?.id || null;
                                                                const nextSectionId = resolveInternalPostLinkScopePickerAvailableSections(
                                                                    resolvedChapterId,
                                                                )[0]?.id || null;
                                                                const nextChapterId = resolvedChapterId;
                                                                if (!nextChapterId || !nextSectionId) {
                                                                    return;
                                                                }
                                                                setInternalPostLinkScopePickerSelection({
                                                                    scope: POST_CONTENT_SCOPE.section,
                                                                    chapterId: nextChapterId,
                                                                    sectionId: nextSectionId,
                                                                });
                                                            },
                                                        })}
                                                    />
                                                )}
                                            </HistoryTargetButtonGroup>
                                            {(internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.chapter
                                                    || internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.section)
                                                    && internalPostLinkScopePickerChapterScopeOptions.length > 0 && (
                                                <HistoryTargetSelectGroup $centered>
                                                    <HistoryScopeSelect
                                                        $compact
                                                        $fill
                                                        customCursorActive={customCursorActive}
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        aria-label="Internal link target chapter scope"
                                                        value={internalPostLinkScopePickerSelection.chapterId || ''}
                                                        onMouseEnter={(e) => onCursorEnter(
                                                            CURSOR_TARGET.editorButton,
                                                            [CURSOR_SIGN.click],
                                                                e.currentTarget as HTMLElement,
                                                        )}
                                                        onMouseLeave={onCursorLeave}
                                                        onChange={handleInternalPostLinkScopePickerChapterChange}
                                                    >
                                                        {internalPostLinkScopePickerChapterScopeOptions.map((chapter) => (
                                                            <option key={chapter.id} value={chapter.id}>
                                                                {chapter.title}
                                                            </option>
                                                        ))}
                                                    </HistoryScopeSelect>
                                                    {internalPostLinkScopePickerSelection.scope === POST_CONTENT_SCOPE.section
                                                            && internalPostLinkScopePickerSelectedChapterAvailableSections.length > 0 && (
                                                        <>
                                                            <HistoryScopeSeparator>/</HistoryScopeSeparator>
                                                            <HistoryScopeSelect
                                                                $compact
                                                                $fill
                                                                customCursorActive={customCursorActive}
                                                                className={HOVER_TARGET_CLASSNAME}
                                                                aria-label="Internal link target section scope"
                                                                value={internalPostLinkScopePickerSelection.sectionId || ''}
                                                                onMouseEnter={(e) => onCursorEnter(
                                                                    CURSOR_TARGET.editorButton,
                                                                    [CURSOR_SIGN.click],
                                                                        e.currentTarget as HTMLElement,
                                                                )}
                                                                onMouseLeave={onCursorLeave}
                                                                onChange={handleInternalPostLinkScopePickerSectionChange}
                                                            >
                                                                {internalPostLinkScopePickerSelectedChapterAvailableSections.map((section) => (
                                                                    <option key={section.id} value={section.id}>
                                                                        {section.title}
                                                                    </option>
                                                                ))}
                                                            </HistoryScopeSelect>
                                                        </>
                                                    )}
                                                </HistoryTargetSelectGroup>
                                            )}
                                        </HistoryTargetControls>
                                    </InternalPostLinkScopePickerTargetRow>
                                </InternalPostLinkScopePickerControlsColumn>
                            </HistoryAccessSection>
                            <InternalPostLinkScopePickerHelpText>
                                {internalPostLinkScopePickerStagedTargetSummary
                                    ? 'Destination staged below. Click another heading, paragraph, or block quote to replace it, or confirm it here.'
                                    : 'Click any heading, paragraph, or block quote in the preview below to stage it as the destination.'}
                            </InternalPostLinkScopePickerHelpText>
                            {internalPostLinkScopePickerStagedTargetSummary && (
                                <InternalPostLinkScopePickerSummaryCard
                                    data-testid="internal-post-link-scope-picker-staged-target"
                                >
                                    <InternalPostLinkScopePickerSummaryEyebrow>
                                        Destination selected
                                    </InternalPostLinkScopePickerSummaryEyebrow>
                                    <HistoryPublishSummaryTitle>
                                        {internalPostLinkScopePickerStagedTargetSummary.title}
                                    </HistoryPublishSummaryTitle>
                                    <InternalPostLinkScopePickerSummarySubtitle>
                                        {internalPostLinkScopePickerStagedTargetSummary.scopeLabel}
                                    </InternalPostLinkScopePickerSummarySubtitle>
                                    <InternalPostLinkScopePickerSummaryActions>
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            background={themeObj.verascopeColor.purple200}
                                            text="Redo"
                                            icon={EditIcon}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: (e) => onCursorEnter(
                                                    CURSOR_TARGET.editorButton,
                                                    [CURSOR_SIGN.click],
                                                    e.currentTarget as HTMLElement,
                                                ),
                                                onMouseLeave: onCursorLeave,
                                            })}
                                            {...getPressEventHandlers({
                                                onTouchStart: handleInternalPostLinkScopePickerRedo,
                                                onMouseDown: handleInternalPostLinkScopePickerRedo,
                                            })}
                                        />
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            background={color}
                                            text="Accept"
                                            icon={ArrowIcon}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: (e) => onCursorEnter(
                                                    CURSOR_TARGET.editorButton,
                                                    [CURSOR_SIGN.click],
                                                    e.currentTarget as HTMLElement,
                                                ),
                                                onMouseLeave: onCursorLeave,
                                            })}
                                            {...getPressEventHandlers({
                                                onTouchStart: handleInternalPostLinkScopePickerAccept,
                                                onMouseDown: handleInternalPostLinkScopePickerAccept,
                                            })}
                                        />
                                    </InternalPostLinkScopePickerSummaryActions>
                                </InternalPostLinkScopePickerSummaryCard>
                            )}
                        </InternalPostLinkScopePickerActions>
                        <InternalPostLinkScopePickerPreviewContainer
                            ref={internalPostLinkScopePickerPreviewRef}
                            data-testid="internal-post-link-scope-picker-preview"
                            onMouseDownCapture={handleInternalPostLinkScopePickerPreviewPointerDownCapture}
                            onTouchStartCapture={handleInternalPostLinkScopePickerPreviewPointerDownCapture}
                        >
                            {!internalPostLinkScopePickerPreview.available && (
                                <HistoryAccessPane>
                                    <HistoryEmptyState>
                                        This scope is not loaded yet. Open it in the editor first, then come back to pick a destination here.
                                    </HistoryEmptyState>
                                </HistoryAccessPane>
                            )}
                            {internalPostLinkScopePickerPreview.available && (
                                <PostEditor
                                    customCursorActive={customCursorActive}
                                    id={`${id}-internal-post-link-preview`}
                                    user={user}
                                    userIsAdmin={userIsAdmin}
                                    hasSound={false}
                                    currentSessionId={currentSessionId}
                                    postValue={internalPostLinkScopePickerPreview.postValue}
                                    postValueNodes={internalPostLinkScopePickerPreview.postValueNodes}
                                    rootPostTitle={rootPostTitle}
                                    rootPostValue={rootPostValue}
                                    rootPostValueNodes={rootPostValueNodes}
                                    rootPostPreviewValueNodes={rootPostPreviewValueNodes}
                                    type={type}
                                    color={color}
                                    readOnly
                                    placeholder={placeholder}
                                    bufferHeight={0}
                                    parentRef={internalPostLinkScopePickerPreviewRef.current}
                                    scrollParentRef={internalPostLinkScopePickerPreviewRef}
                                    isAuthor={isAuthor}
                                    acceptedFiles={[]}
                                    uploadingMedia={new Map()}
                                    setUploadingMedia={setUploadingMedia}
                                    updateUploadingMedia={updateUploadingMedia}
                                    removeUploadingMedia={removeUploadingMedia}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    setInputFocused={noopSetInputFocused}
                                    savePost={noopSavePost}
                                    postValueIsSaving={false}
                                    setEditor={noopSetEditor}
                                    postQuote={null}
                                    annotations={[]}
                                    setAnnotations={noopSetAnnotations}
                                    chapters={chapters}
                                    setAnnotationHighlightsRendered={noopSetBooleanState}
                                    annotationHighlightsRendered={false}
                                    setRemeasurePostHeight={noopSetBooleanState}
                                    setSnackbarData={noopSetSnackbarData}
                                    currentZoomLevel={currentZoomLevel}
                                    hasSuccess={false}
                                    hasError={false}
                                    selectedPostValuePath={internalPostLinkScopePickerPreview.path}
                                    disableProgressiveReadOnly
                                    forcePointerCursor
                                    unusedZoomLevelCount={unusedZoomLevelCount}
                                    zoomLevelIcons={zoomLevelIcons}
                                    resizingAnnotationView={false}
                                />
                            )}
                        </InternalPostLinkScopePickerPreviewContainer>
                    </HistoryPreviewPane>
                </HistorySinglePaneBody>
            </HistoryModalContainer>
        </Modal>
    );

    const renderElement = useCallback(({
        element,
        attributes,
        children,
    } : {
        element: any,
        attributes: any,
        children: any,
    }) => {
        if (BlockQuoteElement.isBlockQuoteElement(element)) {
            return (
                <BlockQuote
                    paragraphId={element.paragraphId}
                    plainText={Node.string(element)}
                    attributes={attributes}
                >
                    {children}
                </BlockQuote>
            );
        }

        if (BottomBufferElement.isBottomBufferElement(element)) {
            return (
                <BottomBuffer
                    adjustSelectionAroundBottomBuffer={() => BottomBufferHelpers.adjustSelectionAroundBottomBuffer(editor)}
                    height={bufferHeight}
                    currentZoomLevel={currentZoomLevel}
                    attributes={attributes}
                >
                    {children}
                </BottomBuffer>
            );
        }

        if (CaptionElement.isCaptionElement(element)) {
            return (
                <Caption
                    id={element.id}
                    text={Node.string(element)}
                    contentType={element.figureContentType}
                    readOnly={readOnly}
                    attributes={attributes}
                    placeholder="Write a caption..."
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                >
                    {children}
                </Caption>
            );
        }

        if (DividerElement.isDividerElement(element)) {
            return (
                <Divider
                    color={color}
                    readOnly={readOnly}
                    attributes={attributes}
                >
                    {children}
                </Divider>
            );
        }

        if (FigureElement.isFigureElement(element)) {
            const { url, filePath, id: figureId } = element.children[0];
            switch (element.figureContentType) {
            case FigureContentTypes.youtube:
                return (
                    <YouTubeEmbed
                        id={figureId}
                        url={url}
                        color={color}
                        readOnly={readOnly}
                        user={user}
                        currentSessionId={currentSessionId}
                        postId={id}
                        editorType={type}
                        attributes={attributes}
                        onLoad={onEmbedLoad}
                    >
                        {children}
                    </YouTubeEmbed>
                );
            case FigureContentTypes.twitter:
                return (
                    <TwitterEmbed
                        id={figureId}
                        url={url}
                        color={color}
                        readOnly={readOnly}
                        user={user}
                        currentSessionId={currentSessionId}
                        postId={id}
                        editorType={type}
                        attributes={attributes}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        onLoad={onEmbedLoad}
                    >
                        {children}
                    </TwitterEmbed>
                );
            case FigureContentTypes.spotify:
                return (
                    <SpotifyEmbed
                        url={url}
                        color={color}
                        attributes={attributes}
                        onLoad={onEmbedLoad}
                    >
                        {children}
                    </SpotifyEmbed>
                );
            case FigureContentTypes.vimeo:
                return (
                    <VimeoEmbed
                        id={figureId}
                        url={url}
                        color={color}
                        readOnly={readOnly}
                        user={user}
                        currentSessionId={currentSessionId}
                        postId={id}
                        editorType={type}
                        attributes={attributes}
                        onLoad={onEmbedLoad}
                    >
                        {children}
                    </VimeoEmbed>
                );
            default:
                return (
                    <Figure
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        url={url}
                        user={user}
                        postId={id}
                        filePath={filePath}
                        editorType={type}
                        parentRef={parentRef}
                        editorRef={containerRef.current}
                        attributes={attributes}
                        enableGallery={enableGallery}
                        layoutType={element.orientation}
                        isGalleryChild={element.isGalleryChild}
                        rerender={rerenderMeasuringElements}
                        setRerender={setRerenderMeasuringElements}
                        createGallery={(imgId, imgURL, path) => FigureHelpers.bundleFigureWithNewFigureIntoGallery(editor, imgId, imgURL, path)}
                        uploadingMedia={uploadingMedia}
                        setUploadingMedia={setUploadingMedia}
                        updateUploadingMedia={updateUploadingMedia}
                        onCursorEnter={onRenderableElementCursorEnter}
                        onCursorLeave={onRenderableElementCursorLeave}
                        setSnackbarData={setSnackbarData}
                    >
                        {children}
                    </Figure>
                );
            }
        }

        if (AudioNoteElement.isAudioNoteElement(element)) {
            if (lightweightReadOnly) {
                return (
                    <div {...attributes}>
                        {element.title || 'Audio note'}
                    </div>
                );
            }
            return (
                <AudioNote
                    id={element.id}
                    customCursorActive={customCursorActive}
                    user={user}
                    userIsAdmin={userIsAdmin}
                    hasSound={hasSound}
                    editorType={type}
                    filePath={element.filePath}
                    title={element.title}
                    description={element.description}
                    isPostContentDictation={element.isPostContentDictation}
                    postContentTranscriptText={postContentDictationSourceTextsByAudioNoteId[element.id] || ''}
                    readOnlyUsesPreviewSource={readOnlyUsesPreviewSource}
                    previewAudioEndPromptText={previewAudioEndPromptText}
                    color={color}
                    readOnly={readOnly}
                    attributes={attributes}
                    currentSessionId={currentSessionId}
                    postId={id}
                    chapterId={readOnlyChapterId}
                    sectionId={readOnlySectionId}
                    parentRef={containerRef.current || undefined}
                    viewportRootRef={scrollParentRef}
                    isRecordingAudioNote={isRecordingAudioNote}
                    setIsRecordingAudioNote={setIsRecordingAudioNote}
                    broadcastHeightAdjusted={onAudioNoteAdjustHeight}
                    uploadingMedia={uploadingMedia}
                    setUploadingMedia={setUploadingMedia}
                    updateUploadingMedia={updateUploadingMedia}
                    removeUploadingMedia={removeUploadingMedia}
                    updateAudioNote={({
                        id: audioNoteId,
                        filePath,
                        title,
                        uploaded,
                        description,
                        isPostContentDictation,
                    }) => AudioNoteHelpers.updateAudioNote(editor, {
                        id: audioNoteId,
                        filePath,
                        title,
                        uploaded,
                        description,
                        isPostContentDictation,
                    })}
                    {...(readOnly
                        ? {
                            isAuthor,
                        } : {}
                    )}
                    onCursorEnter={onRenderableElementCursorEnter}
                    onCursorLeave={onRenderableElementCursorLeave}
                    setInputFocused={setInputFocused}
                    setSnackbarData={setSnackbarData}
                    onReadOnlyPlaybackStateChange={handleReadOnlyAudioPlaybackStateChange}
                >
                    {children}
                </AudioNote>
            );
        }

        if (GalleryElement.isGalleryElement(element)) {
            return (
                <Gallery
                    attributes={attributes}
                    rerender={rerenderMeasuringElements}
                    setRerender={setRerenderMeasuringElements}
                >
                    {children}
                </Gallery>
            );
        }

        if (HeaderElement.isH1(element)) {
            return (
                <HeaderOne
                    isFirstElement={element.isFirstElement}
                    readOnly={readOnly}
                    paragraphId={element.paragraphId}
                    plainText={Node.string(element)}
                    attributes={attributes}
                >
                    {children}
                </HeaderOne>
            );
        }

        if (HeaderElement.isH2(element)) {
            let headerTwoPartNumber: number | null = null;

            if (showHeaderTwoPartMarkers) {
                try {
                    const headerTwoPath = ReactEditor.findPath(editor, element).join(',');
                    headerTwoPartNumber = headerTwoPartNumbersByPath.get(headerTwoPath) || null;
                } catch (_error) {
                    headerTwoPartNumber = null;
                }
            }

            return (
                <HeaderTwo
                    isFirstElement={element.isFirstElement}
                    paragraphId={element.paragraphId}
                    plainText={Node.string(element)}
                    showPartMarker={showHeaderTwoPartMarkers && !HeaderElement.hidesPartMarker(element)}
                    partNumber={headerTwoPartNumber}
                    partCount={headerTwoPartCount}
                    attributes={attributes}
                >
                    {children}
                </HeaderTwo>
            );
        }

        if (FigureContentElement.isFigureContentElement(element)) {
            if (element.figureContentType === FigureContentTypes.image) {
                return (
                    <Image
                        id={element.id}
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        user={user}
                        postId={id}
                        color={color}
                        readOnly={readOnly}
                        editorType={type}
                        imagePath={element.filePath}
                        attributes={attributes}
                        layoutType={element.orientation}
                        isGalleryChild={element.isGalleryChild}
                        onLoad={onImageLoad}
                        setImagePath={(filePath) => FigureHelpers.setFigureUrlFilePath(editor, undefined, filePath)}
                        setLayout={(orientation) => FigureHelpers.setFigureOrientation(editor, orientation)}
                        removeFigure={() => FigureHelpers.removeFigure(editor)}
                        swapFigureOrder={() => FigureHelpers.shuffleGalleryOrder(editor)}
                        uploadingMedia={uploadingMedia}
                        setUploadingMedia={setUploadingMedia}
                        updateUploadingMedia={updateUploadingMedia}
                        onCursorEnter={onRenderableElementCursorEnter}
                        onCursorLeave={onRenderableElementCursorLeave}
                        setSnackbarData={setSnackbarData}
                    >
                        {children}
                    </Image>
                );
            }

            return (
                <HiddenVoidContent
                    {...attributes}
                >
                    {children}
                </HiddenVoidContent>
            );
        }

        if (WebLinkElement.isWebLinkElement(element)) {
            if (lightweightReadOnly) {
                return (
                    <a
                        {...attributes}
                        href={element.href}
                    >
                        {children}
                    </a>
                );
            }
            return (
                <InlineLink
                    id={element.id}
                    customCursorActive={customCursorActive}
                    color={color}
                    href={element.href}
                    readOnly={readOnly}
                    currentSessionId={currentSessionId}
                    postId={id}
                    editorType={type}
                    rootPostTitle={rootPostTitle}
                    rootPostValueNodes={rootPostValueNodes}
                    rootPostPreviewValueNodes={rootPostPreviewValueNodes}
                    chapters={internalPostLinkPreviewChapters}
                    hasSuccess={hasSuccess}
                    hasError={hasError}
                    onCursorEnter={onRenderableElementCursorEnter}
                    onCursorLeave={onRenderableElementCursorLeave}
                    parentRef={containerRef.current}
                    onNavigateInternalPostLink={onNavigateInternalPostLink}
                    internalPostLinkNavigationActive={(
                        readOnly
                        && !!activeInternalPostLinkNavigationHref
                        && activeInternalPostLinkNavigationHref === element.href
                    )}
                    setSnackbarData={setSnackbarData}
                    attributes={attributes}
                >
                    {children}
                </InlineLink>
            );
        }

        if (ZoomLevelElement.isZoomLevelElement(element)) {
            if (lightweightReadOnly) {
                return (
                    <span {...attributes}>
                        {children}
                    </span>
                );
            }
            return (
                <ZoomLevel
                    customCursorActive={customCursorActive}
                    color={color}
                    level={{
                        level: element.level,
                        icon: zoomLevelIcons?.get(element.level) || null,
                    }}
                    currentZoomLevel={currentZoomLevel}
                    isInline={element.levelType === ZOOM_LEVEL_TYPE.inline}
                    readOnly={readOnly}
                    currentSessionId={currentSessionId}
                    hasSuccess={hasSuccess}
                    hasError={hasError}
                    unusedZoomLevelCount={unusedZoomLevelCount}
                    onCursorEnter={onRenderableElementCursorEnter}
                    onCursorLeave={onRenderableElementCursorLeave}
                    parentRef={containerRef.current}
                    attributes={attributes}
                >
                    {children}
                </ZoomLevel>
            );
        }

        if (ListElement.isListElement(element)) {
            const listType = ListElement.isOL(element)
                ? ListElement.LIST_TYPE.ordered : ListElement.LIST_TYPE.unordered;
            return (
                <List
                    type={listType}
                    attributes={attributes}
                >
                    {children}
                </List>
            );
        }

        if (ListItemElement.isListItemElement(element)) {
            return (
                <ListItem
                    attributes={attributes}
                >
                    {children}
                </ListItem>
            );
        }

        return (
            <Paragraph
                hasSuccess={hasSuccess}
                hasError={hasError}
                paragraphId={element.paragraphId}
                plainText={Node.string(element)}
                attributes={attributes}
            >
                {children}
            </Paragraph>
        );
    }, [
        id,
        color,
        user,
        userIsAdmin,
        readOnly,
        hasSound,
        type,
        currentSessionId,
        type,
        uploadingMedia,
        currentZoomLevel,
        setUploadingMedia,
        updateUploadingMedia,
        rerenderMeasuringElements,
        containerRef.current,
        parentRef,
        scrollParentRef,
        isRecordingAudioNote,
        onAudioNoteAdjustHeight,
        handleReadOnlyAudioPlaybackStateChange,
        postContentDictationSourceTextsByAudioNoteId,
        onCursorEnter,
        onRenderableElementCursorEnter,
        onRenderableElementCursorLeave,
        onCursorLeave,
        setInputFocused,
        onImageLoad,
        onEmbedLoad,
        setSnackbarData,
        hasSuccess,
        hasError,
        zoomLevelIcons,
        customCursorActive,
        lightweightReadOnly,
        showHeaderTwoPartMarkers,
        headerTwoPartNumbersByPath,
        headerTwoPartCount,
        onNavigateInternalPostLink,
        activeInternalPostLinkNavigationHref,
    ]);

    const renderLeaf = useCallback(({
        attributes,
        children,
        leaf,
    }: {
        attributes: any,
        children: any,
        leaf: any,
    }) => {
        const annotationDecorations = Object.keys(leaf).filter((key) => key.includes(ANNOTATION_DECORATION_PREFIX));
        const revealQuote = Object.keys(leaf)
            .filter((key) => key.includes(REVEAL_QUOTE_DECORATION_PREFIX))
            .reduce((acc, key) => acc || leaf[key], false);
        const remoteCursors = getRemoteCursorsOnLeaf<ICollaborativeCursorData, TextWithRemoteCursors<ICollaborativeCursorData>>(
            leaf as TextWithRemoteCursors<ICollaborativeCursorData>,
        );
        const remoteCursorSelectionBackground = getRemoteCursorSelectionBackground({
            remoteCursors,
            defaultColor: DEFAULT_REMOTE_CURSOR_COLOR,
        });
        let leafHighlightColor = null;
        if (
            leaf.dictationHighlight
            || leaf.postQuote
            || (leaf.annotationQuote && revealQuote)
        ) {
            leafHighlightColor = leaf.highlightColor;
        }
        const leafHighlightDuration = leaf.dictationHighlight || shouldSuppressReadOnlyDecorationHighlights
            ? 0
            : HIGHLIGHT_TRANSITION_DURATION;
        return (
            <SlateLeaf
                {...attributes}
                {...(leaf.postQuote && { 'data-quote': 'post-quote' })} // When highlighting
                {...(leaf.annotationQuote && { 'data-quote': 'annotation-quote' })} // When revealing annotation context
                {...(leaf.dictationHighlight && {
                    'data-dictation': 'post-audio-sync',
                })}
                {...(annotationDecorations.reduce((acc, key) => {
                    acc[`data-${key}`] = leaf[key];
                    return acc;
                }, {} as Record<string, string>))}
                bold={leaf[EMPHASIZER_MARK.bold]}
                italics={leaf[EMPHASIZER_MARK.italicize]}
                underline={leaf[EMPHASIZER_MARK.underline]}
                strikethrough={leaf[EMPHASIZER_MARK.strikethrough]}
                highlightColor={leafHighlightColor}
                highlightDuration={leafHighlightDuration}
            >
                {remoteCursorSelectionBackground
                    ? (
                        <RemoteCursorSelectionHighlight background={remoteCursorSelectionBackground}>
                            {children}
                        </RemoteCursorSelectionHighlight>
                    )
                    : children}
            </SlateLeaf>
        );
    }, [
        shouldSuppressReadOnlyDecorationHighlights,
    ]);

    return (
        <Container
            ref={containerRef}
            visible={!hide}
            parentWidth={parentWidth}
            bufferHeight={bufferHeight}
            withExternalBottomBuffer={
                usingCollaborativeDoc
            }
            withBottomBuffer={!!currentZoomLevel
            && currentZoomLevel.level !== ZOOM_LEVEL.five}
        >
            {renderModal()}
            {renderInternalPostLinkScopePickerModal()}
            {initializedValue && (
                <Slate
                    editor={editor}
                    value={renderedSlateValue}
                    onChange={() => {
                        const hasContentOperation = editor.operations.some(
                            (operation) => operation.type !== 'set_selection',
                        );
                        if (hasContentOperation) {
                            if (suppressContentChangeCountRef.current > 0) {
                                suppressContentChangeCountRef.current -= 1;
                                return;
                            }
                            // Persist the normalized live tree instead of the raw callback
                            // payload because plugins may already have rewritten children.
                            // Selection-only updates should not churn React state, because
                            // doing so can make cursor placement feel unstable.
                            setValue(ensureNonEmptySlateValue(
                                editor.children as Descendant[],
                                INITIAL_EDITOR_VALUE,
                            ));
                        }
                        const nativeSelection = window.getSelection();

                        if (
                            !readOnly
                            && !updateToolbars
                            && nativeSelection
                            && nativeSelection.isCollapsed
                        ) {
                            // Update only if not selecting text
                            setUpdateToolbars(true);
                        }
                    }}
                >
                    {usingCollaborativeDoc
                        ? (
                            <CollaborativeEditable
                                id={id}
                                readOnly={readOnly}
                                placeholder={placeholder}
                                customCursorActive={customCursorActive}
                                containerRef={containerRef}
                                decorate={decorate}
                                renderElement={renderElement}
                                renderLeaf={renderLeaf}
                                handleOnKeyDown={handleOnKeyDown}
                                onReadOnlyAudioTextSeekInteraction={handleReadOnlyAudioTextSeekInteraction}
                                handleReadOnlyAudioTextTouchStart={handleReadOnlyAudioTextTouchStart}
                                handleReadOnlyAudioTextTouchMove={handleReadOnlyAudioTextTouchMove}
                                clearReadOnlyAudioTextTouchGesture={clearReadOnlyAudioTextTouchGesture}
                                onEditableEnter={onEditableEnter}
                                onEditableLeave={onEditableLeave}
                                onEditableFocus={onEditableFocus}
                                onEditableBlur={onEditableBlur}
                                onEditablePointerDownCapture={onEditablePointerDownCapture}
                                handleEditableCopy={handleEditableCopy}
                                handleEditablePaste={handleEditablePaste}
                                usePointerCursor={editableUsesPointerCursor}
                            />
                        )
                        : (
                            <Editable
                                {...(readOnly
                                    ? {
                                        readOnly: true,
                                    } : {}
                                )}
                                {...(!customCursorActive
                                    ? {
                                        style: {
                                            cursor: editableUsesPointerCursor ? 'pointer' : 'text',
                                        },
                                    } : {}
                                )}
                                data-id={id}
                                className={`${HOVER_TARGET_CLASSNAME} ${SLATE_EDITOR_CLASSNAME} ${SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME}`}
                                spellCheck
                                decorate={decorate}
                                renderElement={renderElement}
                                renderLeaf={renderLeaf}
                                placeholder={!readOnly ? placeholder : undefined}
                                scrollSelectionIntoView={readOnly ? SUPPRESS_SCROLL_SELECTION_INTO_VIEW : undefined}
                                onKeyDown={handleOnKeyDown}
                                onTouchStart={handleReadOnlyAudioTextTouchStart}
                                onTouchMove={handleReadOnlyAudioTextTouchMove}
                                onMouseUp={handleReadOnlyAudioTextSeekInteraction}
                                onTouchEnd={handleReadOnlyAudioTextSeekInteraction}
                                onTouchCancel={clearReadOnlyAudioTextTouchGesture}
                                onMouseDownCapture={onEditablePointerDownCapture}
                                onTouchStartCapture={onEditablePointerDownCapture}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onEditableEnter,
                                    onMouseLeave: onEditableLeave,
                                })}
                                onFocus={onEditableFocus}
                                onBlur={onEditableBlur}
                                onCopy={handleEditableCopy}
                                onPaste={handleEditablePaste}
                            />
                        )}
                    {readOnlyChunkAppendPending && (
                        <>
                            {shouldShowReadOnlyChunkPlaceholder && (
                                <ReadOnlyChunkLoader
                                    ref={readOnlyChunkLoaderRef}
                                    data-testid="post-editor-read-only-chunk-placeholder"
                                >
                                    {Array.from(
                                        {
                                            length: readOnlyChunkPlaceholderBlockCount
                                                * READ_ONLY_CHUNK_PLACEHOLDER_LINES_PER_BLOCK,
                                        },
                                        (_, index) => {
                                            const isFirstLine = index === 0;
                                            const isBlockStart = !isFirstLine
                                                && index % READ_ONLY_CHUNK_PLACEHOLDER_LINES_PER_BLOCK === 0;
                                            return (
                                                <PlaceholderBox
                                                    key={`read-only-chunk-placeholder-${index + 1}`}
                                                    width={READ_ONLY_CHUNK_PLACEHOLDER_WIDTH}
                                                    maxWidth={READ_ONLY_CHUNK_PLACEHOLDER_MAX_WIDTH}
                                                    height={BODY_FONT_SIZE}
                                                    margin={isFirstLine
                                                        ? '0px auto'
                                                        : `${isBlockStart
                                                            ? READ_ONLY_CHUNK_PLACEHOLDER_BLOCK_GAP
                                                            : READ_ONLY_CHUNK_PLACEHOLDER_LINE_GAP}px auto 0px`}
                                                    background={themeObj.color.cream400}
                                                    shimmerLightColor="#d8d3cb"
                                                    shimmerDarkColor="#c6c1ba"
                                                />
                                            );
                                        },
                                    )}
                                </ReadOnlyChunkLoader>
                            )}
                            <ReadOnlyPrefetchSentinel
                                ref={readOnlyPrefetchSentinelRef}
                                data-testid="post-editor-read-only-prefetch-sentinel"
                            />
                        </>
                    )}
                    {!readOnly
                    && (
                        <PortableToolbar
                            // Post Editor Only
                            handleEmojiSelect={handleEmojiSelect}
                            onSelectionToolbarVisibilityChange={setShowSelectionToolbar}
                            // Common to PostEditor and AnnotationEditor
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            user={user}
                            postId={id}
                            type={EDITOR_TOOLBAR_TYPE.selection}
                            hasSound={hasSound}
                            buttonLength={PORTABLE_TOOLBAR_BUTTON_LENGTH}
                            editorType={type}
                            editorId={id}
                            setRef={(ref) => { selectionToolbarRef.current = ref; }}
                            color={color}
                            value={value}
                            selectedLinkURL={getSelectedLinkURL}
                            selectedLinkID={getSelectedLinkID}
                            top={selectionToolbarPosition.y}
                            left={selectionToolbarPosition.x}
                            defaultOpenGroups={new Map([
                                [EDITOR_TOOLBAR_TOOL_GROUP.layout, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.media, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.text, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.embeddings, true],
                            ])}
                            selection={editor.selection}
                            setSelection={(selection) => Transforms.select(editor, selection)}
                            updatePosition={updateSelectionToolbarPosition}
                            toggleBold={() => EmphasizerHelpers.toggleBold(editor)}
                            containsBold={EmphasizerHelpers.selectionContainsBold(editor)}
                            toggleItalics={() => EmphasizerHelpers.toggleItalicize(editor)}
                            containsItalics={EmphasizerHelpers.selectionContainsItalicize(editor)}
                            toggleStrikethrough={() => EmphasizerHelpers.toggleStrikethrough(editor)}
                            containsStrikethrough={EmphasizerHelpers.selectionContainsStrikethrough(editor)}
                            toggleUnderline={() => EmphasizerHelpers.toggleUnderline(editor)}
                            containsUnderline={EmphasizerHelpers.selectionContainsUnderline(editor)}
                            toggleHeaderOne={() => HeaderHelpers.toggleH1(editor)}
                            containsHeaderOne={HeaderHelpers.hasSelectedH1(editor)}
                            toggleHeaderTwo={() => HeaderHelpers.toggleH2(editor)}
                            containsHeaderTwo={HeaderHelpers.hasSelectedH2(editor)}
                            toggleHeaderTwoPartMarker={() => HeaderHelpers.toggleH2PartMarker(editor)}
                            containsHeaderTwoPartMarkerHidden={HeaderHelpers.hasSelectedH2PartMarkerHidden(editor)}
                            toggleNumberedList={() => ListHelpers.toggleOL(editor)}
                            containsNumberedList={ListHelpers.hasSelectedOL(editor)}
                            toggleBulletedList={() => ListHelpers.toggleUL(editor)}
                            containsBulletedList={ListHelpers.hasSelectedUL(editor)}
                            toggleQuote={() => BlockQuoteHelpers.toggleBlockQuote(editor)}
                            containsQuote={BlockQuoteHelpers.hasSelectedBlockQuote(editor)}
                            toggleDivider={() => DividerHelpers.toggleDivider(editor)}
                            containsDivider={DividerHelpers.hasSelectedDivider(editor)}
                            toggleZoom={() => {
                                if (currentZoomLevel) {
                                    // Sets zoom level to the current zoom level
                                    // If user is on zoom level 5, set it to zoom level 5
                                    // To push to zoom 4, user will have to be in zoom level 4
                                    ZoomLevelHelpers.toggleZoom(editor, currentZoomLevel.level);
                                }
                            }}
                            containsZoom={!!currentZoomLevel
                                && ZoomLevelHelpers.hasSelectionZoomLevels(editor)
                                && !ZoomLevelHelpers.selectionHasHigherZoomLevels(editor, currentZoomLevel.level)}
                            createFigure={(figureId, filePath, caption) => FigureHelpers.insertFigure(editor, figureId, undefined, filePath, caption)}
                            containsFigure={FigureHelpers.hasSelectedImageFigure(editor)}
                            createLink={(linkId, href, selection) => {
                                if (selection) {
                                    Transforms.select(editor, selection);
                                }
                                WebLinkHelpers.wrapSelectionInLinkCmd(editor, linkId, href);
                            }}
                            updateLink={(linkId, href, selection) => {
                                if (selection) {
                                    Transforms.select(editor, selection);
                                }
                                WebLinkHelpers.setWebLinkHref(editor, linkId, href);
                            }}
                            removeLink={() => WebLinkHelpers.removeWebLink(editor)}
                            containsLink={WebLinkHelpers.hasSelectedLink(editor)}
                            selectEndDocument={moveCursorToEndOfDocument}
                            createYouTubeEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsYouTube={FigureHelpers.hasSelectedYouTubeFigure(editor)}
                            createTwitterEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsTwitter={FigureHelpers.hasSelectedTwitterFigure(editor)}
                            createSpotifyEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsSpotify={FigureHelpers.hasSelectedSpotifyFigure(editor)}
                            createVimeoEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsVimeo={FigureHelpers.hasSelectedVimeoFigure(editor)}
                            insertAudioNote={(audioNoteId) => AudioNoteHelpers.insertAudioNote(editor, { id: audioNoteId })}
                            containsAudioNote={AudioNoteHelpers.hasSelectedAudioNote(editor)}
                            uploadingMedia={uploadingMedia}
                            setUploadingMedia={setUploadingMedia}
                            updateUploadingMedia={updateUploadingMedia}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            setSnackbarData={setSnackbarData}
                            internalPostLinkSelectionActive={internalPostLinkSelectionActive}
                            internalPostLinkTarget={pendingInternalPostLinkTarget}
                            internalPostLinkCurrentScopeLabel={currentInternalPostLinkScopeLabel}
                            startInternalPostLinkSelection={startInlineInternalPostLinkSelection}
                            openInternalPostLinkScopePicker={() => {
                                openInternalPostLinkScopePicker();
                            }}
                            redoInternalPostLinkSelection={redoInternalPostLinkSelection}
                            clearInternalPostLinkSelection={clearInternalPostLinkSelection}
                            acceptInternalPostLinkSelectionRequestKey={internalPostLinkSelectionSubmitKey}
                        />
                    )}
                    {!readOnly
                    && (
                        <PortableToolbar
                            // Post Editor Only
                            type={EDITOR_TOOLBAR_TYPE.block}
                            editorType={type}
                            editorId={id}
                            value={value}
                            top={blockToolbarPosition.y}
                            left={blockToolbarPosition.x}
                            hide={showSelectionToolbar}
                            handleEmojiSelect={handleEmojiSelect}
                            // Common to PostEditor and AnnotationEditor
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            user={user}
                            postId={id}
                            hasSound={hasSound}
                            color={color}
                            buttonLength={PORTABLE_TOOLBAR_BUTTON_LENGTH}
                            defaultOpenGroups={new Map([
                                [EDITOR_TOOLBAR_TOOL_GROUP.text, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.layout, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.media, true],
                                [EDITOR_TOOLBAR_TOOL_GROUP.embeddings, true],
                            ])}
                            selection={editor.selection}
                            setSelection={(selection) => Transforms.select(editor, selection)}
                            toggleBold={() => EmphasizerHelpers.toggleBold(editor)}
                            containsBold={EmphasizerHelpers.selectionContainsBold(editor)}
                            toggleItalics={() => EmphasizerHelpers.toggleItalicize(editor)}
                            containsItalics={EmphasizerHelpers.selectionContainsItalicize(editor)}
                            toggleStrikethrough={() => EmphasizerHelpers.toggleStrikethrough(editor)}
                            containsStrikethrough={EmphasizerHelpers.selectionContainsStrikethrough(editor)}
                            toggleUnderline={() => EmphasizerHelpers.toggleUnderline(editor)}
                            containsUnderline={EmphasizerHelpers.selectionContainsUnderline(editor)}
                            toggleHeaderOne={() => HeaderHelpers.toggleH1(editor)}
                            containsHeaderOne={HeaderHelpers.hasSelectedH1(editor)}
                            toggleHeaderTwo={() => HeaderHelpers.toggleH2(editor)}
                            containsHeaderTwo={HeaderHelpers.hasSelectedH2(editor)}
                            toggleHeaderTwoPartMarker={() => HeaderHelpers.toggleH2PartMarker(editor)}
                            containsHeaderTwoPartMarkerHidden={HeaderHelpers.hasSelectedH2PartMarkerHidden(editor)}
                            toggleNumberedList={() => ListHelpers.toggleOL(editor)}
                            containsNumberedList={ListHelpers.hasSelectedOL(editor)}
                            toggleBulletedList={() => ListHelpers.toggleUL(editor)}
                            containsBulletedList={ListHelpers.hasSelectedUL(editor)}
                            toggleQuote={() => BlockQuoteHelpers.toggleBlockQuote(editor)}
                            containsQuote={BlockQuoteHelpers.hasSelectedBlockQuote(editor)}
                            toggleDivider={() => DividerHelpers.toggleDivider(editor)}
                            containsDivider={DividerHelpers.hasSelectedDivider(editor)}
                            toggleZoom={() => {
                                if (currentZoomLevel) {
                                    // Sets zoom level to the current zoom level
                                    // If user is on zoom level 5, set it to zoom level 5
                                    // To push to zoom 4, user will have to be in zoom level 4
                                    ZoomLevelHelpers.toggleZoom(editor, currentZoomLevel.level);
                                }
                            }}
                            containsZoom={!!currentZoomLevel
                                && ZoomLevelHelpers.hasSelectionZoomLevels(editor)
                                && !ZoomLevelHelpers.selectionHasHigherZoomLevels(editor, currentZoomLevel.level)}
                            createFigure={(figureId, filePath, caption) => FigureHelpers.insertFigure(editor, figureId, undefined, filePath, caption)}
                            containsFigure={FigureHelpers.hasSelectedImageFigure(editor)}
                            createLink={(linkId, href, selection) => {
                                if (selection) {
                                    Transforms.select(editor, selection);
                                }
                                WebLinkHelpers.wrapSelectionInLinkCmd(editor, linkId, href);
                            }}
                            updateLink={(linkId, href, selection) => {
                                if (selection) {
                                    Transforms.select(editor, selection);
                                }
                                WebLinkHelpers.setWebLinkHref(editor, linkId, href);
                            }}
                            removeLink={() => WebLinkHelpers.removeWebLink(editor)}
                            // We leave out containsLink because Block Toolbar should not show it as applied
                            selectEndDocument={moveCursorToEndOfDocument}
                            createYouTubeEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsYouTube={FigureHelpers.hasSelectedYouTubeFigure(editor)}
                            createTwitterEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsTwitter={FigureHelpers.hasSelectedTwitterFigure(editor)}
                            createSpotifyEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsSpotify={FigureHelpers.hasSelectedSpotifyFigure(editor)}
                            createVimeoEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsVimeo={FigureHelpers.hasSelectedVimeoFigure(editor)}
                            insertAudioNote={(audioNoteId) => AudioNoteHelpers.insertAudioNote(editor, { id: audioNoteId })}
                            containsAudioNote={AudioNoteHelpers.hasSelectedAudioNote(editor)}
                            uploadingMedia={uploadingMedia}
                            setUploadingMedia={setUploadingMedia}
                            updateUploadingMedia={updateUploadingMedia}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            setSnackbarData={setSnackbarData}
                            internalPostLinkSelectionActive={internalPostLinkSelectionActive}
                            internalPostLinkTarget={pendingInternalPostLinkTarget}
                            internalPostLinkCurrentScopeLabel={currentInternalPostLinkScopeLabel}
                            startInternalPostLinkSelection={startInlineInternalPostLinkSelection}
                            openInternalPostLinkScopePicker={() => {
                                openInternalPostLinkScopePicker();
                            }}
                            redoInternalPostLinkSelection={redoInternalPostLinkSelection}
                            clearInternalPostLinkSelection={clearInternalPostLinkSelection}
                        />
                    )}
                </Slate>
            )}
            {usingCollaborativeDoc && (
                <CollaborativeBottomBuffer
                    height={bufferHeight}
                />
            )}
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    parentWidth: number | string | null,
    visible: boolean,
    withBottomBuffer: boolean,
    withExternalBottomBuffer: boolean,
    bufferHeight: number,
}
const RemoteCursorSelectionHighlight = styled.span<{ background: string }>`
    background: ${({ background }) => background};
    border-radius: 3px;
`;

const RemoteCursorLayer = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 4;
`;

const RemoteCursorOverlay = styled.div<{
    top: number,
    left: number,
    height: number,
}>`
    position: absolute;
    top: ${({ top }) => `${top}px`};
    left: ${({ left }) => `${left}px`};
    height: ${({ height }) => `${height}px`};
    transform: translateX(-1px);
`;

const RemoteCursorLabel = styled.div<{ backgroundColor: string }>`
    position: absolute;
    bottom: calc(100% + 4px);
    left: 0;
    padding: 2px 6px;
    border-radius: 999px;
    background: ${({ backgroundColor }) => backgroundColor};
    color: #fff;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
`;

const RemoteCursorCaret = styled.div<{ color: string }>`
    width: 2px;
    height: 100%;
    background: ${({ color }) => color};
    border-radius: 999px;
`;

const HiddenVoidContent = styled.span.attrs({
    style: HIDDEN_VOID_CONTENT_STYLE,
})``;

const Container = styled.div<ContainerProps>`
    position: relative;
    width: 100%;
    height: fit-content;
    max-width: ${({ parentWidth }) => (typeof parentWidth === 'string'
        ? parentWidth
        : `${POST_EDITOR_MAX_WIDTH}px`
    )};
    margin: 0 auto;
    padding-bottom: ${({ withBottomBuffer, withExternalBottomBuffer, bufferHeight }) => `${withBottomBuffer && !withExternalBottomBuffer
        ? bufferHeight
        : 0}px`};
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    background: inherit;
    transition: ${({ theme }) => `opacity 300ms ${theme.motion.delayEasing}`};

    // Add background to Editable div so that Slate components can
    // inherit the background color
    & > .${SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME} {
        background: inherit;
        overflow-anchor: none;
    }
`;

const CollaborativeBottomBuffer = styled.div<{
    height: number,
}>`
    width: 100%;
    height: ${({ height }) => `${height}px`};
    margin: 0;
    padding: 0;
    pointer-events: none;
    overflow-anchor: none;
`;

const ReadOnlyPrefetchSentinel = styled.div`
    width: 100%;
    height: 1px;
    pointer-events: none;
    /* Prevent the browser from using the end-of-chunk sentinel as the scroll
     * anchor; when the sentinel moves during an append, that anchor can nudge
     * the reader upward and make them lose their place.
     */
    overflow-anchor: none;
`;

const ReadOnlyChunkLoader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-top: ${0.5 * BODY_FONT_SIZE}px;
    margin-bottom: ${4 * BODY_FONT_SIZE}px;
    pointer-events: none;
    /* Keep the placeholder out of the browser's native anchor selection so it
     * cannot become the scroll anchor during a chunk handoff.
     */
    overflow-anchor: none;

    & > * {
        overflow-anchor: none;
    }
`;

const InternalPostLinkScopePickerActions = styled(HistoryPreviewActions)`
    align-items: center;
`;

const InternalPostLinkScopePickerHelpText = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    line-height: 1.5em;
    text-align: center;
    width: min(100%, ${42 * BODY_FONT_SIZE}px);
    margin: 0 auto;
`;

const InternalPostLinkScopePickerControlsColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${0.625 * BODY_FONT_SIZE}px;
    width: fit-content;
    max-width: 100%;
    min-width: 0;
    margin: 0 auto;
    text-align: center;
`;

const InternalPostLinkScopePickerTargetRow = styled(HistoryTargetRow)`
    width: fit-content;
    max-width: 100%;
    min-width: 0;
`;

const InternalPostLinkScopePickerSummaryCard = styled(HistoryPublishSummaryCard)`
    align-self: center;
    width: min(100%, ${32 * BODY_FONT_SIZE}px);
    text-align: center;
`;

const InternalPostLinkScopePickerSummaryEyebrow = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.75 * BODY_FONT_SIZE}px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const InternalPostLinkScopePickerSummarySubtitle = styled(HistoryPublishSummarySubtitle)`
    text-align: center;
`;

const InternalPostLinkScopePickerSummaryActions = styled(HistoryPublishSummaryActions)`
    justify-content: center;
`;

const InternalPostLinkScopePickerPreviewContainer = styled.div`
    min-height: 0;
    flex: 1;
    padding: ${0.75 * BODY_FONT_SIZE}px ${BODY_FONT_SIZE}px ${BODY_FONT_SIZE}px;
    overflow-y: auto;
    background: ${themeObj.color.white};
    border-top: 1px solid ${themeObj.color.neutral300};
`;

export default PostEditor;
